import React, { useState, useEffect, useRef } from 'react';
import {
  PlusIcon,
  BuildingStorefrontIcon,
  MapPinIcon,
  CalendarIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  EyeIcon,
  DocumentTextIcon,
  CheckIcon,
  PhoneIcon,
  MagnifyingGlassIcon,
  DocumentDuplicateIcon,
  QrCodeIcon,
  CheckCircleIcon,
  PrinterIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  PencilSquareIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { useSearchParams } from 'react-router-dom';
// Mock data managers no longer used - Farm Diary now uses real Supabase data
import GoogleMapsModal from '../components/GoogleMapsModal';
import FarmMapOverview from '../components/FarmMapOverview';
import MyGAPReport from '../components/MyGAPReport';
import { useAuth } from '../contexts/AuthContext';
import { useFarm } from '../contexts/FarmContext';
import { supabase } from '../config/supabase';
import itraceSerializationService from '../services/itraceSerializationService';
import CreateFarmWithCycle from '../components/CreateFarmWithCycle';
import OrganizationSelector from '../components/OrganizationSelector';
import FarmSelector from '../components/FarmSelector';

const FarmManagement = () => {
  const { user, isAdmin } = useAuth();
  const { farms: contextFarms, loading: contextLoading, refreshFarms, hasOrganizations, hasFarms } = useFarm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [farms, setFarms] = useState([]);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateFarmModal, setShowCreateFarmModal] = useState(false);
  const [showMapView, setShowMapView] = useState(false);
  
  // Searchable farm selector state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [farmSearchQuery, setFarmSearchQuery] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load farms from context (handles both admin and regular users)
  useEffect(() => {
    if (contextFarms && contextFarms.length > 0) {
      // Transform context farms to match component structure
      const transformedFarms = contextFarms.map(farm => {
        const subscriptionEnd = farm.subscriptionEndDate ? new Date(farm.subscriptionEndDate) : null;
        const today = new Date();
        const daysRemaining = subscriptionEnd ? Math.ceil((subscriptionEnd - today) / (1000 * 60 * 60 * 24)) : null;

        return {
          id: farm.farm_id || farm.id,
          name: farm.farm_name || farm.farmName || 'Unknown Farm',
          address: farm.address || '',
          city: farm.city || '',
          state: farm.state || '',
          postcode: farm.postcode || '',
          size: farm.farmsize || farm.farm_size ? `${farm.farmsize || farm.farm_size} hectares` : 'N/A',
          mobileNumber: farm.mobileNumber || farm.mobile_number || '',
          subscriptionEndDate: farm.subscriptionEndDate || farm.subscription_end_date,
          daysRemaining: daysRemaining,
          createdAt: farm.created_at,
          role: farm.role || 'Member',
          latitude: farm.latitude || farm.lat,
          longitude: farm.longitude || farm.lng
        };
      });

      setFarms(transformedFarms);
      
      // Auto-select farm from URL query param (e.g., from dashboard map "View Details")
      const farmIdFromUrl = searchParams.get('farmId');
      if (farmIdFromUrl && transformedFarms.length > 0) {
        const targetFarm = transformedFarms.find(f => String(f.id) === farmIdFromUrl);
        if (targetFarm) {
          setSelectedFarm(targetFarm);
          setSelectedTab('overview');
          // Clear the query param so it doesn't persist on refresh
          setSearchParams({}, { replace: true });
        } else if (!selectedFarm) {
          setSelectedFarm(transformedFarms[0]);
        }
      } else if (transformedFarms.length > 0 && !selectedFarm) {
        setSelectedFarm(transformedFarms[0]);
      }
      setLoading(false);
    } else if (!contextLoading) {
      setFarms([]);
      setLoading(false);
    }
  }, [contextFarms, contextLoading]);

  // Filter farms based on search query
  const filteredFarms = farms.filter(farm => 
    farm.name?.toLowerCase().includes(farmSearchQuery.toLowerCase()) ||
    farm.city?.toLowerCase().includes(farmSearchQuery.toLowerCase()) ||
    farm.state?.toLowerCase().includes(farmSearchQuery.toLowerCase())
  );

  const handleCreateFarm = () => {
    setShowCreateFarmModal(true);
  };

  const handleFarmSelect = (farm) => {
    setSelectedFarm(farm);
    setSelectedTab('overview');
    setIsDropdownOpen(false);
    setFarmSearchQuery('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Farm Management</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Manage your farms, crops, and production cycles</p>
        </div>
        <div className="flex items-center flex-wrap gap-2 sm:gap-3 lg:gap-4">
          {hasOrganizations && <OrganizationSelector />}
          {hasFarms && <FarmSelector />}
          <button
            onClick={handleCreateFarm}
            className="btn-primary flex items-center gap-2 whitespace-nowrap"
          >
            <PlusIcon className="h-4 w-4" />
            <span>Create New Farm</span>
          </button>
        </div>
      </div>

      {/* Farm Selection & Navigation */}
      {farms.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">Current Farm:</label>
              
              {/* Searchable Farm Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="input pl-10 pr-10 w-72 text-left cursor-pointer flex items-center justify-between bg-white"
                >
                  <BuildingStorefrontIcon className="h-5 w-5 absolute left-3 text-gray-400" />
                  <span className="truncate">
                    {selectedFarm ? `${selectedFarm.name} (${selectedFarm.size})` : 'Select Farm'}
                  </span>
                  <ChevronDownIcon className={`h-5 w-5 absolute right-3 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div className="absolute z-50 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-hidden">
                    {/* Search Input */}
                    <div className="p-2 border-b border-gray-200">
                      <div className="relative">
                        <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          value={farmSearchQuery}
                          onChange={(e) => setFarmSearchQuery(e.target.value)}
                          placeholder="Search farms..."
                          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      </div>
                    </div>

                    {/* Farm Count Header */}
                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-500">
                      {isAdmin ? `All Farms (${farms.length})` : `Your Farms (${farms.length})`}
                      {farmSearchQuery && ` • Showing ${filteredFarms.length} results`}
                    </div>

                    {/* Farm Options List */}
                    <div className="max-h-64 overflow-y-auto">
                      {filteredFarms.length > 0 ? (
                        filteredFarms.map((farm) => (
                          <div
                            key={farm.id}
                            onClick={() => handleFarmSelect(farm)}
                            className={`px-4 py-3 cursor-pointer hover:bg-gray-100 flex items-center gap-3 border-b border-gray-50 ${
                              selectedFarm?.id === farm.id ? 'bg-green-50 border-l-2 border-l-green-500' : ''
                            }`}
                          >
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <BuildingStorefrontIcon className="h-4 w-4 text-green-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-900 truncate">{farm.name}</div>
                              <div className="text-xs text-gray-500 flex items-center gap-2">
                                <span>{farm.size}</span>
                                {farm.city && <span>• {farm.city}, {farm.state}</span>}
                              </div>
                            </div>
                            {selectedFarm?.id === farm.id && (
                              <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-8 text-center text-gray-500 text-sm">
                          <MagnifyingGlassIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                          No farms found matching "{farmSearchQuery}"
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {selectedFarm && (
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <MapPinIcon className="h-4 w-4" />
                <span>{selectedFarm.city}, {selectedFarm.state}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Farm Map Overview */}
      {farms.length > 0 && (
        <div>
          <button
            onClick={() => setShowMapView(!showMapView)}
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-green-600 transition-colors mb-2"
          >
            <MapPinIcon className="h-4 w-4" />
            <span>{showMapView ? 'Hide Map' : 'Show Farm Map'}</span>
            <svg className={`w-4 h-4 transition-transform ${showMapView ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showMapView && (
            <FarmMapOverview
              farms={contextFarms}
              selectedFarmId={selectedFarm?.id}
              onFarmSelect={(farmId) => {
                const farm = farms.find(f => f.id === farmId || f.id === parseInt(farmId));
                if (farm) handleFarmSelect(farm);
              }}
              onViewDetails={(farmId) => {
                const farm = farms.find(f => f.id === farmId || f.id === parseInt(farmId));
                if (farm) handleFarmSelect(farm);
              }}
              showViewDetails={true}
              mapHeight="350px"
            />
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
        {[
          { id: 'overview', name: 'Farm Overview', icon: BuildingStorefrontIcon },
          { id: 'create-produce', name: 'Add Produce', icon: PlusIcon },
          { id: 'farm-diary', name: 'Farm Diary', icon: DocumentTextIcon },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setSelectedTab(tab.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${
              selectedTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            <span>{tab.name}</span>
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="min-h-96">
        {/* Farm Overview Tab */}
        {selectedTab === 'overview' && selectedFarm && (
          <FarmOverview
            farm={selectedFarm}
            onFarmUpdated={refreshFarms}
            onCreateCycle={() => setSelectedTab('create-produce')}
          />
        )}

        {/* Create Produce Tab */}
        {selectedTab === 'create-produce' && selectedFarm && (
          <CreateProduce 
            farmId={selectedFarm.id}
            onSeasonCreated={() => {
              // Switch back to overview tab after creation
              setSelectedTab('overview');
            }}
          />
        )}

        {/* Farm Diary Tab */}
        {selectedTab === 'farm-diary' && selectedFarm && (
          <FarmDiary farmId={selectedFarm.id} />
        )}

        {/* No Farm State */}
        {farms.length === 0 && (
          <div className="text-center py-12">
            <BuildingStorefrontIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900">No farms created</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating your first farm.</p>
            <div className="mt-6">
              <button
                onClick={handleCreateFarm}
                className="btn-primary flex items-center space-x-2 mx-auto"
              >
                <PlusIcon className="h-4 w-4" />
                <span>Create Your First Farm</span>
              </button>
            </div>
          </div>
        )}

        {/* Create Farm Modal (new simplified 2-step wizard with first cycle) */}
        {showCreateFarmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <CreateFarmWithCycle
                  onClose={() => setShowCreateFarmModal(false)}
                  onFarmCreated={(result) => {
                    refreshFarms();
                    setShowCreateFarmModal(false);
                    if (result?.farmName) {
                      alert(`✅ Farm "${result.farmName}" and first cycle "${result.cycleName}" created successfully.`);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ===================================
// 📅 EDIT CYCLE DATES MODAL
// Edits season.date_start / date_end. Offers to shift existing SOP activity
// dates by the same delta (otherwise Day 1 of the SOP detaches from the cycle
// start). Warns when editing a completed cycle — date_end is the recorded
// harvest date, not just an estimate, and iTrace already snapshotted it.
// ===================================
const EditCycleDatesModal = ({ cycle, activitiesCount, onClose, onSaved }) => {
  const [dateStart, setDateStart] = useState(cycle.startDate || '');
  const [dateEnd, setDateEnd] = useState(cycle.endDate || '');
  const [shiftActivities, setShiftActivities] = useState(activitiesCount > 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isCompleted = cycle.status === 'completed';
  const startChanged = dateStart !== (cycle.startDate || '');
  const endChanged = dateEnd !== (cycle.endDate || '');
  const deltaDays = startChanged && cycle.startDate && dateStart
    ? Math.round((new Date(dateStart) - new Date(cycle.startDate)) / 86400000)
    : 0;

  const handleSave = async () => {
    setError('');
    if (!dateStart) { setError('Start date is required.'); return; }
    if (dateEnd && new Date(dateEnd) <= new Date(dateStart)) {
      setError('End date must be after start date.'); return;
    }
    if (!startChanged && !endChanged) { onClose(); return; }

    setSaving(true);
    const updates = {};
    if (startChanged) updates.date_start = dateStart;
    if (endChanged) updates.date_end = dateEnd || null;

    const { error: seasonErr } = await supabase
      .from('season').update(updates).eq('id', cycle.id);
    if (seasonErr) {
      setSaving(false);
      setError(`Failed to save cycle: ${seasonErr.message}`);
      return;
    }

    // Shift each SOP activity's date_activity by the same delta when requested.
    if (startChanged && shiftActivities && cycle.sopRecordId && deltaDays !== 0) {
      const { data: acts, error: fetchErr } = await supabase
        .from('sop_record_activity')
        .select('id, date_activity')
        .eq('sop_record', cycle.sopRecordId);
      if (fetchErr) {
        setSaving(false);
        setError(`Cycle saved, but failed to fetch activities to shift: ${fetchErr.message}`);
        return;
      }
      for (const a of (acts || [])) {
        if (!a.date_activity) continue;
        const newDate = new Date(new Date(a.date_activity).getTime() + deltaDays * 86400000)
          .toISOString().split('T')[0];
        const { error: upErr } = await supabase
          .from('sop_record_activity').update({ date_activity: newDate }).eq('id', a.id);
        if (upErr) {
          setSaving(false);
          setError(`Cycle saved, but activity shift failed mid-way: ${upErr.message}`);
          return;
        }
      }
    }

    setSaving(false);
    onSaved({
      startDate: updates.date_start ?? cycle.startDate,
      endDate: updates.date_end ?? cycle.endDate
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Edit Cycle Dates</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="text-sm text-gray-600">
            <span className="font-medium">{cycle.name}</span>
          </div>

          {isCompleted && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
              <strong>This cycle is completed.</strong> The end date is the recorded harvest date.
              Changes here rewrite history in Supabase, but will <em>not</em> propagate to
              the iTrace Plant Batch already created for this cycle.
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
            <input
              type="date"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              className="input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date {isCompleted ? '(recorded harvest date)' : '(expected)'}
            </label>
            <input
              type="date"
              value={dateEnd || ''}
              onChange={(e) => setDateEnd(e.target.value)}
              className="input"
            />
          </div>

          {startChanged && activitiesCount > 0 && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
              <p className="text-yellow-900">
                This cycle has <strong>{activitiesCount}</strong> SOP {activitiesCount === 1 ? 'activity' : 'activities'} anchored
                to Day 1 = cycle start date.
              </p>
              <label className="flex items-center gap-2 mt-2 text-yellow-900">
                <input
                  type="checkbox"
                  checked={shiftActivities}
                  onChange={(e) => setShiftActivities(e.target.checked)}
                />
                <span>Shift all activity dates by {deltaDays > 0 ? '+' : ''}{deltaDays} day{Math.abs(deltaDays) === 1 ? '' : 's'}</span>
              </label>
              {!shiftActivities && (
                <p className="text-xs text-yellow-700 mt-1">
                  Unchecked: activity dates stay where they are. Day 1 will no longer match the cycle start.
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">{error}</div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={saving}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="btn-primary px-4 py-2">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

// ===================================
// 🌱 EDIT PLANTING SYSTEM MODAL
// Edits season.planting_system (FK to planting_system_size_unit) and
// season.planting_system_size (numeric quantity). Loads available systems
// filtered by the cycle's produce agro_category — matching the Add Produce
// wizard's behavior so users can't accidentally assign a Fish system to a
// Chili cycle.
// ===================================
const EditPlantingSystemModal = ({ cycle, onClose, onSaved }) => {
  const [systems, setSystems] = useState([]);
  const [systemId, setSystemId] = useState(cycle.plantingSystemId || '');
  const [size, setSize] = useState(cycle.plantingSize != null ? String(cycle.plantingSize) : '');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      let agroCatId = null;
      if (cycle.produceNameId) {
        const { data: prod } = await supabase
          .from('produce_view')
          .select('agro_category_id')
          .eq('id', cycle.produceNameId)
          .maybeSingle();
        agroCatId = prod?.agro_category_id ?? null;
      }
      let query = supabase
        .from('planting_system_size_unit')
        .select('id, size_unit_name, size_unit_name_malay, agri_cat')
        .order('id');
      if (agroCatId) {
        query = query.or(`agri_cat.eq.${agroCatId},agri_cat.is.null`);
      }
      const { data, error: err } = await query;
      if (err) {
        setError(`Failed to load planting systems: ${err.message}`);
        setSystems([]);
      } else {
        setSystems(data || []);
      }
      setLoading(false);
    };
    load();
  }, [cycle.produceNameId]);

  const handleSave = async () => {
    setError('');
    if (!systemId) { setError('Please select a planting system.'); return; }
    const sizeNum = size === '' ? null : Number(size);
    if (size !== '' && (Number.isNaN(sizeNum) || sizeNum < 0)) {
      setError('Quantity must be a non-negative number.'); return;
    }

    setSaving(true);
    const { error: err } = await supabase
      .from('season')
      .update({
        planting_system: Number(systemId),
        planting_system_size: sizeNum
      })
      .eq('id', cycle.id);
    setSaving(false);
    if (err) {
      setError(`Save failed: ${err.message}`);
      return;
    }
    const picked = systems.find(s => s.id === Number(systemId));
    onSaved({
      plantingSystemId: Number(systemId),
      plantingSystemName: picked?.size_unit_name || null,
      plantingSize: sizeNum
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Edit Planting System</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="text-sm text-gray-600">
            <span className="font-medium">{cycle.name}</span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Planting System (type & technology) *
            </label>
            {loading ? (
              <div className="text-sm text-gray-500 py-2">Loading options…</div>
            ) : (
              <select
                value={systemId}
                onChange={(e) => setSystemId(e.target.value)}
                className="input"
              >
                <option value="">Select a planting system…</option>
                {systems.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.size_unit_name}{s.size_unit_name_malay ? ` (${s.size_unit_name_malay})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
            <input
              type="number"
              min="0"
              step="1"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              placeholder="e.g. 500"
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Number of units of the chosen planting system (polybags, holes, beds, tanks, etc.).
              Leave blank if unknown.
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">{error}</div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={saving}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving || loading}
            className="btn-primary px-4 py-2">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

// Local calendar date as YYYY-MM-DD. toISOString() would give the UTC date, which is
// still yesterday in Malaysia before 8am.
const localDateString = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Same format as the Farm Overview tabs: "5 Mar 2026".
const formatCycleDate = (dateStr) => (dateStr
  ? new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  : '-');

const formatQty = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 3 });

const SHEET_MONTHS = { jan: 1, feb: 2, mar: 3, mac: 3, apr: 4, may: 5, mei: 5, jun: 6, jul: 7, aug: 8, ogo: 8, sep: 9, oct: 10, okt: 10, nov: 11, dec: 12, dis: 12 };

// Parse a spreadsheet date cell — 2026-07-28, 28/7/2026 (day first), 28-Jul-2026,
// 28 Julai 2026 or an Excel serial number — into YYYY-MM-DD, or null.
const parseSheetDate = (raw) => {
  const s = String(raw || '').trim();
  let y, m, d, mt;
  if ((mt = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/))) {
    [, y, m, d] = mt;
  } else if ((mt = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/))) {
    [, d, m, y] = mt;
  } else if ((mt = s.match(/^(\d{1,2})[\s\-/.]+([A-Za-z]{3,})[\s\-/.,]+(\d{2,4})$/))) {
    [, d, , y] = mt;
    m = SHEET_MONTHS[mt[2].slice(0, 3).toLowerCase()];
    if (!m) return null;
  } else if (/^\d{5}(\.\d+)?$/.test(s)) {
    return new Date(Date.UTC(1899, 11, 30) + Math.floor(Number(s)) * 86400000).toISOString().slice(0, 10);
  } else {
    return null;
  }
  y = Number(y); m = Number(m); d = Number(d);
  if (y < 100) y += 2000;
  if (y < 2000) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return dt.toISOString().slice(0, 10);
};

// Spreadsheet quantity cell ("12.5", "1,234", "7,8", "10 pcs") → { n, unit }: n is null when
// blank and NaN when not a number; unit is a trailing unit word, if any.
const parseSheetQty = (raw) => {
  let s = String(raw ?? '').trim().replace(/\s/g, '');
  const suffix = s.match(/[a-z]+$/i);
  const unit = suffix ? suffix[0] : null;
  if (unit) s = s.slice(0, -unit.length);
  if (!s || s === '-') return { n: unit ? NaN : null, unit }; // "abc" / "n/a" is not a blank cell
  if (/^\d{1,3}(,\d{3})+(\.\d+)?$/.test(s)) s = s.replace(/,/g, '');
  else s = s.replace(',', '.');
  const n = Number(s);
  return { n: Number.isFinite(n) ? n : NaN, unit };
};

// Parse CSV (comma or semicolon, as Excel saves it) or tab-separated text (cells copied from
// Excel) into non-empty rows of trimmed cells. Handles quoted cells with commas and newlines.
const parseDelimited = (text) => {
  const src = String(text || '').replace(/^\ufeff/, '');
  const first = src.split(/\r?\n/, 1)[0] || '';
  const delim = first.includes('\t') ? '\t' : (first.split(';').length > first.split(',').length ? ';' : ',');
  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (quoted) {
      if (ch !== '"') cell += ch;
      else if (src[i + 1] === '"') { cell += '"'; i += 1; }
      else quoted = false;
    } else if (ch === '"' && cell.trim() === '') {
      quoted = true;
      cell = '';
    } else if (ch === delim) {
      row.push(cell.trim()); cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i += 1;
      row.push(cell.trim()); rows.push(row); row = []; cell = '';
    } else {
      cell += ch;
    }
  }
  if (cell !== '' || row.length) { row.push(cell.trim()); rows.push(row); }
  return rows.map((cells, i) => ({ line: i + 1, cells })).filter(r => r.cells.some(c => c !== ''));
};

// Map columns from a header row (any order, English or Malay names). Returns null when the row
// isn't a recognisable header, in which case the documented column order applies.
const mapSheetColumns = (headerCells, spec) => {
  const idx = {};
  headerCells.forEach((h, i) => {
    const t = String(h || '').toLowerCase();
    const key = Object.keys(spec).find(k => idx[k] === undefined && spec[k].test(t));
    if (key) idx[key] = i;
  });
  return Object.keys(idx).length >= 2 ? idx : null;
};

const csvCell = (v) => {
  const t = String(v ?? '');
  return /[",\r\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
};

// Save rows as a CSV file (with a BOM so Excel reads it as UTF-8).
const downloadCsv = (filename, rows) => {
  const blob = new Blob(['\ufeff' + rows.map(r => r.map(csvCell).join(',')).join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Read a chosen .csv / .txt file as text; Excel workbooks need saving as CSV (or copy-paste) first.
const readSheetFile = (file) => new Promise((resolve, reject) => {
  if (/\.(xlsx?|xlsm|ods|numbers)$/i.test(file.name)) {
    reject(new Error('That is an Excel workbook. In Excel use File → Save As → "CSV (Comma delimited)", or copy the cells and paste them below.'));
    return;
  }
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(new Error('Could not read the file.'));
  reader.readAsText(file);
});

const YIELD_SHEET_COLUMNS = {
  date: /date|tarikh/,
  grade: /grade|gred/,
  qty: /qty|quantity|kuantiti|hasil|amount|jumlah|berat|weight|kg/,
  unit: /unit/,
};
// Example rows of the sample file (using the cycle's own grade names); uploading them unchanged is refused.
const yieldSampleRows = (g1 = 'A', g2 = 'B') => [
  ['28/07/2026', g1, '25', 'kg'],
  ['28/07/2026', g2, '10.5', 'kg'],
  ['30/07/2026', g1, '50', 'kg'],
];

const YIELD_UPLOAD_MODES = [
  { id: 'harvest', name: 'Harvest' },
  { id: 'discard', name: 'Discard' },
  { id: 'paste', name: 'Spreadsheet (CSV / paste)' },
];

// ===================================
// 🌾 UPLOAD YIELD MODAL
// Records a Harvest (one date, one row per grade), a Discard, or many harvest rows pasted
// from a spreadsheet, through the upload_yield_transactions RPC. The RPC writes the same
// rows as the Kapitani app's Add Harvest / Discard screens (yield_stock per cycle × grade,
// yield_transaction at midnight UTC, one completed Harvest diary entry per date) and is
// SECURITY DEFINER because admins usually have no farm_user_role on the farm.
// See supabase/migrations/20260924_yield_financial_upload_rpcs.sql.
// ===================================
const UploadYieldModal = ({ farm, cycles, initialCycleId, yields, onClose, onUploaded }) => {
  const [cycleId, setCycleId] = useState(initialCycleId || cycles[0]?.id || '');
  const [mode, setMode] = useState('harvest');
  const [date, setDate] = useState(localDateString());
  const newRow = () => ({ key: `${Date.now()}-${Math.random()}`, grade: '', amount: '', unit: '' });
  const [rows, setRows] = useState(() => [newRow()]);
  const [discard, setDiscard] = useState({ stock: '', amount: '', reason: '' });
  const [pasteText, setPasteText] = useState('');
  const [parsed, setParsed] = useState(null);
  const [allGrades, setAllGrades] = useState([]);
  const [agriCat, setAgriCat] = useState(null);
  const [units, setUnits] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blockedReason, setBlockedReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dupCount, setDupCount] = useState(0);

  const today = localDateString();
  const cycle = cycles.find(c => c.id === Number(cycleId));
  const cycleStocks = stocks.filter(s => s.season_id === Number(cycleId));
  // Grades of the cycle's category (crops vs fish) plus any grade the cycle already has stock in;
  // all grades, like the app, when the category has none (e.g. livestock).
  const stockGradeIds = new Set(cycleStocks.map(s => s.yield_grade_id));
  const grades = agriCat && allGrades.some(g => g.agri_cat === agriCat)
    ? allGrades.filter(g => g.agri_cat === agriCat || stockGradeIds.has(g.id))
    : allGrades;
  const needsStartDate = mode !== 'discard' && cycle && !cycle.startDate;
  const unitSymbol = (id) => units.find(u => u.id === Number(id))?.unit_symbol || '';
  // A grade that already has stock in this cycle keeps that stock's unit (the RPC enforces it too).
  const lockedUnitFor = (gradeId) => cycleStocks.find(s => s.yield_grade_id === Number(gradeId))?.unit_id || null;
  const rowUnit = (r) => Number(lockedUnitFor(r.grade) || r.unit || 2);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [g, u, r, s] = await Promise.all([
        supabase.from('yield_grade').select('id, grade_name, agri_cat, rank').order('agri_cat').order('rank'),
        supabase.from('unit').select('id, unit_name, unit_symbol').contains('tag', ['harvest']).order('id'),
        supabase.from('transaction_type_reason').select('id, reason, reason_malay').contains('tags', ['discard harvest']).order('id'),
        supabase.rpc('get_yield_stocks_for_farm', { p_farm: farm.id }),
      ]);
      setAllGrades(g.data || []);
      setUnits(u.data || []);
      setReasons(r.data || []);
      setStocks(s.data || []);
      if (s.error) {
        setBlockedReason(s.error.code === '42501'
          ? 'You do not have permission to record yield for this farm.'
          : `Could not load harvest stock: ${s.error.message}`);
      } else if (g.error || u.error || r.error) {
        setBlockedReason(`Could not load form options: ${(g.error || u.error || r.error).message}`);
      }
      setLoading(false);
    })();
  }, [farm.id]);

  // Grades are per agri category (crops vs fish) — follow the cycle's produce.
  useEffect(() => {
    setAgriCat(null);
    if (!cycle?.produceNameId) return undefined;
    let cancelled = false;
    supabase.from('produce_view').select('agro_category_id').eq('id', cycle.produceNameId).maybeSingle()
      .then(({ data }) => { if (!cancelled) setAgriCat(data?.agro_category_id ?? null); });
    return () => { cancelled = true; };
  }, [cycle?.produceNameId]);

  useEffect(() => { setDupCount(0); }, [cycleId, mode, date, rows, discard, parsed]);

  const changeCycle = (id) => {
    setCycleId(id);
    setRows([newRow()]);
    setDiscard({ stock: '', amount: '', reason: '' });
    setParsed(null);
    setError('');
  };

  const updateRow = (key, patch) => setRows(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)));

  const matchGrade = (raw) => {
    const s = String(raw || '').trim().toLowerCase().replace(/^(gred|grade)\s*/, '').trim();
    if (!s) return null;
    return grades.find(g => (g.grade_name || '').toLowerCase() === s)
      || grades.find(g => (g.grade_name || '').toLowerCase().split('/').map(x => x.trim()).includes(s))
      || null;
  };

  const matchUnit = (raw) => {
    const s = String(raw || '').trim().toLowerCase();
    if (!s) return null;
    const alias = { kilo: 'kg', kgs: 'kg', pcs: 'piece', pc: 'piece', pieces: 'piece', biji: 'piece', ekor: 'piece' }[s] || s;
    return units.find(u => (u.unit_symbol || '').toLowerCase() === alias || (u.unit_name || '').toLowerCase() === alias) || null;
  };

  const sampleGrades = [grades[0]?.grade_name || 'A', grades[1]?.grade_name || grades[0]?.grade_name || 'B'];
  const downloadSample = () => downloadCsv('yield_upload_sample.csv', [['Date', 'Grade', 'Quantity', 'Unit'], ...yieldSampleRows(...sampleGrades)]);

  const loadFile = async (input) => {
    const f = input.files?.[0];
    input.value = '';
    if (!f) return;
    try {
      const text = await readSheetFile(f);
      setPasteText(text);
      checkPastedRows(text);
    } catch (e) {
      setParsed(null);
      setError(e.message);
    }
  };

  // Columns Date, Grade, Quantity, Unit (optional) — from the sample CSV or cells copied from Excel.
  // A header row may name them in any order.
  const checkPastedRows = (text = pasteText) => {
    setError('');
    const sheet = parseDelimited(text);
    const cols = sheet.length ? mapSheetColumns(sheet[0].cells, YIELD_SHEET_COLUMNS) : null;
    const col = cols ? { unit: -1, ...cols } : { date: 0, grade: 1, qty: 2, unit: 3 };
    const entries = [];
    const errors = [];
    const unitOfNewGrade = {}; // grade without stock yet → unit its first row will create the stock in
    let skipped = 0;
    sheet.forEach(({ cells, line }, i) => {
      if (i === 0 && cols) return; // header row
      const cell = (k) => (col[k] >= 0 ? cells[col[k]] || '' : '');
      const text = cells.join(' | ');
      const [dateRaw, gradeRaw, qtyRaw, unitCol] = [cell('date'), cell('grade'), cell('qty'), cell('unit')];
      const { n: qty, unit: qtySuffix } = parseSheetQty(qtyRaw);
      const d = parseSheetDate(dateRaw);
      if (i === 0 && !d && (qty === null || Number.isNaN(qty))) return; // unrecognised header row
      if (qty === null || qty === 0) { skipped += 1; return; }
      const problems = [];
      if (!d) problems.push(`unrecognised date "${dateRaw || ''}"`);
      else if (d > today) problems.push(`date ${d} is in the future`);
      const g = matchGrade(gradeRaw);
      if (!g) problems.push(`unknown grade "${gradeRaw || ''}"`);
      if (Number.isNaN(qty) || qty < 0) problems.push(`invalid quantity "${qtyRaw}"`);
      const colUnit = unitCol ? matchUnit(unitCol) : null;
      const sufUnit = qtySuffix ? matchUnit(qtySuffix) : null;
      if (unitCol && !colUnit) problems.push(`unknown unit "${unitCol}"`);
      if (qtySuffix && !sufUnit) problems.push(`unknown unit "${qtySuffix}" in quantity`);
      if (colUnit && sufUnit && colUnit.id !== sufUnit.id) problems.push(`quantity says ${sufUnit.unit_symbol} but the unit column says ${colUnit.unit_symbol}`);
      const locked = g ? (lockedUnitFor(g.id) || unitOfNewGrade[g.id] || null) : null;
      let unit = colUnit || sufUnit || units.find(u => u.id === Number(locked || 2)) || null;
      if (g && unit && locked && unit.id !== locked) problems.push(`grade ${g.grade_name} is recorded in ${unitSymbol(locked)} for this cycle`);
      if (problems.length) { errors.push({ line, text, problems }); return; }
      if (!lockedUnitFor(g.id)) unitOfNewGrade[g.id] = unit?.id || 2;
      entries.push({ line, date: d, grade: g.id, gradeName: g.grade_name, amount: qty, unit: unit?.id || 2, unitSym: unit?.unit_symbol || 'kg' });
    });
    // Refuse the sample file's example rows so they never end up as real harvest.
    const sampleKeys = new Set(yieldSampleRows(...sampleGrades).map(([d, g, q]) => `${parseSheetDate(d)}|${g}|${Number(q)}`));
    const isSample = entries.length > 0 && entries.every(e => sampleKeys.has(`${e.date}|${e.gradeName}|${e.amount}`));
    if (isSample) errors.push({ line: 0, text: 'sample file', problems: ['these are the example rows from the sample file — replace them with your own harvest records'] });
    const totals = {};
    entries.forEach(e => { totals[e.unitSym] = (totals[e.unitSym] || 0) + e.amount; });
    setParsed({ entries: isSample ? [] : entries, errors, skipped, dates: new Set(entries.map(e => e.date)).size, totals: isSample ? {} : totals });
  };

  const buildEntries = () => {
    if (!cycle) throw new Error('Choose a cycle.');
    if (mode === 'paste') {
      if (!parsed) throw new Error('Choose a CSV file or paste your rows, then press "Check rows".');
      if (parsed.errors.length) throw new Error(`Fix the ${parsed.errors.length} row(s) with errors, then check again.`);
      if (!parsed.entries.length) throw new Error('No rows with a quantity to upload.');
      return parsed.entries.map(e => ({ type: 'harvest', date: e.date, grade: e.grade, amount: e.amount, unit: e.unit, gradeName: e.gradeName }));
    }
    if (!date) throw new Error('Choose the date.');
    if (date > today) throw new Error('The date cannot be in the future.');
    if (mode === 'harvest') {
      return rows.map((r, i) => {
        const amount = Number(r.amount);
        if (!r.grade) throw new Error(`Row ${i + 1}: choose a grade.`);
        if (!(amount > 0)) throw new Error(`Row ${i + 1}: enter a quantity greater than 0.`);
        const g = allGrades.find(x => x.id === Number(r.grade));
        return { type: 'harvest', date, grade: Number(r.grade), amount, unit: rowUnit(r), gradeName: g?.grade_name };
      });
    }
    const st = cycleStocks.find(s => s.yield_stock_id === Number(discard.stock));
    const amount = Number(discard.amount);
    if (!st) throw new Error('Choose the harvest stock to discard from.');
    if (!(amount > 0)) throw new Error('Enter a quantity greater than 0.');
    if (amount > Number(st.balance)) throw new Error(`Only ${formatQty(st.balance)} ${st.unit_symbol || ''} of grade ${st.grade_name} left.`);
    if (!discard.reason) throw new Error('Choose a reason.');
    return [{ type: 'discard', date, stock: st.yield_stock_id, grade: st.yield_grade_id, amount, reason: discard.reason, gradeName: st.grade_name }];
  };

  const submit = async (force = false) => {
    setError('');
    let entries;
    try { entries = buildEntries(); } catch (e) { setError(e.message); return; }
    if (!force) {
      // Same date + grade + quantity already recorded for this cycle — likely a double entry.
      const existing = new Set(yields
        .filter(y => y.yield_season_id === Number(cycleId))
        .map(y => `${y.yield_in ? 'in' : 'out'}|${String(y.yield_transaction_date || '').slice(0, 10)}|${y.grade_name}|${Number(y.yield_transaction_amount)}`));
      const dups = entries.filter(e => existing.has(`${e.type === 'harvest' ? 'in' : 'out'}|${e.date}|${e.gradeName}|${e.amount}`));
      if (dups.length) { setDupCount(dups.length); return; }
    }
    setSaving(true);
    const { data, error: rpcErr } = await supabase.rpc('upload_yield_transactions', {
      p_season: Number(cycleId),
      p_entries: entries.map(({ gradeName, ...rest }) => rest),
    });
    if (rpcErr) {
      setSaving(false);
      // The RPC numbers rows by upload order; in paste mode point at the pasted line instead.
      const m = mode === 'paste' ? rpcErr.message.match(/^Row (\d+):/) : null;
      const line = m && parsed?.entries[Number(m[1]) - 1]?.line;
      setError(line ? rpcErr.message.replace(/^Row \d+:/, `Line ${line}:`) : rpcErr.message);
      return;
    }
    onUploaded(Number(cycleId), data);
  };

  const selectedDiscardStock = cycleStocks.find(s => s.yield_stock_id === Number(discard.stock));
  const pickedGrades = rows.map(r => r.grade).filter(Boolean);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Upload Yield Transaction</h3>
          <button type="button" onClick={onClose} disabled={saving} className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cycle *</label>
            <select value={cycleId} onChange={(e) => changeCycle(e.target.value ? Number(e.target.value) : '')} className="input">
              <option value="">Select a cycle…</option>
              {cycles.map(c => (
                <option key={c.id} value={c.id}>{c.produceName} | {formatCycleDate(c.startDate)}</option>
              ))}
            </select>
          </div>

          <div className="flex gap-1 border-b border-gray-200">
            {YIELD_UPLOAD_MODES.map(m => (
              <button
                key={m.id}
                type="button"
                onClick={() => { setMode(m.id); setError(''); }}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                  mode === m.id ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >{m.name}</button>
            ))}
          </div>

          {loading ? (
            <div className="text-sm text-gray-500 py-4">Loading…</div>
          ) : mode === 'paste' ? (
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700 space-y-1">
                <p>
                  Columns: <strong>Date</strong> (day first, e.g. 28/07/2026), <strong>Grade</strong> ({grades.map(g => g.grade_name).join(', ') || 'A, B, C'}),{' '}
                  <strong>Quantity</strong> and optional <strong>Unit</strong> (kg or piece, default kg). One row per grade per day;
                  blank or zero quantities are skipped. Each date also gets one completed Harvest entry in the farm diary.
                </p>
                <p>Fill in the sample in Excel and save it as CSV, then choose the file — or copy the cells from Excel and paste them below.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={downloadSample} className="btn-secondary text-sm flex items-center gap-1">
                  <DocumentTextIcon className="h-4 w-4" /> Download sample file
                </button>
                <label className="btn-secondary text-sm cursor-pointer">
                  Choose CSV file…
                  <input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={(e) => loadFile(e.target)} className="hidden" />
                </label>
              </div>
              <textarea
                rows={8}
                value={pasteText}
                onChange={(e) => { setPasteText(e.target.value); setParsed(null); }}
                placeholder={'28/7/2026\tA\t25\n28/7/2026\tB\t10.5\n30/7/2026\tA\t50'}
                className="input font-mono text-xs"
              />
              <button type="button" onClick={() => checkPastedRows()} disabled={!pasteText.trim() || !cycle} className="btn-secondary text-sm disabled:opacity-50">
                Check rows
              </button>
              {parsed && (
                <div className="space-y-2">
                  <div className={`p-3 rounded text-sm border ${parsed.errors.length ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800'}`}>
                    {parsed.entries.length} row(s) ready
                    {parsed.entries.length > 0 && <> over {parsed.dates} date(s) — {Object.entries(parsed.totals).map(([u, t]) => `${formatQty(t)} ${u}`).join(', ')}</>}
                    {parsed.skipped > 0 && <> · {parsed.skipped} blank skipped</>}
                    {parsed.errors.length > 0 && <> · <strong>{parsed.errors.length} with errors</strong></>}
                  </div>
                  {parsed.errors.length > 0 && (
                    <ul className="text-xs text-red-700 space-y-1 max-h-32 overflow-y-auto">
                      {parsed.errors.map(er => (
                        <li key={er.line}>
                          {er.line > 0 && <>Line {er.line} (<span className="font-mono">{er.text}</span>): </>}
                          {er.problems.join('; ')}
                        </li>
                      ))}
                    </ul>
                  )}
                  {parsed.entries.length > 0 && (
                    <div className="max-h-48 overflow-y-auto border border-gray-200 rounded">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="text-left px-2 py-1 font-medium text-gray-500">Line</th>
                            <th className="text-left px-2 py-1 font-medium text-gray-500">Date</th>
                            <th className="text-left px-2 py-1 font-medium text-gray-500">Grade</th>
                            <th className="text-right px-2 py-1 font-medium text-gray-500">Quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parsed.entries.map(e => (
                            <tr key={e.line} className="border-t border-gray-100">
                              <td className="px-2 py-1 text-gray-500">{e.line}</td>
                              <td className="px-2 py-1">{e.date}</td>
                              <td className="px-2 py-1">{e.gradeName}</td>
                              <td className="px-2 py-1 text-right">{formatQty(e.amount)} {e.unitSym}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {mode === 'harvest' ? 'Harvest date *' : 'Discard date *'}
                </label>
                <input type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} className="input max-w-xs" />
              </div>

              {mode === 'harvest' ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-700">
                    <div className="col-span-5">Grade *</div>
                    <div className="col-span-4">Quantity *</div>
                    <div className="col-span-2">Unit</div>
                  </div>
                  {rows.map(r => {
                    const locked = lockedUnitFor(r.grade);
                    return (
                      <div key={r.key} className="grid grid-cols-12 gap-2 items-center">
                        <select value={r.grade} onChange={(e) => updateRow(r.key, { grade: e.target.value })} className="input text-sm col-span-5">
                          <option value="">Select grade…</option>
                          {grades.filter(g => String(g.id) === r.grade || !pickedGrades.includes(String(g.id))).map(g => (
                            <option key={g.id} value={g.id}>{g.grade_name}</option>
                          ))}
                        </select>
                        <input
                          type="number" min="0" step="any" value={r.amount} placeholder="e.g. 25"
                          onChange={(e) => updateRow(r.key, { amount: e.target.value })}
                          className="input text-sm col-span-4"
                        />
                        <select
                          value={rowUnit(r)} disabled={!!locked}
                          onChange={(e) => updateRow(r.key, { unit: e.target.value })}
                          title={locked ? 'This grade already has stock in this unit for the cycle' : undefined}
                          className="input text-sm col-span-2 disabled:bg-gray-50"
                        >
                          {units.map(u => <option key={u.id} value={u.id}>{u.unit_symbol}</option>)}
                        </select>
                        <div className="col-span-1 text-right">
                          {rows.length > 1 && (
                            <button type="button" onClick={() => setRows(prev => prev.filter(x => x.key !== r.key))} className="text-gray-400 hover:text-red-600" title="Remove row">
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {rows.length < grades.length && (
                    <button type="button" onClick={() => setRows(prev => [...prev, newRow()])} className="text-sm text-green-700 hover:text-green-800 flex items-center gap-1">
                      <PlusIcon className="h-4 w-4" /> Add another grade
                    </button>
                  )}
                  <p className="text-xs text-gray-500">
                    One completed Harvest entry is also added to the farm diary for this date, as in the Kapitani app.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cycleStocks.length === 0 ? (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-900">
                      This cycle has no harvest recorded yet, so there is nothing to discard.
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Harvest stock *</label>
                        <select value={discard.stock} onChange={(e) => setDiscard(d => ({ ...d, stock: e.target.value }))} className="input text-sm">
                          <option value="">Select grade…</option>
                          {cycleStocks.map(s => (
                            <option key={s.yield_stock_id} value={s.yield_stock_id} disabled={!(Number(s.balance) > 0)}>
                              Grade {s.grade_name} — {formatQty(s.balance)} {s.unit_symbol || ''} left
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quantity *{selectedDiscardStock ? ` (${selectedDiscardStock.unit_symbol || ''})` : ''}
                          </label>
                          <input
                            type="number" min="0" step="any" value={discard.amount}
                            max={selectedDiscardStock ? Number(selectedDiscardStock.balance) : undefined}
                            onChange={(e) => setDiscard(d => ({ ...d, amount: e.target.value }))}
                            className="input text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                          <select value={discard.reason} onChange={(e) => setDiscard(d => ({ ...d, reason: e.target.value }))} className="input text-sm">
                            <option value="">Select reason…</option>
                            {reasons.map(r => (
                              <option key={r.id} value={r.reason_malay || r.reason}>{r.reason}{r.reason_malay ? ` (${r.reason_malay})` : ''}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {needsStartDate && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-900">
              This cycle has no start date. Set it first (Cycle tab → edit dates), because each harvest diary
              entry is counted in days from the start of the cycle.
            </div>
          )}
          {dupCount > 0 && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-900">
              {dupCount} of these row(s) match yield already recorded for this cycle (same date, grade and quantity).
              Check that this is not a double entry before uploading.
            </div>
          )}
          {blockedReason && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">{blockedReason}</div>
          )}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">{error}</div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
          <button
            type="button"
            onClick={() => submit(dupCount > 0)}
            disabled={saving || loading || !!blockedReason || !cycle || needsStartDate}
            className="btn-primary px-4 py-2 disabled:opacity-50"
          >
            {saving ? 'Uploading…' : dupCount > 0 ? 'Upload anyway' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Fetch every row of a PostgREST query in pages (the API caps a response at 1000 rows;
// e.g. there are ~3,600 global pesticides).
const fetchAllRows = async (buildQuery, pageSize = 1000) => {
  const out = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await buildQuery().range(from, from + pageSize - 1);
    if (error) throw error;
    out.push(...(data || []));
    if (!data || data.length < pageSize) return out;
  }
};

const FINANCIAL_SHEET_HEADER = ['Date', 'Type', 'Category', 'Amount (RM)', 'Description', 'Vendor', 'Cycle'];
const FINANCIAL_SHEET_COLUMNS = {
  date: /date|tarikh/,
  type: /^type|^jenis/,
  category: /categ|kategori/,
  amount: /amount|amaun|jumlah|harga|nilai|rm/,
  description: /desc|keterangan|butiran|note|catatan|perkara/,
  vendor: /vendor|supplier|pembekal|kedai/,
  cycle: /cycle|season|musim|kitaran/,
};
// Example rows of the sample file; uploading them unchanged is refused.
const financialSampleRows = (cycleName = '') => [
  ['20/07/2026', 'Expense', 'Fertiliser', '45.50', 'NPK 15-15-15, 2 bags', 'Kedai Baja Ali', ''],
  ['31/07/2026', 'Expense', 'Salary/Wages', '300.00', 'Upah pekerja Julai', '', cycleName],
  ['25/07/2026', 'Income', 'Direct Sales', '120.00', 'Jualan cili 30kg - Pasar Tani', '', ''],
  ['01/08/2026', 'Income', 'Grant', '1000.00', 'Geran pertanian', '', ''],
];
// "Expense" / "Income" in English or Malay → 'expense' | 'income'; null when blank, undefined when unknown.
const parseDirection = (raw) => {
  const t = String(raw || '').trim().toLowerCase();
  if (!t) return null;
  if (/^(exp|belanja|perbelanjaan|kos|cost|keluar|debit)/.test(t)) return 'expense';
  if (/^(inc|pendapatan|jualan|sale|masuk|terima|credit)/.test(t)) return 'income';
  return undefined;
};
const normName = (x) => String(x || '').toLowerCase().replace(/fertilizer/g, 'fertiliser').replace(/\s+/g, ' ').trim();

const RECEIPT_TYPES = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/gif': 'gif' };
const RECEIPT_MAX_BYTES = 10 * 1024 * 1024;

// ===================================
// 💰 UPLOAD FINANCIAL MODAL
// Records an expense or income receipt (one financial_transaction per item) through the
// upload_financial_transactions RPC, mirroring the Kapitani app's Add Expense / Add Income:
// supplier org for expenses, optional receipt image in storage bucket 'file' under
// fintransaction/, material purchases stocked into the farm's inventory, and harvest sales
// written as orders + order_cart + a Sale yield_transaction.
// ===================================
const UploadFinancialModal = ({ farm, cycles, financials, onClose, onUploaded }) => {
  const [mode, setMode] = useState('form'); // 'form' | 'sheet'
  const [sheetText, setSheetText] = useState('');
  const [sheet, setSheet] = useState(null);
  const [sheetDupCount, setSheetDupCount] = useState(0);
  const [direction, setDirection] = useState('expense');
  const [date, setDate] = useState(localDateString());
  const [description, setDescription] = useState('');
  const [vendor, setVendor] = useState('');
  const [file, setFile] = useState(null);
  const newItem = () => ({
    key: `${Date.now()}-${Math.random()}`, subtype: '', amount: '', note: '', season: '',
    material: '', quantity: '', unit: '', yield_stock: '', sold_quantity: '',
  });
  const [items, setItems] = useState(() => [newItem()]);
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [farmOrg, setFarmOrg] = useState(null);
  const [materialsByType, setMaterialsByType] = useState({}); // material_type → rows, or 'error'
  const requestedMaterialTypes = useRef(new Set());
  const [materialRetry, setMaterialRetry] = useState(0);
  const [loading, setLoading] = useState(true);
  const [blockedReason, setBlockedReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const today = localDateString();
  const isIncome = direction === 'income';
  const subtypeById = (id) => subtypes.find(s => s.id === Number(id));
  const tagsOf = (st) => st?.tags || [];
  const takesMaterial = (st) => !isIncome && tagsOf(st).includes('material') && !!st?.material_type;
  const sellsHarvest = (st) => isIncome && tagsOf(st).includes('harvest');

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [t, st, u, s, f] = await Promise.all([
        supabase.from('financial_transaction_type').select('id, name_type, isincome').order('id'),
        supabase.from('financial_transaction_subtype').select('id, sub_type_name, sub_type_name_malay, type, material_type, tags').order('id'),
        supabase.from('unit').select('id, unit_name, unit_symbol').order('unit_name'),
        supabase.rpc('get_yield_stocks_for_farm', { p_farm: farm.id }),
        supabase.from('farm').select('organisation').eq('id', farm.id).maybeSingle(),
      ]);
      setTypes(t.data || []);
      setSubtypes(st.data || []);
      setUnits(u.data || []);
      setStocks(s.data || []);
      setFarmOrg(f.data?.organisation ?? null);
      if (s.error) {
        setBlockedReason(s.error.code === '42501'
          ? 'You do not have permission to record financials for this farm.'
          : `Could not load harvest stock: ${s.error.message}`);
      } else if (t.error || st.error || u.error) {
        setBlockedReason(`Could not load form options: ${(t.error || st.error || u.error).message}`);
      }
      setLoading(false);
    })();
  }, [farm.id]);

  // Load the material catalog for each material type in use: global + the farm org's own of that
  // type, plus the org's untyped materials (added from the SOP builder / Add Stock) so they are
  // reused instead of duplicated — the RPC gives them this type.
  const neededMaterialTypes = [...new Set(items.map(it => subtypeById(it.subtype)).filter(takesMaterial).map(st => st.material_type))].join(',');
  useEffect(() => {
    if (loading) return;
    neededMaterialTypes.split(',').filter(Boolean).map(Number).forEach(async (mt) => {
      if (requestedMaterialTypes.current.has(mt)) return;
      requestedMaterialTypes.current.add(mt);
      try {
        const rows = await fetchAllRows(() => {
          const q = supabase.from('material').select('id, name_material');
          return (farmOrg
            ? q.or(`and(material_type.eq.${mt},created_by_org.is.null),and(material_type.eq.${mt},created_by_org.eq.${farmOrg}),and(material_type.is.null,created_by_org.eq.${farmOrg})`)
            : q.eq('material_type', mt).is('created_by_org', null)
          ).order('name_material').order('id');
        });
        setMaterialsByType(m => ({ ...m, [mt]: rows }));
      } catch (e) {
        requestedMaterialTypes.current.delete(mt);
        setMaterialsByType(m => ({ ...m, [mt]: 'error' }));
      }
    });
  }, [neededMaterialTypes, loading, farmOrg, materialRetry]);

  const updateItem = (key, patch) => setItems(prev => prev.map(it => (it.key === key ? { ...it, ...patch } : it)));

  const changeDirection = (d) => {
    if (d === direction || saving) return;
    setDirection(d);
    setItems([newItem()]);
    setError('');
  };

  const pickFile = (input) => {
    const f = input.files?.[0] || null;
    setError('');
    const problem = !f ? null
      : !RECEIPT_TYPES[f.type] ? 'Receipt must be a PNG, JPEG or GIF image.'
      : f.size > RECEIPT_MAX_BYTES ? 'Receipt image must be 10 MB or smaller.'
      : null;
    if (problem) {
      // Clear both the input and the state so a previously chosen receipt isn't uploaded instead.
      input.value = '';
      setFile(null);
      setError(problem);
      return;
    }
    setFile(f);
  };

  const buildItems = () => {
    const usedStock = {};
    return items.map((it, i) => {
      const n = i + 1;
      const st = subtypeById(it.subtype);
      if (!st) throw new Error(`Item ${n}: choose a category.`);
      const amount = Number(it.amount);
      if (!(amount > 0)) throw new Error(`Item ${n}: enter an amount greater than 0.`);
      const out = { subtype: st.id, amount, description: it.note.trim() || null };
      if (tagsOf(st).includes('seasoncost') && it.season) out.season = Number(it.season);
      if (takesMaterial(st) && it.material) {
        const qty = Number(it.quantity);
        if (!(qty > 0)) throw new Error(`Item ${n}: enter the quantity bought.`);
        if (!it.unit) throw new Error(`Item ${n}: choose the unit of the quantity bought.`);
        Object.assign(out, { material: Number(it.material), quantity: qty, unit: Number(it.unit) });
      } else if (takesMaterial(st) && it.quantity) {
        throw new Error(`Item ${n}: choose the material bought, or clear the quantity.`);
      }
      if (sellsHarvest(st) && it.yield_stock) {
        const stock = stocks.find(s => s.yield_stock_id === Number(it.yield_stock));
        const qty = Number(it.sold_quantity);
        if (!stock) throw new Error(`Item ${n}: choose the harvest sold.`);
        if (!(qty > 0)) throw new Error(`Item ${n}: enter the quantity sold.`);
        // rounded so 1.1 + 2.2 doesn't exceed a 3.3 balance through float error
        usedStock[stock.yield_stock_id] = Math.round(((usedStock[stock.yield_stock_id] || 0) + qty) * 1e6) / 1e6;
        if (usedStock[stock.yield_stock_id] > Number(stock.balance)) {
          throw new Error(`Item ${n}: only ${formatQty(stock.balance)} ${stock.unit_symbol || ''} of grade ${stock.grade_name} left.`);
        }
        Object.assign(out, { yield_stock: stock.yield_stock_id, sold_quantity: qty });
      }
      return out;
    });
  };

  const submit = async () => {
    setError('');
    let payloadItems;
    try {
      if (!date) throw new Error('Choose the transaction date.');
      if (date > today) throw new Error('The transaction date cannot be in the future.');
      payloadItems = buildItems();
    } catch (e) {
      setError(e.message);
      return;
    }
    setSaving(true);
    let uploadedPath = null;
    let rolledBack = true; // false when we can't tell whether the server saved the rows
    try {
      let documentUrl = '';
      if (file) {
        // Same bucket and naming as the app: file/fintransaction/<microseconds>.<ext>
        uploadedPath = `fintransaction/${Date.now()}${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}.${RECEIPT_TYPES[file.type]}`;
        const { error: upErr } = await supabase.storage.from('file').upload(uploadedPath, file, { contentType: file.type, upsert: false });
        if (upErr) { uploadedPath = null; throw new Error(`Receipt upload failed: ${upErr.message}`); }
        documentUrl = supabase.storage.from('file').getPublicUrl(uploadedPath).data.publicUrl;
      }
      const { data, error: rpcErr } = await supabase.rpc('upload_financial_transactions', {
        p_farm: farm.id,
        p_header: { direction, date, description: description.trim(), vendor: isIncome ? '' : vendor.trim(), document: documentUrl },
        p_items: payloadItems,
      });
      if (rpcErr) {
        // A database error (SQLSTATE code) means the transaction rolled back; a network error doesn't
        // tell us whether it committed.
        rolledBack = !!rpcErr.code;
        throw new Error(rolledBack ? rpcErr.message
          : `Could not confirm the upload (${rpcErr.message}). Check the Financial tab before trying again.`);
      }
      onUploaded(data);
    } catch (e) {
      // Nothing was recorded, so don't leave the receipt behind in storage.
      if (uploadedPath && rolledBack) {
        try { await supabase.storage.from('file').remove([uploadedPath]); } catch (_) { /* best effort */ }
      }
      setError(e.message);
      setSaving(false);
    }
  };

  const typesForDirection = types.filter(t => !!t.isincome === isIncome);
  const saleStocks = stocks.filter(s => Number(s.balance) > 0);

  // ---- Spreadsheet mode: one row = one transaction (no receipt / inventory / harvest links) ----
  const directionOf = (st) => (types.find(t => t.id === st.type)?.isincome ? 'income' : 'expense');
  const seasonCostNames = subtypes.filter(st => tagsOf(st).includes('seasoncost')).map(st => st.sub_type_name).join(', ');
  const sampleCycleName = cycles[0]?.produceName || '';

  useEffect(() => { setSheetDupCount(0); }, [sheet, mode]);

  const downloadSample = () => downloadCsv('financial_upload_sample.csv', [FINANCIAL_SHEET_HEADER, ...financialSampleRows(sampleCycleName)]);

  const loadSheetFile = async (input) => {
    const f = input.files?.[0];
    input.value = '';
    if (!f) return;
    try {
      const text = await readSheetFile(f);
      setSheetText(text);
      checkSheet(text);
    } catch (e) {
      setSheet(null);
      setError(e.message);
    }
  };

  const checkSheet = (text = sheetText) => {
    setError('');
    const rows = parseDelimited(text);
    const cols = rows.length ? mapSheetColumns(rows[0].cells, FINANCIAL_SHEET_COLUMNS) : null;
    if (cols && (cols.date === undefined || cols.category === undefined || cols.amount === undefined)) {
      setSheet(null);
      setError('The header row needs at least Date, Category and Amount columns (see the sample file).');
      return;
    }
    const col = cols
      ? { type: -1, description: -1, vendor: -1, cycle: -1, ...cols }
      : { date: 0, type: 1, category: 2, amount: 3, description: 4, vendor: 5, cycle: 6 };
    const entries = [];
    const errors = [];
    let skipped = 0;
    rows.forEach(({ cells, line }, i) => {
      if (i === 0 && cols) return; // header row
      const cell = (k) => (col[k] >= 0 ? cells[col[k]] || '' : '');
      const catRaw = cell('category');
      const amountRaw = cell('amount');
      const d = parseSheetDate(cell('date'));
      const amt = parseSheetQty(amountRaw.replace(/^(rm|myr)\s*/i, ''));
      if (i === 0 && !d && (amt.n === null || Number.isNaN(amt.n))) return; // unrecognised header row
      if (!catRaw && amt.n === null) { skipped += 1; return; }
      const problems = [];
      if (!d) problems.push(`unrecognised date "${cell('date')}"`);
      else if (d > today) problems.push(`date ${d} is in the future`);
      let dir = parseDirection(cell('type'));
      if (dir === undefined) problems.push(`type "${cell('type')}" should be Expense or Income`);
      const matches = subtypes.filter(st => [st.sub_type_name, st.sub_type_name_malay, `${st.sub_type_name} (${st.sub_type_name_malay})`]
        .some(n => n && normName(n) === normName(catRaw)));
      const candidates = dir ? matches.filter(st => directionOf(st) === dir) : matches;
      let st = null;
      if (!catRaw) problems.push('category is missing');
      else if (!matches.length) problems.push(`unknown category "${catRaw}"`);
      else if (!candidates.length) problems.push(`"${catRaw}" is not an ${dir} category`);
      else if (candidates.length > 1) problems.push(`"${catRaw}" is both an expense and an income category — fill in the Type column`);
      else { st = candidates[0]; dir = directionOf(st); }
      const amount = amt.n;
      if (!(amount > 0 && amount < 1e12) || (amt.unit && !/^(rm|myr)$/i.test(amt.unit))) problems.push(`invalid amount "${amountRaw}"`);
      const vendorRaw = cell('vendor');
      if (vendorRaw && dir === 'income') problems.push('Vendor is for expenses only — put the buyer in Description');
      const cycleRaw = cell('cycle');
      let cyc = null;
      if (cycleRaw) {
        const cm = cycles.filter(c => normName(c.produceName) === normName(cycleRaw));
        if (!cm.length) problems.push(`unknown cycle "${cycleRaw}"`);
        else if (cm.length > 1) problems.push(`more than one cycle is called "${cycleRaw}"`);
        else if (st && !tagsOf(st).includes('seasoncost')) problems.push(`a cycle can only be set for ${seasonCostNames}`);
        else cyc = cm[0];
      }
      if (problems.length) { errors.push({ line, text: cells.join(' | '), problems }); return; }
      entries.push({
        line, date: d, direction: dir, subtype: st.id, subtypeName: st.sub_type_name, amount,
        description: cell('description'), vendor: dir === 'expense' ? vendorRaw : '', season: cyc?.id || null, cycleName: cyc?.produceName || '',
      });
    });
    const sampleKeys = new Set(financialSampleRows(sampleCycleName).map(([d, , c, a]) => `${parseSheetDate(d)}|${normName(c)}|${Number(a)}`));
    const isSample = entries.length > 0 && entries.every(e => sampleKeys.has(`${e.date}|${normName(e.subtypeName)}|${e.amount}`));
    if (isSample) errors.push({ line: 0, text: '', problems: ['these are the example rows from the sample file — replace them with your own transactions'] });
    const kept = isSample ? [] : entries;
    setSheet({
      entries: kept, errors, skipped,
      totalIn: kept.filter(e => e.direction === 'income').reduce((t, e) => t + e.amount, 0),
      totalOut: kept.filter(e => e.direction === 'expense').reduce((t, e) => t + e.amount, 0),
    });
  };

  const submitSheet = async (force = false) => {
    setError('');
    if (!sheet) { setError('Choose a CSV file or paste your rows, then press "Check rows".'); return; }
    if (sheet.errors.length) { setError(`Fix the ${sheet.errors.length} row(s) with errors, then check again.`); return; }
    if (!sheet.entries.length) { setError('No rows to upload.'); return; }
    if (!force) {
      // Same date + category + amount already recorded on this farm — likely a double entry.
      const existing = new Set((financials || []).map(f => `${f.in ? 'income' : 'expense'}|${f.date_transaction}|${f.financial_transaction_subtype}|${Number(f.amount)}`));
      const dups = sheet.entries.filter(e => existing.has(`${e.direction}|${e.date}|${e.subtype}|${e.amount}`)).length;
      if (dups) { setSheetDupCount(dups); return; }
    }
    setSaving(true);
    const { data, error: rpcErr } = await supabase.rpc('upload_financial_rows', {
      p_farm: farm.id,
      p_rows: sheet.entries.map(e => ({
        header: { direction: e.direction, date: e.date, description: e.description, vendor: e.vendor, document: '' },
        item: { subtype: e.subtype, amount: e.amount, ...(e.season ? { season: e.season } : {}) },
      })),
    });
    if (rpcErr) {
      setSaving(false);
      if (!rpcErr.code) {
        setError(`Could not confirm the upload (${rpcErr.message}). Check the Financial tab before trying again.`);
        return;
      }
      // The RPC numbers rows in upload order; point at the spreadsheet line instead.
      const m = rpcErr.message.match(/^Row (\d+):/);
      const line = m && sheet.entries[Number(m[1]) - 1]?.line;
      setError(line ? rpcErr.message.replace(/^Row \d+:/, `Line ${line}:`) : rpcErr.message);
      return;
    }
    onUploaded(data);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Upload Financial Transaction</h3>
          <button type="button" onClick={onClose} disabled={saving} className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="flex gap-1 border-b border-gray-200">
            {[{ id: 'form', name: 'Form' }, { id: 'sheet', name: 'Spreadsheet (CSV / paste)' }].map(m => (
              <button
                key={m.id}
                type="button"
                onClick={() => { if (!saving) { setMode(m.id); setError(''); } }}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                  mode === m.id ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >{m.name}</button>
            ))}
          </div>

          {loading ? (
            <div className="text-sm text-gray-500 py-4">Loading…</div>
          ) : mode === 'sheet' ? (
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700 space-y-1">
                <p>
                  One row per transaction. Columns: <strong>Date</strong> (day first, e.g. 20/07/2026), <strong>Type</strong> (Expense or Income),{' '}
                  <strong>Category</strong>, <strong>Amount (RM)</strong>, and optional <strong>Description</strong>,{' '}
                  <strong>Vendor</strong> (expenses) and <strong>Cycle</strong> (only for {seasonCostNames || 'season costs'}).
                </p>
                <p>
                  Fill in the sample in Excel and save it as CSV, then choose the file — or copy the cells and paste them below.
                  To attach a receipt, stock a purchase into inventory or link a sale to harvest, use the Form tab.
                </p>
                <details>
                  <summary className="cursor-pointer text-green-700">Category names you can use</summary>
                  <div className="mt-1 space-y-1">
                    {types.map(t => (
                      <p key={t.id}>
                        <strong>{t.name_type}</strong> ({t.isincome ? 'Income' : 'Expense'}):{' '}
                        {subtypes.filter(st => st.type === t.id).map(st => st.sub_type_name).join(', ')}
                      </p>
                    ))}
                  </div>
                </details>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={downloadSample} className="btn-secondary text-sm flex items-center gap-1">
                  <DocumentTextIcon className="h-4 w-4" /> Download sample file
                </button>
                <label className="btn-secondary text-sm cursor-pointer">
                  Choose CSV file…
                  <input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={(e) => loadSheetFile(e.target)} className="hidden" />
                </label>
              </div>
              <textarea
                rows={7}
                value={sheetText}
                onChange={(e) => { setSheetText(e.target.value); setSheet(null); }}
                placeholder={'20/07/2026\tExpense\tFertiliser\t45.50\tNPK 2 bags\tKedai Baja Ali\n25/07/2026\tIncome\tDirect Sales\t120\tJualan cili'}
                className="input font-mono text-xs"
              />
              <button type="button" onClick={() => checkSheet()} disabled={!sheetText.trim()} className="btn-secondary text-sm disabled:opacity-50">
                Check rows
              </button>
              {sheet && (
                <div className="space-y-2">
                  <div className={`p-3 rounded text-sm border ${sheet.errors.length ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800'}`}>
                    {sheet.entries.length} row(s) ready
                    {sheet.entries.length > 0 && <> — income RM {sheet.totalIn.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}, expenses RM {sheet.totalOut.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>}
                    {sheet.skipped > 0 && <> · {sheet.skipped} blank skipped</>}
                    {sheet.errors.length > 0 && <> · <strong>{sheet.errors.length} with errors</strong></>}
                  </div>
                  {sheet.errors.length > 0 && (
                    <ul className="text-xs text-red-700 space-y-1 max-h-32 overflow-y-auto">
                      {sheet.errors.map(er => (
                        <li key={er.line}>
                          {er.line > 0 && <>Line {er.line} (<span className="font-mono">{er.text}</span>): </>}
                          {er.problems.join('; ')}
                        </li>
                      ))}
                    </ul>
                  )}
                  {sheet.entries.length > 0 && (
                    <div className="max-h-56 overflow-auto border border-gray-200 rounded">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            {['Line', 'Date', 'Type', 'Category', 'Amount', 'Description', 'Vendor', 'Cycle'].map(h => (
                              <th key={h} className={`px-2 py-1 font-medium text-gray-500 ${h === 'Amount' ? 'text-right' : 'text-left'}`}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sheet.entries.map(e => (
                            <tr key={e.line} className="border-t border-gray-100">
                              <td className="px-2 py-1 text-gray-500">{e.line}</td>
                              <td className="px-2 py-1 whitespace-nowrap">{e.date}</td>
                              <td className={`px-2 py-1 ${e.direction === 'income' ? 'text-green-700' : 'text-red-700'}`}>{e.direction === 'income' ? 'Income' : 'Expense'}</td>
                              <td className="px-2 py-1">{e.subtypeName}</td>
                              <td className="px-2 py-1 text-right whitespace-nowrap">RM {e.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td className="px-2 py-1">{e.description || '-'}</td>
                              <td className="px-2 py-1">{e.vendor || '-'}</td>
                              <td className="px-2 py-1">{e.cycleName || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              {sheetDupCount > 0 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-900">
                  {sheetDupCount} of these row(s) match a transaction already recorded on this farm (same date, category and amount).
                  Check that this is not a double entry before uploading.
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
                {[{ id: 'expense', name: 'Expense' }, { id: 'income', name: 'Income' }].map(d => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => changeDirection(d.id)}
                    disabled={saving}
                    className={`px-4 py-1.5 text-sm font-medium ${direction === d.id ? 'bg-green-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >{d.name}</button>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Transaction date *</label>
                  <input type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} className="input" />
                </div>
                {!isIncome && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vendor / supplier</label>
                    <input type="text" value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="e.g. Kedai Baja Ali" className="input" />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text" value={description} maxLength={255} onChange={(e) => setDescription(e.target.value)}
                  placeholder={isIncome ? 'e.g. Buyer name / invoice no.' : 'e.g. Receipt no. / what was bought'}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Receipt image</label>
                <input type="file" accept="image/png,image/jpeg,image/gif" onChange={(e) => pickFile(e.target)} className="block text-sm text-gray-600" />
                {file && <p className="text-xs text-gray-500 mt-1">{file.name} ({Math.ceil(file.size / 1024)} KB)</p>}
              </div>

              {items.map((it, idx) => {
                const st = subtypeById(it.subtype);
                const tags = tagsOf(st);
                const saleStock = stocks.find(s => s.yield_stock_id === Number(it.yield_stock));
                return (
                  <div key={it.key} className="border border-gray-200 rounded-lg p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-500 uppercase">Item {idx + 1}</span>
                      {items.length > 1 && (
                        <button type="button" onClick={() => setItems(prev => prev.filter(x => x.key !== it.key))} className="text-gray-400 hover:text-red-600" title="Remove item">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Category *</label>
                        <select
                          value={it.subtype}
                          onChange={(e) => updateItem(it.key, { subtype: e.target.value, season: '', material: '', quantity: '', unit: '', yield_stock: '', sold_quantity: '' })}
                          className="input text-sm"
                        >
                          <option value="">Select category…</option>
                          {typesForDirection.map(t => (
                            <optgroup key={t.id} label={t.name_type}>
                              {subtypes.filter(s => s.type === t.id).map(s => (
                                <option key={s.id} value={s.id}>{s.sub_type_name}{s.sub_type_name_malay ? ` (${s.sub_type_name_malay})` : ''}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Amount (RM) *</label>
                        <input
                          type="number" min="0" step="0.01" value={it.amount} placeholder="0.00"
                          onChange={(e) => updateItem(it.key, { amount: e.target.value })}
                          className="input text-sm"
                        />
                      </div>
                    </div>

                    {tags.includes('seasoncost') && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Cycle (optional)</label>
                        <select value={it.season} onChange={(e) => updateItem(it.key, { season: e.target.value })} className="input text-sm">
                          <option value="">Not linked to a cycle</option>
                          {cycles.map(c => <option key={c.id} value={c.id}>{c.produceName} | {formatCycleDate(c.startDate)}</option>)}
                        </select>
                      </div>
                    )}

                    {takesMaterial(st) && (
                      <div className="bg-gray-50 rounded p-3 space-y-2">
                        <div className="text-xs font-medium text-gray-700">Add to farm inventory (optional)</div>
                        {materialsByType[st.material_type] === 'error' ? (
                          <div className="text-xs text-red-700">
                            Could not load materials.{' '}
                            <button
                              type="button"
                              className="underline"
                              onClick={() => {
                                setMaterialsByType(m => { const next = { ...m }; delete next[st.material_type]; return next; });
                                setMaterialRetry(n => n + 1);
                              }}
                            >Retry</button>
                          </div>
                        ) : materialsByType[st.material_type] ? (
                          <MaterialSelect
                            value={it.material}
                            onChange={(v) => updateItem(it.key, { material: v })}
                            options={materialsByType[st.material_type]}
                            orgId={farmOrg}
                            materialType={st.material_type}
                            onCreated={(m) => setMaterialsByType(prev => ({
                              ...prev,
                              [st.material_type]: [...(prev[st.material_type] || []), m].sort((a, b) => (a.name_material || '').localeCompare(b.name_material || '')),
                            }))}
                            placeholder={`Select ${st.sub_type_name.toLowerCase()} bought…`}
                          />
                        ) : (
                          <div className="text-xs text-gray-500">Loading materials…</div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number" min="0" step="any" value={it.quantity} placeholder="Quantity bought"
                            onChange={(e) => updateItem(it.key, { quantity: e.target.value })}
                            className="input text-sm"
                          />
                          <select value={it.unit} onChange={(e) => updateItem(it.key, { unit: e.target.value })} className="input text-sm">
                            <option value="">— unit —</option>
                            {units.map(u => <option key={u.id} value={u.id}>{u.unit_symbol ? `${u.unit_symbol} (${u.unit_name})` : u.unit_name}</option>)}
                          </select>
                        </div>
                        <p className="text-xs text-gray-500">Stocks the item into this farm's inventory (used by myGAP 4-3 / 4-5).</p>
                      </div>
                    )}

                    {!isIncome && tags.includes('produce') && (
                      <p className="text-xs text-gray-500">Recorded as an expense only — seed / fry stock is not added to inventory from here.</p>
                    )}

                    {sellsHarvest(st) && (
                      <div className="bg-gray-50 rounded p-3 space-y-2">
                        <div className="text-xs font-medium text-gray-700">Harvest sold (optional)</div>
                        {saleStocks.length === 0 ? (
                          <div className="text-xs text-gray-500">No harvest stock left on this farm to link.</div>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <select
                              value={it.yield_stock}
                              onChange={(e) => updateItem(it.key, { yield_stock: e.target.value, sold_quantity: '' })}
                              className="input text-sm sm:col-span-2"
                            >
                              <option value="">Not linked to harvest</option>
                              {saleStocks.map(s => (
                                <option key={s.yield_stock_id} value={s.yield_stock_id}>
                                  {s.season_name || `Cycle ${s.season_id}`} · Grade {s.grade_name} · {formatQty(s.balance)} {s.unit_symbol || ''} left
                                </option>
                              ))}
                            </select>
                            <input
                              type="number" min="0" step="any" value={it.sold_quantity} disabled={!it.yield_stock}
                              placeholder={saleStock ? `Qty sold (${saleStock.unit_symbol || ''})` : 'Qty sold'}
                              onChange={(e) => updateItem(it.key, { sold_quantity: e.target.value })}
                              className="input text-sm disabled:bg-gray-100"
                            />
                          </div>
                        )}
                        <p className="text-xs text-gray-500">Links the sale to the harvest, so it shows as a Sale in the Yield tab and myGAP 4-6.</p>
                      </div>
                    )}

                    <input
                      type="text" value={it.note} maxLength={255}
                      onChange={(e) => updateItem(it.key, { note: e.target.value })}
                      placeholder="Item note (optional — defaults to the description above)"
                      className="input text-sm"
                    />
                  </div>
                );
              })}
              <button type="button" onClick={() => setItems(prev => [...prev, newItem()])} className="text-sm text-green-700 hover:text-green-800 flex items-center gap-1">
                <PlusIcon className="h-4 w-4" /> Add another item
              </button>
            </>
          )}

          {blockedReason && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">{blockedReason}</div>
          )}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">{error}</div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-2">
          <span className="text-sm text-gray-600">
            {mode === 'sheet'
              ? <>{sheet ? `${sheet.entries.length} row(s)` : ''}</>
              : <>Total: <strong>RM {items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></>}
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
            <button
              type="button"
              onClick={() => (mode === 'sheet' ? submitSheet(sheetDupCount > 0) : submit())}
              disabled={saving || loading || !!blockedReason}
              className="btn-primary px-4 py-2 disabled:opacity-50"
            >
              {saving ? 'Uploading…' : mode === 'sheet' && sheetDupCount > 0 ? 'Upload anyway' : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===================================
// 🚜 FARM OVERVIEW COMPONENT (Enhanced with Kapitani-style tabs)
// ===================================

const FarmOverview = ({ farm, onFarmUpdated, onCreateCycle }) => {
  const [activeTab, setActiveTab] = useState('cycle');
  const [cycles, setCycles] = useState([]);
  const [activities, setActivities] = useState([]);
  const [inventory, setInventory] = useState([]);
  // Add-stock (inventory) modal state
  const [showAddStock, setShowAddStock] = useState(false);
  const [addStock, setAddStock] = useState({ material: '', amount: '', unit: '' });
  const [addStockBusy, setAddStockBusy] = useState(false);
  const [addStockMsg, setAddStockMsg] = useState('');
  const [invMaterialOptions, setInvMaterialOptions] = useState([]);
  const [invUnitOptions, setInvUnitOptions] = useState([]);
  const [invFarmOrgId, setInvFarmOrgId] = useState(null);
  const [yields, setYields] = useState([]);
  const [financials, setFinancials] = useState([]);
  const [showUploadYield, setShowUploadYield] = useState(false);
  const [showUploadFinancial, setShowUploadFinancial] = useState(false);
  const [uploadNotice, setUploadNotice] = useState(null); // { tab, text } after a successful upload
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCycleId, setSelectedCycleId] = useState(null);
  const [extendedFarmData, setExtendedFarmData] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  // Inline cycle title edit + per-row dates modal
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [titleDraft, setTitleDraft] = useState('');
  const [titleSaving, setTitleSaving] = useState(false);
  const [datesModalCycle, setDatesModalCycle] = useState(null);
  const [plantingSysModalCycle, setPlantingSysModalCycle] = useState(null);
  const [availableVendors, setAvailableVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    size: '',
    address1: '',
    address2: '',
    city: '',
    postcode: '',
    state: '',
    latitude: '',
    longitude: ''
  });

  // Farm location for map - use extended data from farm_view
  const farmLocation = extendedFarmData?.address_latitude && extendedFarmData?.address_longitude 
    ? { lat: parseFloat(extendedFarmData.address_latitude), lng: parseFloat(extendedFarmData.address_longitude) }
    : null;

  useEffect(() => {
    if (farm) {
      loadExtendedFarmData();
      loadAllData();
    }
  }, [farm]);

  // FarmOverview stays mounted when another farm is picked; drop upload UI state from the old farm.
  useEffect(() => {
    setUploadNotice(null);
    setShowUploadYield(false);
    setShowUploadFinancial(false);
  }, [farm?.id]);

  // Load available vendors when edit modal opens
  useEffect(() => {
    if (showEditModal) {
      loadVendors();
      restoreMiddlewareId();
    }
  }, [showEditModal]);

  // No middleware number on our record: look the farm up in the middleware's
  // own list (API 14) and restore it, so the vendor list and the middleware
  // sync work for this farm.
  const restoreMiddlewareId = async () => {
    if (!farm?.id || !extendedFarmData || extendedFarmData.middleware_id) return;
    const farmId = farm.id;
    try {
      const { default: innatesMiddlewareService } = await import('../services/innatesMiddlewareService');
      const record = await innatesMiddlewareService.lookupRecord('farm', farmId);
      if (!record?.found || record.isDeleted) return;
      await supabase.from('farm').update({ middleware_id: record.id }).eq('id', farmId);
      setExtendedFarmData(prev => (prev && prev.id === farmId ? { ...prev, middleware_id: record.id } : prev));
    } catch (err) {
      console.warn('⚠️ Could not restore middleware id:', err.message);
    }
  };

  const loadVendors = async () => {
    setVendorLoading(true);
    try {
      const { default: innatesMiddlewareService } = await import('../services/innatesMiddlewareService');
      const result = await innatesMiddlewareService.getVendorList();
      console.log('📦 Vendors loaded:', result);
      setAvailableVendors(result?.vendor || []);
    } catch (err) {
      console.warn('⚠️ Could not load vendors:', err.message);
      setAvailableVendors([]);
    } finally {
      setVendorLoading(false);
    }
  };

  const handleVendorToggle = (vendorId) => {
    setSelectedVendors(prev => 
      prev.includes(vendorId) 
        ? prev.filter(id => id !== vendorId)
        : [...prev, vendorId]
    );
  };

  const handleSaveVendors = async () => {
    if (selectedVendors.length === 0) return;
    
    try {
      const { default: innatesMiddlewareService } = await import('../services/innatesMiddlewareService');
      await innatesMiddlewareService.subscribeVendors({
        farmId: String(farm.id),
        vendors: selectedVendors.map(id => ({ vendorId: id }))
      });
      console.log('✅ Vendors subscribed successfully');
    } catch (err) {
      console.warn('⚠️ Could not subscribe vendors:', err.message);
    }
  };

  // Fetch extended farm data from farm_view for address, coordinates, subscription status
  const loadExtendedFarmData = async () => {
    if (!farm?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('farm_view')
        .select('*')
        .eq('id', farm.id)
        .single();
      
      if (error) {
        console.log('⚠️ Could not load extended farm data:', error.message);
      } else {
        console.log('📍 Extended farm data loaded:', data?.addressFull, 'Lat:', data?.address_latitude, 'Lng:', data?.address_longitude);
        setExtendedFarmData(data);
        // Pre-populate edit form
        setEditFormData({
          name: data?.farm_name || farm.name || '',
          size: data?.farm_size || '',
          address1: data?.addressLine1 || '',
          address2: data?.addressLine2 || '',
          city: data?.city || '',
          postcode: data?.postcode || '',
          state: data?.state || '',
          latitude: data?.address_latitude || '',
          longitude: data?.address_longitude || ''
        });
      }
    } catch (err) {
      console.log('⚠️ Error loading extended farm data:', err);
    }
  };

  // Handle farm edit with middleware sync
  const handleEditFarm = async () => {
    setEditLoading(true);
    setEditError('');
    
    try {
      // 1. Update farm record
      const { error: farmError } = await supabase
        .from('farm')
        .update({
          farmName: editFormData.name,
          farmsize: editFormData.size ? parseFloat(editFormData.size) : null
        })
        .eq('id', farm.id);
      
      if (farmError) throw farmError;
      
      // 2. Update address record if exists
      if (extendedFarmData?.address_id) {
        const { error: addressError } = await supabase
          .from('address')
          .update({
            addressLine1: editFormData.address1,
            addressLine2: editFormData.address2,
            city: editFormData.city,
            postcode: editFormData.postcode,
            state: editFormData.state,
            latitude: editFormData.latitude ? parseFloat(editFormData.latitude) : null,
            longitude: editFormData.longitude ? parseFloat(editFormData.longitude) : null
          })
          .eq('id', extendedFarmData.address_id);
        
        if (addressError) console.warn('⚠️ Could not update address:', addressError.message);
      }
      
      // 3. Sync to middleware if middleware_id exists
      if (extendedFarmData?.middleware_id) {
        try {
          const { default: innatesMiddlewareService } = await import('../services/innatesMiddlewareService');
          try {
            // API 4 asks for the farmer number too; the middleware's farm
            // record (API 14) holds it.
            const record = await innatesMiddlewareService.lookupRecord('farm', farm.id);
            await innatesMiddlewareService.updateFarm({
              farmerId: record?.farmerId ? String(record.farmerId) : undefined,
              farmId: String(farm.id),
              id: extendedFarmData.middleware_id,
              location: editFormData.latitude && editFormData.longitude 
                ? `${editFormData.latitude},${editFormData.longitude}` 
                : ''
            });
            console.log('✅ Farm synced to middleware');
          } catch (updateError) {
            console.warn('⚠️ Middleware farm update failed (non-blocking):', updateError.message);
          }
          
          // 4. Subscribe to selected vendors if any (independent of the farm update)
          if (selectedVendors.length > 0) {
            await innatesMiddlewareService.subscribeVendors({
              farmId: String(farm.id),
              vendors: selectedVendors.map(id => ({ vendorId: id }))
            });
            console.log('✅ Vendors subscribed:', selectedVendors);
          }
        } catch (mwError) {
          console.warn('⚠️ Middleware sync failed (non-blocking):', mwError.message);
        }
      }
      
      console.log('✅ Farm updated successfully');
      setShowEditModal(false);
      loadExtendedFarmData(); // Refresh data
      if (onFarmUpdated) onFarmUpdated();
    } catch (err) {
      console.error('❌ Error updating farm:', err);
      setEditError(err.message || 'Failed to update farm');
    } finally {
      setEditLoading(false);
    }
  };

  // Handle farm delete with middleware sync
  const handleDeleteFarm = async () => {
    setDeleteLoading(true);
    setDeleteError('');
    
    try {
      // 1. Sync delete to middleware if middleware_id exists
      if (extendedFarmData?.middleware_id) {
        try {
          const { default: innatesMiddlewareService } = await import('../services/innatesMiddlewareService');
          await innatesMiddlewareService.deleteFarm({
            farmId: String(farm.id),
            id: extendedFarmData.middleware_id
          });
          console.log('✅ Farm deleted from middleware');
        } catch (mwError) {
          console.warn('⚠️ Middleware delete failed (continuing with local delete):', mwError.message);
        }
      }
      
      // 2. Delete farm_user_role entries
      const { error: roleError } = await supabase
        .from('farm_user_role')
        .delete()
        .eq('farm', farm.id);
      
      if (roleError) console.warn('⚠️ Could not delete farm roles:', roleError.message);
      
      // 3. Delete user_org_farm entries
      const { error: uofError } = await supabase
        .from('user_org_farm')
        .delete()
        .eq('farm', farm.id);
      
      if (uofError) console.warn('⚠️ Could not delete user_org_farm:', uofError.message);
      
      // 4. Delete the farm record
      const { error: farmError } = await supabase
        .from('farm')
        .delete()
        .eq('id', farm.id);
      
      if (farmError) throw farmError;
      
      console.log('✅ Farm deleted successfully');
      setShowDeleteModal(false);
      if (onFarmUpdated) onFarmUpdated();
    } catch (err) {
      console.error('❌ Error deleting farm:', err);
      setDeleteError(err.message || 'Failed to delete farm');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Load the farm's org + material catalog + units when the Add-Stock modal opens.
  useEffect(() => {
    if (!showAddStock || !farm?.id) return;
    (async () => {
      const { data: farmRow } = await supabase.from('farm').select('organisation').eq('id', farm.id).maybeSingle();
      const org = farmRow?.organisation ?? null;
      setInvFarmOrgId(org);
      if (org) {
        const { data: mats } = await supabase.from('material').select('id, name_material').eq('created_by_org', org).order('name_material');
        setInvMaterialOptions(mats || []);
      }
      const { data: units } = await supabase.from('unit').select('id, unit_name, unit_symbol').order('unit_name');
      setInvUnitOptions(units || []);
    })();
  }, [showAddStock, farm?.id]);

  // Add stock to the farm's inventory: find-or-create the inventory item and
  // record an "In" (Purchase) transaction so the balance reflects correctly.
  const handleAddStock = async () => {
    const amt = parseFloat(addStock.amount);
    if (!addStock.material) { setAddStockMsg('Pick a material'); return; }
    if (!(amt > 0)) { setAddStockMsg('Enter a quantity greater than 0'); return; }
    setAddStockBusy(true);
    setAddStockMsg('');
    try {
      const materialId = parseInt(addStock.material, 10);
      const unitId = addStock.unit ? parseInt(addStock.unit, 10) : null;
      const { data: existing } = await supabase.from('inventory_item')
        .select('id, balance_amount').eq('farm', farm.id).eq('material', materialId).limit(1).maybeSingle();
      let itemId = existing?.id;
      if (!itemId) {
        const matName = invMaterialOptions.find(m => String(m.id) === String(materialId))?.name_material || 'Material';
        const { data: created, error: insErr } = await supabase.from('inventory_item')
          .insert({ inventory_name: matName, material: materialId, balance_amount: amt, balance_amount_unit: unitId, active: true, farm: farm.id })
          .select('id').single();
        if (insErr) throw insErr;
        itemId = created.id;
      }
      const { error: txErr } = await supabase.from('inventory_transaction')
        .insert({ amount: amt, amount_unit: unitId, in: true, farm: farm.id, description: 'Stock added via dashboard',
                  date_transact: new Date().toISOString().split('T')[0], inventory_item: itemId, transaction_type: 4 });
      if (txErr) throw txErr;
      setShowAddStock(false);
      setAddStock({ material: '', amount: '', unit: '' });
      loadAllData();
    } catch (e) {
      setAddStockMsg(`Add stock failed: ${e.message || e}`);
    } finally {
      setAddStockBusy(false);
    }
  };

  // After an upload, reload everything: harvests also add diary entries (SOP tab) and
  // material purchases add inventory stock.
  const handleYieldUploaded = (cycleId, result) => {
    setShowUploadYield(false);
    setSelectedCycleId(cycleId);
    const n = result?.transactions || 0;
    const diary = (result?.diary_ids || []).length;
    setUploadNotice({
      tab: 'yield',
      text: `Uploaded ${n} yield transaction${n === 1 ? '' : 's'}${diary ? ` and ${diary} harvest diary entr${diary === 1 ? 'y' : 'ies'}` : ''}.`,
    });
    loadAllData();
  };

  const handleFinancialUploaded = (result) => {
    setShowUploadFinancial(false);
    const n = result?.transactions || 0;
    const inv = (result?.inventory_transaction_ids || []).length;
    const sales = (result?.sale_yield_transaction_ids || []).length;
    const extras = [inv && `${inv} stocked into inventory`, sales && `${sales} linked to harvest sales`].filter(Boolean);
    setUploadNotice({
      tab: 'financial',
      text: `Recorded ${n} financial transaction${n === 1 ? '' : 's'}${extras.length ? ` (${extras.join(', ')})` : ''}.`,
    });
    loadAllData();
  };

  const loadAllData = async () => {
    if (!farm?.id) {
      console.log('❌ FarmOverview: No farm ID provided');
      return;
    }
    setLoading(true);
    
    console.log('📥 FarmOverview: Loading data for farm ID:', farm.id, 'Name:', farm.name);
    
    try {
      // Load seasons/cycles with unit + planting-system info - handle is_soft_deleted being null or false
      const { data: seasons, error: seasonsError } = await supabase
        .from('season')
        .select('*, unit:expected_harvest_amount_unit(id, unit_name, unit_symbol), planting_system_info:planting_system(id, size_unit_name, size_unit_name_malay)')
        .eq('farm', farm.id)
        .or('is_soft_deleted.eq.false,is_soft_deleted.is.null')
        .order('created_at', { ascending: false });

      console.log('📊 Seasons query result:', seasons?.length || 0, 'seasons found', seasonsError ? `Error: ${seasonsError.message}` : '');
      if (seasons && seasons.length > 0) {
        console.log('📊 First season:', seasons[0]);
      }
      
      if (seasonsError) throw seasonsError;

      const transformedCycles = seasons?.map(season => {
        const startDate = season.date_start ? new Date(season.date_start) : null;
        const harvestDate = season.date_harvest_start ? new Date(season.date_harvest_start) : null;
        const endDate = season.date_end ? new Date(season.date_end) : null;
        const today = new Date();

        let status = 'active';
        if (endDate && today > endDate) status = 'completed';

        return {
          id: season.id,
          name: season.name_season || 'Unknown',
          produceName: season.name_season || 'Unknown',
          produceNameId: season.produce_name || null,
          plantingSystemId: season.planting_system || null,
          plantingSystemName: season.planting_system_info?.size_unit_name || null,
          plantingSize: season.planting_system_size != null ? Number(season.planting_system_size) : null,
          startDate: season.date_start,
          endDate: season.date_end,
          harvestStartDate: season.date_harvest_start,
          harvestEndDate: season.date_harvest_end,
          expectedYield: season.expected_harvest_amount || 0,
          yieldUnit: season.unit?.unit_symbol || 'kg',
          sopRecordId: season.sop_record,
          status
        };
      }) || [];

      setCycles(transformedCycles);
      // Keep the cycle the user is looking at across reloads (e.g. after an upload).
      setSelectedCycleId(prev => (prev && transformedCycles.some(c => c.id === prev))
        ? prev
        : (transformedCycles[0]?.id ?? null));

      // Load SOP activities using the view that has all joined data (process_name, status_name, etc.)
      const { data: sopActivities, error: actError } = await supabase
        .from('sop_record_activity_view_include_season')
        .select('*')
        .eq('farm_id', farm.id)
        .order('season_id', { ascending: false })
        .order('day', { ascending: true });

      console.log('📊 SOP activities:', sopActivities?.length || 0, actError ? `Error: ${actError.message}` : '');
      if (!actError) {
        setActivities(sopActivities || []);
      }

      // Load inventory for this farm using RPC function (bypasses RLS for admin access)
      const { data: invData, error: invError } = await supabase
        .rpc('get_inventory_transactions_for_farm', { farm_id_input: farm.id });

      console.log('📊 Inventory transactions for farm', farm.id, ':', invData?.length || 0, invError ? `Error: ${invError.message}` : '');
      if (!invError && invData?.length > 0) {
        // Group by inventory_item
        const groupedInv = {};
        invData?.forEach(tx => {
          const itemId = tx.inventory_item_id;
          const itemName = tx.material_name || tx.inventory_name || tx.description || `Item #${itemId}`;
          const itemType = tx.material_type_name || '';
          
          if (!groupedInv[itemId]) {
            groupedInv[itemId] = {
              id: itemId,
              name: itemName,
              type: itemType,
              transactions: 0,
              totalIn: 0,
              totalOut: 0,
              unit: 'units'
            };
          }
          groupedInv[itemId].transactions++;
          // Use 'is_in' boolean field to determine transaction direction
          if (tx.is_in === true) {
            groupedInv[itemId].totalIn += parseFloat(tx.amount) || 0;
          } else {
            groupedInv[itemId].totalOut += parseFloat(tx.amount) || 0;
          }
        });
        setInventory(Object.values(groupedInv));
      } else {
        setInventory([]);
      }

      // Load yield transactions using RPC function (bypasses RLS for admin access)
      const { data: yData, error: yError } = await supabase
        .rpc('get_yield_transactions_for_farm', { farm_id_input: farm.id });

      console.log('📊 Yield transactions for farm', farm.id, ':', yData?.length || 0, yError ? `Error: ${yError.message}` : '');
      if (!yError) {
        setYields(yData || []);
      }

      // Load financial transactions with subtype join for category names
      const { data: finData, error: finError } = await supabase
        .from('financial_transaction')
        .select('*, subtype:financial_transaction_subtype(id, sub_type_name)')
        .eq('farm', farm.id)
        .order('date_transaction', { ascending: false });

      console.log('📊 Financial transactions:', finData?.length || 0, finError ? `Error: ${finError.message}` : '');
      if (!finError) {
        setFinancials(finData || []);
      }

      // Load employees using RPC function with full user details
      const { data: empData, error: empError } = await supabase
        .rpc('get_farm_employees', { farm_id_input: farm.id });

      console.log('📊 Employees:', empData?.length || 0, empError ? `Error: ${empError.message}` : '');
      if (!empError) {
        setEmployees(empData || []);
      }

      console.log('✅ FarmOverview data loading complete');
      setLoading(false);
    } catch (err) {
      console.error('❌ Error loading farm data:', err);
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'cycle', name: 'Cycle' },
    { id: 'sop', name: 'Planting Schedule / SOP' },
    { id: 'inventory', name: 'Inventory' },
    { id: 'yield', name: 'Yield' },
    { id: 'financial', name: 'Financial' },
    { id: 'employee', name: 'Employee' },
    { id: 'mygap', name: 'myGAP' }
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Map a planting-system display name to a sensible unit word for the size.
  // For systems where a unit word would be misleading (e.g. "Pasture-Based Farming"),
  // returns an empty string so the renderer falls back to just "{size}".
  const plantingSystemUnitWord = (systemName) => {
    if (!systemName) return '';
    const m = {
      'Polly Beg (Fertigation)': 'polybags',
      'Tree (Conventional)': 'trees',
      'Table (Fertigation)': 'tables',
      'Hole (Hydroponic)': 'holes',
      'Block/Log': 'blocks',
      'Wicking Bed': 'beds',
      'Vermicompost': 'bins',
    };
    if (m[systemName]) return m[systemName];
    // Fish/Animal systems: tank/cage/pond etc — too varied to map cleanly.
    if (/^Fish \(/.test(systemName)) return '';
    if (/^Animal \(|^Crab \(|^Buffalo \(/.test(systemName)) return '';
    return '';
  };

  // Save the inline-edited cycle title to season.name_season.
  const saveCycleTitle = async (cycleId) => {
    const newName = titleDraft.trim();
    const current = cycles.find(c => c.id === cycleId);
    if (!current || !newName || newName === current.name) {
      setEditingTitleId(null);
      return;
    }
    setTitleSaving(true);
    const { error } = await supabase
      .from('season')
      .update({ name_season: newName })
      .eq('id', cycleId);
    setTitleSaving(false);
    if (error) {
      console.error('Cycle title save error:', error);
      alert(`Failed to rename cycle: ${error.message}`);
      return;
    }
    setCycles(prev => prev.map(c => c.id === cycleId ? { ...c, name: newName, produceName: newName } : c));
    setEditingTitleId(null);
  };

  // Single line summary: "990 polybags · Hole (Hydroponic)" — gracefully degrades.
  const formatPlantingSystem = (cycle) => {
    const size = cycle.plantingSize;
    const system = cycle.plantingSystemName;
    if (!size && !system) return 'Not set';
    const unitWord = plantingSystemUnitWord(system);
    const sizeStr = size != null ? `${size.toLocaleString()}${unitWord ? ' ' + unitWord : ''}` : '';
    if (sizeStr && system) return `${sizeStr} · ${system}`;
    if (sizeStr) return sizeStr;
    return system;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Get activities for selected cycle (using season_id from the view)
  const selectedCycle = cycles.find(c => c.id === selectedCycleId);
  const cycleActivities = selectedCycleId 
    ? activities.filter(a => a.season_id === selectedCycleId)
    : [];
  
  // Get yields for selected cycle
  const selectedCycleYields = selectedCycleId
    ? yields.filter(y => y.yield_season_id === selectedCycleId)
    : [];

  return (
    <div className="space-y-6">
      {/* Farm Details Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Map Placeholder */}
          <div className="lg:w-1/3">
            <div className="bg-gray-100 rounded-lg h-48 flex items-center justify-center border border-gray-200">
              {farmLocation ? (
                <iframe
                  title="Farm Location"
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  style={{ borderRadius: '8px' }}
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${farmLocation.lng - 0.01},${farmLocation.lat - 0.01},${farmLocation.lng + 0.01},${farmLocation.lat + 0.01}&layer=mapnik&marker=${farmLocation.lat},${farmLocation.lng}`}
                  allowFullScreen
                />
              ) : (
                <div className="text-center text-gray-500">
                  <MapPinIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">Map not available</p>
                </div>
              )}
            </div>
          </div>

          {/* Farm Details */}
          <div className="lg:w-2/3">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Farm Details</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowEditModal(true)}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1.5 transition-colors"
                >
                  <Cog6ToothIcon className="h-4 w-4" />
                  Edit Farm
                </button>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg flex items-center gap-1.5 transition-colors"
                >
                  <XMarkIcon className="h-4 w-4" />
                  Delete
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Subscription</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    extendedFarmData?.subscription_is_active ? 'bg-green-500 text-white' : 'bg-red-100 text-red-800'
                  }`}>
                    {extendedFarmData?.subscription_is_active ? 'Active' : 'Expired'}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-500">Name</div>
                <div className="font-medium text-gray-900">{extendedFarmData?.farm_name || farm.name}</div>
              </div>
              <div>
                <div className="text-gray-500">Size, Acre</div>
                <div className="font-medium text-gray-900">{extendedFarmData?.farm_size || farm.size}</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-gray-500">Address</div>
                <div className="font-medium text-gray-900">
                  {extendedFarmData?.addressFull || `${farm.address || 'No address'}${farm.city ? `, ${farm.city}` : ''}${farm.state ? `, ${farm.state}` : ''}`}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-green-600 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* CYCLE TAB */}
          {activeTab === 'cycle' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Production Cycles</h3>
                <button
                  type="button"
                  onClick={onCreateCycle}
                  className="btn-primary flex items-center gap-2 text-sm"
                >
                  <PlusIcon className="h-4 w-4" />
                  Create Cycle
                </button>
              </div>

              {cycles.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Produce</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Period</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Harvest</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cycles.map(cycle => (
                        <tr key={cycle.id} className="border-b border-gray-100 hover:bg-gray-50 group">
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              {cycle.produceIcon ? (
                                <img src={cycle.produceIcon} alt="" className="w-10 h-10 rounded-full object-cover" />
                              ) : (
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                  <span className="text-green-600 text-lg">🌱</span>
                                </div>
                              )}
                              <div className="min-w-0">
                                {editingTitleId === cycle.id ? (
                                  <input
                                    type="text"
                                    autoFocus
                                    disabled={titleSaving}
                                    value={titleDraft}
                                    onChange={(e) => setTitleDraft(e.target.value)}
                                    onBlur={() => saveCycleTitle(cycle.id)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') { e.preventDefault(); saveCycleTitle(cycle.id); }
                                      else if (e.key === 'Escape') { setEditingTitleId(null); }
                                    }}
                                    className="font-medium text-gray-900 px-2 py-0.5 border border-green-400 rounded outline-none focus:ring-1 focus:ring-green-400 w-full"
                                  />
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => { setEditingTitleId(cycle.id); setTitleDraft(cycle.name); }}
                                    className="font-medium text-gray-900 text-left hover:bg-yellow-50 px-1 -mx-1 rounded transition-colors"
                                    title="Click to rename cycle"
                                  >
                                    {cycle.produceName} {cycle.produceNameMalay && `| ${cycle.produceNameMalay}`}
                                  </button>
                                )}
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  {cycle.startingStageIcon && (
                                    <img src={cycle.startingStageIcon} alt="" className="w-4 h-4" />
                                  )}
                                  <span>{cycle.startingStage}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setPlantingSysModalCycle(cycle)}
                                  className="text-xs text-green-600 text-left hover:bg-yellow-50 px-1 -mx-1 rounded transition-colors"
                                  title="Click to edit planting system & quantity"
                                >
                                  {formatPlantingSystem(cycle)}
                                </button>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-start gap-2">
                              <div className="text-sm">
                                <div>Start: {formatDate(cycle.startDate)}</div>
                                <div>End: {formatDate(cycle.endDate) || '-'}</div>
                              </div>
                              <button
                                type="button"
                                onClick={() => setDatesModalCycle(cycle)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700"
                                title="Edit cycle dates"
                              >
                                <PencilSquareIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="text-sm">
                              <div>Start: {formatDate(cycle.harvestStartDate)}</div>
                              <div>End: {formatDate(cycle.harvestEndDate) || '-'}</div>
                              <div className="mt-1">
                                <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                                  Expected Harvest: {(cycle.expectedYield || 0).toLocaleString()} {cycle.yieldUnit || 'kg'}
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <DocumentTextIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p>No production cycles yet</p>
                </div>
              )}
            </div>
          )}

          {/* SOP TAB */}
          {activeTab === 'sop' && (
            <div>
              <div className="flex items-center gap-4 mb-4">
                <select
                  value={selectedCycleId || ''}
                  onChange={(e) => setSelectedCycleId(parseInt(e.target.value))}
                  className="input max-w-xs"
                >
                  <option value="">Select a cycle...</option>
                  {cycles.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.produceName} | {formatDate(c.startDate)}
                    </option>
                  ))}
                </select>
              </div>

              {selectedCycle && cycleActivities.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Day</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Process</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cycleActivities.map(activity => (
                        <tr key={activity.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm">{formatDate(activity.date_activity)}</td>
                          <td className="py-3 px-4 text-sm">Day {activity.day}</td>
                          <td className="py-3 px-4">
                            <div 
                              className="text-sm font-medium px-2 py-1 rounded inline-block"
                              style={{ backgroundColor: activity.color_code || '#f3f4f6' }}
                            >
                              {activity.process_name || 'N/A'}
                            </div>
                            {activity.name_material && (
                              <div className="text-xs text-gray-500 mt-1">
                                Material: {activity.name_material} {activity.dosage_amount && `Dosage: ${activity.dosage_amount}${activity.dosage_unit_symbol || ''}`}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded text-xs ${
                              activity.status_name === 'Completed' ? 'bg-green-100 text-green-800' :
                              activity.status_name === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {activity.status_name || 'Planned'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">{activity.description || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <DocumentTextIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p>{selectedCycle ? 'No SOP activities for this cycle' : 'Select a cycle to view SOP'}</p>
                </div>
              )}
            </div>
          )}

          {/* INVENTORY TAB */}
          {activeTab === 'inventory' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-700">Inventory Stock</h3>
                <button
                  onClick={() => { setShowAddStock(true); setAddStockMsg(''); }}
                  className="btn-primary text-sm flex items-center gap-1"
                >
                  <PlusIcon className="h-4 w-4" /> Add Stock
                </button>
              </div>
              {inventory.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Inventory</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Count</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">In</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Out</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inventory.map(item => (
                        <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium text-gray-900">{item.name}</td>
                          <td className="py-3 px-4 text-sm text-gray-600">{item.type}</td>
                          <td className="py-3 px-4 text-sm">Transaction: {item.transactions}</td>
                          <td className="py-3 px-4 text-sm text-green-600">In: {item.totalIn.toFixed(1)} {item.unit}</td>
                          <td className="py-3 px-4 text-sm text-red-600">Out: {item.totalOut.toFixed(1)} {item.unit}</td>
                          <td className="py-3 px-4 text-sm font-medium">
                            Balance: {(item.totalIn - item.totalOut).toFixed(1)} {item.unit}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <DocumentTextIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p>No inventory records — click "Add Stock" to add some.</p>
                </div>
              )}

              {showAddStock && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-lg max-w-md w-full p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">Add Inventory Stock</h3>
                      <button onClick={() => setShowAddStock(false)} className="text-gray-400 hover:text-gray-600">
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Material / Item</label>
                      <MaterialSelect
                        value={addStock.material}
                        onChange={(v) => setAddStock(s => ({ ...s, material: v }))}
                        options={invMaterialOptions}
                        orgId={invFarmOrgId}
                        onCreated={(m) => setInvMaterialOptions(prev => [...prev, m].sort((a, b) => (a.name_material || '').localeCompare(b.name_material || '')))}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
                        <input
                          type="number" min="0" step="any"
                          value={addStock.amount}
                          onChange={(e) => setAddStock(s => ({ ...s, amount: e.target.value }))}
                          placeholder="e.g. 100"
                          className="input text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Unit</label>
                        <select
                          value={addStock.unit}
                          onChange={(e) => setAddStock(s => ({ ...s, unit: e.target.value }))}
                          className="input text-sm"
                        >
                          <option value="">— unit —</option>
                          {invUnitOptions.map(u => (
                            <option key={u.id} value={u.id}>{u.unit_symbol ? `${u.unit_symbol} (${u.unit_name})` : u.unit_name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {addStockMsg && <div className="text-sm text-red-600">{addStockMsg}</div>}
                    <div className="flex justify-end gap-2 pt-1">
                      <button onClick={() => setShowAddStock(false)} disabled={addStockBusy} className="btn-secondary text-sm">Cancel</button>
                      <button onClick={handleAddStock} disabled={addStockBusy} className="btn-primary text-sm disabled:opacity-50">
                        {addStockBusy ? 'Adding…' : 'Add Stock'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* YIELD TAB */}
          {activeTab === 'yield' && (
            <div>
              <div className="flex items-center gap-4 mb-4">
                <select
                  value={selectedCycleId || ''}
                  onChange={(e) => setSelectedCycleId(e.target.value ? parseInt(e.target.value, 10) : null)}
                  className="input max-w-xs"
                >
                  <option value="">Select a cycle...</option>
                  {cycles.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.produceName} | {formatDate(c.startDate)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => { setUploadNotice(null); setShowUploadYield(true); }}
                  disabled={cycles.length === 0}
                  title={cycles.length === 0 ? 'Create a cycle first' : undefined}
                  className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50"
                >
                  <PlusIcon className="h-4 w-4" />
                  Upload yield transaction
                </button>
              </div>

              {uploadNotice?.tab === 'yield' && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800 flex items-center justify-between">
                  <span>{uploadNotice.text}</span>
                  <button type="button" onClick={() => setUploadNotice(null)} className="text-green-700 hover:text-green-900">
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* Cycle info header */}
              {selectedCycle && (
                <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                  <span className="flex items-center gap-2">
                    <span className="text-green-600">🌱</span>
                    <span className="font-medium">Season Start:</span> {formatDate(selectedCycle.startDate)}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="font-medium">Harvest Start:</span> {formatDate(selectedCycle.harvestStartDate)}
                  </span>
                </div>
              )}

              {selectedCycleYields.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Category</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Grade</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCycleYields.map(y => (
                        <tr key={y.yield_transaction_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm">{formatDate(y.yield_transaction_date)}</td>
                          <td className="py-3 px-4 text-sm">
                            <span className={y.yield_in ? 'text-green-600' : 'text-orange-600'}>
                              {y.transaction_name || (y.yield_in ? 'Harvest' : 'Sale')}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm font-medium">
                            <span className={y.yield_in ? 'text-green-600' : 'text-orange-600'}>
                              {y.yield_in ? '+' : '-'} {parseFloat(y.yield_transaction_amount || 0).toLocaleString()} {y.unit_symbol || 'kg'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm">{y.grade_name || '-'}</td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {y.yield_transaction_description || y.order_status || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <DocumentTextIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p>No yield records</p>
                </div>
              )}
            </div>
          )}

          {/* FINANCIAL TAB */}
          {activeTab === 'financial' && (
            <div>
              <div className="flex items-center justify-end mb-4">
                <button
                  type="button"
                  onClick={() => { setUploadNotice(null); setShowUploadFinancial(true); }}
                  className="btn-primary flex items-center gap-2 text-sm"
                >
                  <PlusIcon className="h-4 w-4" />
                  Upload financial
                </button>
              </div>

              {uploadNotice?.tab === 'financial' && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800 flex items-center justify-between">
                  <span>{uploadNotice.text}</span>
                  <button type="button" onClick={() => setUploadNotice(null)} className="text-green-700 hover:text-green-900">
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              )}

              {financials.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Category</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {financials.map(f => (
                        <tr key={f.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm">{formatDate(f.date_transaction)}</td>
                          <td className="py-3 px-4 text-sm">{f.subtype?.sub_type_name || 'Other'}</td>
                          <td className="py-3 px-4 text-sm font-medium">
                            <span className={f.in === true ? 'text-green-600' : 'text-red-600'}>
                              {f.in === true ? '+' : '-'}
                              <sup className="text-xs">RM</sup>
                              {Math.abs(parseFloat(f.amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {f.description || '-'}
                            {/^https?:\/\//.test(f.document || '') && (
                              <a href={f.document} target="_blank" rel="noopener noreferrer" className="ml-2 text-green-700 hover:underline">Receipt</a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <DocumentTextIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p>No financial records</p>
                </div>
              )}
            </div>
          )}

          {/* EMPLOYEE TAB */}
          {activeTab === 'employee' && (
            <div>
              {employees.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase">Photo</th>
                        <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                        <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase">Email</th>
                        <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase">Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map(emp => (
                        <tr key={emp.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            {emp.image_url ? (
                              <img 
                                src={emp.image_url} 
                                alt={emp.full_name || 'Employee'} 
                                className="w-10 h-10 rounded-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                              />
                            ) : null}
                            <div className={`w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center ${emp.image_url ? 'hidden' : ''}`}>
                              <span className="text-primary-700 font-medium text-sm">
                                {(emp.full_name || emp.email || '?').charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm font-medium text-gray-900">{emp.full_name || 'N/A'}</td>
                          <td className="py-3 px-4 text-sm text-gray-600">{emp.email || '-'}</td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                              {emp.role_name || 'Member'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <DocumentTextIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p>No employees assigned</p>
                </div>
              )}
            </div>
          )}

          {/* MYGAP TAB */}
          {activeTab === 'mygap' && (
            <MyGAPReport
              farm={{
                id: farm.id,
                name: farm.name,
                // Pass the clean per-field address parts so Form 1-1 Section A
                // prefills Address line / Postcode / City / State correctly.
                // (Previously only the combined addressFull was passed, so
                // postcode/city/state never auto-filled and the street line
                // contained the whole mashed address.)
                address: extendedFarmData?.addressLine1 || farm.address || '',
                address2: extendedFarmData?.addressLine2 || '',
                postcode: extendedFarmData?.postcode || farm.postcode || '',
                city: extendedFarmData?.city || farm.city || '',
                state: (extendedFarmData?.state || farm.state || '').trim(),
                latitude: extendedFarmData?.address_latitude || farm.latitude,
                longitude: extendedFarmData?.address_longitude || farm.longitude,
                size: farm.size || extendedFarmData?.farm_size,
              }}
              cycles={cycles}
              selectedCycleId={selectedCycleId}
              onCycleChange={(id) => setSelectedCycleId(id)}
            />
          )}
        </div>
      </div>

      {/* Edit Planting System Modal */}
      {plantingSysModalCycle && (
        <EditPlantingSystemModal
          cycle={plantingSysModalCycle}
          onClose={() => setPlantingSysModalCycle(null)}
          onSaved={(updates) => {
            setCycles(prev => prev.map(c => c.id === plantingSysModalCycle.id ? { ...c, ...updates } : c));
            setPlantingSysModalCycle(null);
          }}
        />
      )}

      {/* Edit Cycle Dates Modal */}
      {datesModalCycle && (
        <EditCycleDatesModal
          cycle={datesModalCycle}
          activitiesCount={activities.filter(a => a.season_id === datesModalCycle.id).length}
          onClose={() => setDatesModalCycle(null)}
          onSaved={(updates) => {
            setCycles(prev => prev.map(c => c.id === datesModalCycle.id ? { ...c, ...updates } : c));
            setDatesModalCycle(null);
          }}
        />
      )}

      {/* Upload Yield / Financial Modals */}
      {showUploadYield && (
        <UploadYieldModal
          farm={farm}
          cycles={cycles}
          initialCycleId={selectedCycleId}
          yields={yields}
          onClose={() => setShowUploadYield(false)}
          onUploaded={handleYieldUploaded}
        />
      )}
      {showUploadFinancial && (
        <UploadFinancialModal
          farm={farm}
          cycles={cycles}
          financials={financials}
          onClose={() => setShowUploadFinancial(false)}
          onUploaded={handleFinancialUploaded}
        />
      )}

      {/* Delete Farm Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">Delete Farm</h3>
              <p className="text-sm text-gray-500 text-center mb-4">
                Are you sure you want to delete <strong>{extendedFarmData?.farm_name || farm.name}</strong>? 
                This action cannot be undone and will remove all associated data.
              </p>
              
              {deleteError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {deleteError}
                </div>
              )}
              
              {extendedFarmData?.middleware_id && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm text-center">
                  This farm will also be deleted from Kapitani Middleware
                </div>
              )}
              
              <div className="flex space-x-3">
                <button
                  onClick={() => { setShowDeleteModal(false); setDeleteError(''); }}
                  className="flex-1 btn-secondary"
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteFarm}
                  disabled={deleteLoading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {deleteLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    'Delete Farm'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Farm Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Edit Farm</h2>
                <button
                  onClick={() => { setShowEditModal(false); setEditError(''); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              {editError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {editError}
                </div>
              )}
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Farm Name *</label>
                    <input
                      type="text"
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                      className="input"
                      placeholder="Farm name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Farm Size (hectares)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.size}
                      onChange={(e) => setEditFormData({...editFormData, size: e.target.value})}
                      className="input"
                      placeholder="e.g. 5.2"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                  <input
                    type="text"
                    value={editFormData.address1}
                    onChange={(e) => setEditFormData({...editFormData, address1: e.target.value})}
                    className="input"
                    placeholder="Street address"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                  <input
                    type="text"
                    value={editFormData.address2}
                    onChange={(e) => setEditFormData({...editFormData, address2: e.target.value})}
                    className="input"
                    placeholder="Apt, suite, unit, etc."
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                    <input
                      type="text"
                      value={editFormData.postcode}
                      onChange={(e) => setEditFormData({...editFormData, postcode: e.target.value})}
                      className="input"
                      placeholder="e.g. 43000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={editFormData.city}
                      onChange={(e) => setEditFormData({...editFormData, city: e.target.value})}
                      className="input"
                      placeholder="e.g. Kajang"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <select
                    value={editFormData.state}
                    onChange={(e) => setEditFormData({...editFormData, state: e.target.value})}
                    className="input"
                  >
                    <option value="">Select a state</option>
                    {['Johor', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan', 'Pahang', 'Perak', 'Perlis', 
                      'Pulau Pinang', 'Sabah', 'Sarawak', 'Selangor', 'Terengganu', 'Kuala Lumpur', 'Labuan', 'Putrajaya'].map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                    <input
                      type="text"
                      value={editFormData.latitude}
                      onChange={(e) => setEditFormData({...editFormData, latitude: e.target.value})}
                      className="input"
                      placeholder="e.g. 3.1390"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                    <input
                      type="text"
                      value={editFormData.longitude}
                      onChange={(e) => setEditFormData({...editFormData, longitude: e.target.value})}
                      className="input"
                      placeholder="e.g. 101.6869"
                    />
                  </div>
                </div>
                
                {/* Vendor Subscription Section */}
                {extendedFarmData?.middleware_id && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subscribe to IoT Vendors</label>
                    {vendorLoading ? (
                      <div className="text-sm text-gray-500">Loading vendors...</div>
                    ) : availableVendors.length > 0 ? (
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {availableVendors.map(vendor => (
                          <label key={vendor.vendorId} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedVendors.includes(vendor.vendorId)}
                              onChange={() => handleVendorToggle(vendor.vendorId)}
                              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-700">{vendor.vendorName}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">No vendors available</div>
                    )}
                  </div>
                )}

                {extendedFarmData?.middleware_id && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
                    <span className="font-medium">Middleware Sync:</span> Changes will be synced to Kapitani Middleware
                  </div>
                )}
                
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => { setShowEditModal(false); setEditError(''); }}
                    className="flex-1 btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEditFarm}
                    disabled={editLoading || !editFormData.name}
                    className="flex-1 btn-primary flex items-center justify-center disabled:opacity-50"
                  >
                    {editLoading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================================
// 🌱 CREATE PRODUCE COMPONENT (Updated to match registration flow)
// ===================================

const CreateProduce = ({ farmId, onSeasonCreated }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1); // 1: Select Produce, 2: Schedule
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Step 1: Produce Selection (direct search like registration)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProduce, setSelectedProduce] = useState(null);
  const [searching, setSearching] = useState(false);

  // Starting Stage
  const [startingStages, setStartingStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);

  // Planting System
  const [plantingSystems, setPlantingSystems] = useState([]);
  const [selectedPlantingSystem, setSelectedPlantingSystem] = useState(null);
  const [plantingSize, setPlantingSize] = useState('');

  // Step 2: Schedule
  const [cycleName, setCycleName] = useState('');
  const [dateStart, setDateStart] = useState(new Date().toISOString().split('T')[0]);
  const [dateEnd, setDateEnd] = useState('');
  const [expectedHarvest, setExpectedHarvest] = useState('');
  const [harvestUnit, setHarvestUnit] = useState('kg');

  // Set default end date on mount
  useEffect(() => {
    const start = new Date();
    const end = new Date(start.setMonth(start.getMonth() + 6));
    setDateEnd(end.toISOString().split('T')[0]);
  }, []);

  // Fetch starting stages and planting systems when produce is selected
  useEffect(() => {
    if (selectedProduce?.agro_category_id) {
      fetchStartingStages(selectedProduce.agro_category_id);
      fetchPlantingSystems(selectedProduce.agro_category_id);
    }
  }, [selectedProduce]);

  const fetchStartingStages = async (agroCatId) => {
    try {
      const { data, error } = await supabase
        .from('culture_type')
        .select('id, name, name_malay, icon')
        .eq('produce_agri_cat', agroCatId);
      
      if (error) throw error;
      setStartingStages(data || []);
    } catch (error) {
      console.error('Error fetching starting stages:', error);
      setStartingStages([]);
    }
  };

  const fetchPlantingSystems = async (agroCatId) => {
    try {
      const { data, error } = await supabase
        .from('planting_system_size_unit')
        .select('id, size_unit_name, size_unit_name_malay, agri_cat')
        .or(`agri_cat.eq.${agroCatId},agri_cat.is.null`)
        .order('id');
      
      if (error) throw error;
      setPlantingSystems(data || []);
    } catch (error) {
      console.error('Error fetching planting systems:', error);
      setPlantingSystems([]);
    }
  };

  // Global produce search (searches all categories)
  const searchProduce = async (query) => {
    if (!query || query.length < 2) return;
    
    setSearching(true);
    try {
      const { data, error } = await supabase
        .from('produce_view')
        .select('id, name_produce, name_produce_malay, icon, produce_main_type_name, agro_name, agro_category_id')
        .eq('active', true)
        .or(`name_produce.ilike.%${query}%,name_produce_malay.ilike.%${query}%,combined_produce_name.ilike.%${query}%`)
        .order('name_produce')
        .limit(15);
      
      if (error) throw error;
      setSearchResults(data || []);
    } catch (error) {
      console.error('Error searching produce:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        searchProduce(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!selectedProduce) newErrors.produce = 'Please select a produce';
        if (!selectedStage) newErrors.stage = 'Please select a starting stage';
        if (!selectedPlantingSystem) newErrors.plantingSystem = 'Please select a planting system';
        break;
      case 2:
        if (!cycleName.trim()) newErrors.cycleName = 'Cycle name is required';
        if (!dateStart) newErrors.dateStart = 'Start date is required';
        if (!dateEnd) newErrors.dateEnd = 'End date is required';
        if (dateStart && dateEnd && new Date(dateEnd) <= new Date(dateStart)) {
          newErrors.dateEnd = 'End date must be after start date';
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 2));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const clearProduceSelection = () => {
    setSelectedProduce(null);
    setSelectedStage(null);
    setSelectedPlantingSystem(null);
    setPlantingSize('');
    setSearchQuery('');
    setSearchResults([]);
    setStartingStages([]);
    setPlantingSystems([]);
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;
    
    if (!user?.email) {
      alert('User not authenticated');
      return;
    }

    setIsLoading(true);
    
    try {
      console.log('🌱 Creating new season...');

      // Get user ID
      const { data: userData, error: userError } = await supabase
        .from('user')
        .select('id')
        .eq('email', user.email)
        .single();

      if (userError || !userData) {
        console.error('❌ Error getting user:', userError);
        alert('Error: Could not get user information');
        setIsLoading(false);
        return;
      }

      const userId = userData.id;
      console.log('👤 User ID:', userId);

      // Create season/cycle record
      const seasonData = {
        farm: farmId,
        name_season: cycleName,
        produce_name: selectedProduce.id,
        culture_type: selectedStage,
        planting_system: selectedPlantingSystem,
        planting_system_size: plantingSize ? parseFloat(plantingSize) : null,
        date_start: dateStart,
        date_end: dateEnd,
        expected_harvest_amount: expectedHarvest ? parseFloat(expectedHarvest) : null,
        created_by: userId,
        is_soft_deleted: false,
        created_at: new Date().toISOString()
      };

      console.log('📝 Season data:', seasonData);

      const { data: newSeason, error: insertError } = await supabase
        .from('season')
        .insert([seasonData])
        .select()
        .single();

      if (insertError) {
        console.error('❌ Error creating season:', insertError);
        alert(`Error creating season: ${insertError.message}`);
        setIsLoading(false);
        return;
      }

      console.log('✅ Season created:', newSeason);

      // Seed a default SOP at cycle creation: if a published template exists for
      // this produce, clone it onto the new cycle so it starts with a ready SOP.
      let seededSop = false;
      try {
        const { data: tmpl } = await supabase
          .from('sop_template')
          .select('id')
          .eq('publish', true).eq('active', true).eq('produce_name', selectedProduce.id)
          .limit(1).maybeSingle();
        if (tmpl?.id) {
          const { data: newSopId, error: cloneErr } = await supabase.rpc('clone_sop_template', {
            template_id: tmpl.id, season_id: newSeason.id
          });
          if (!cloneErr && newSopId) {
            await supabase.functions.invoke('update-season', {
              body: { seasonId: newSeason.id, updates: { sop_record: newSopId } }
            });
            seededSop = true;
          }
        }
      } catch (e) { /* non-fatal: cycle is created either way */ }

      // Success feedback
      alert(`✅ Production cycle "${cycleName}" for ${selectedProduce.name_produce} created successfully!${seededSop ? '\n📋 A default SOP was applied from a template — open Farm Diary to review it.' : ''}`);
      
      // Reset form
      setSelectedProduce(null);
      setSelectedStage(null);
      setSelectedPlantingSystem(null);
      setPlantingSize('');
      setSearchQuery('');
      setCycleName('');
      setDateStart(new Date().toISOString().split('T')[0]);
      const end = new Date();
      end.setMonth(end.getMonth() + 6);
      setDateEnd(end.toISOString().split('T')[0]);
      setExpectedHarvest('');
      setCurrentStep(1);

      // Trigger parent callback to refresh
      if (onSeasonCreated) {
        onSeasonCreated();
      }
      
    } catch (error) {
      console.error('❌ Error creating cycle:', error);
      alert('Error creating production cycle. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Get planting size label based on selected planting system
  const getPlantingSizeLabel = () => {
    if (!selectedPlantingSystem) return 'Planting Size';
    const system = plantingSystems.find(p => p.id === selectedPlantingSystem);
    return system ? `Planting Size (${system.size_unit_name})` : 'Planting Size';
  };

  return (
    <div className="space-y-6">
      {/* Progress Indicator */}
      <div className="flex items-center justify-center space-x-4 mb-8">
        {[
          { step: 1, title: 'Select Produce', completed: currentStep > 1 },
          { step: 2, title: 'Schedule Crop', completed: false }
        ].map((item, index) => (
          <React.Fragment key={item.step}>
            {index > 0 && (
              <div className={`w-12 h-0.5 ${currentStep > index ? 'bg-green-500' : 'bg-gray-300'}`}></div>
            )}
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep > item.step
                  ? 'bg-green-600 text-white'
                  : currentStep === item.step
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-300 text-gray-600'
              }`}>
                {item.completed ? '✓' : item.step}
              </div>
              <span className={`ml-2 text-sm ${
                currentStep === item.step ? 'font-medium text-gray-900' : 'text-gray-500'
              }`}>
                {item.title}
              </span>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Select Produce */}
      {currentStep === 1 && (
        <div className="card">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">What are you growing?</h2>
            <p className="text-sm text-gray-500 mt-1">Search and select your produce</p>
          </div>

          {/* Search Produce */}
          <div className="mb-6">
            {errors.produce && <p className="text-red-500 text-xs mb-1">{errors.produce}</p>}
            
            {!selectedProduce ? (
              <>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search produce name (e.g., tomato, padi, durian)..."
                    className="input pl-10 pr-10"
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                    >
                      <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>
                
                {searching && (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                  </div>
                )}
                
                {searchResults.length > 0 && (
                  <div className="mt-2 border rounded-lg max-h-64 overflow-y-auto bg-white shadow-sm">
                    {searchResults.map(item => (
                      <button
                        key={item.id}
                        onClick={() => {
                          setSelectedProduce(item);
                          setSearchQuery('');
                          setSearchResults([]);
                        }}
                        className="w-full p-3 text-left hover:bg-gray-50 border-b last:border-b-0 flex items-center gap-3"
                      >
                        {item.icon && (
                          <img src={item.icon} alt="" className="w-10 h-10 rounded-full object-cover bg-gray-100" 
                            onError={(e) => e.target.style.display = 'none'} />
                        )}
                        <div className="flex-1">
                          <div className="font-medium text-gray-800">
                            {item.name_produce}
                            {item.name_produce_malay && ` (${item.name_produce_malay})`}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.produce_main_type_name} | {item.agro_name}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
                  <p className="text-center text-gray-500 text-sm py-4">No produce found for "{searchQuery}"</p>
                )}

                {searchQuery.length < 2 && searchQuery.length > 0 && (
                  <p className="text-center text-gray-400 text-sm py-2">Type at least 2 characters to search...</p>
                )}
              </>
            ) : (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {selectedProduce.icon && (
                    <img src={selectedProduce.icon} alt="" className="w-12 h-12 rounded-full object-cover bg-white"
                      onError={(e) => e.target.style.display = 'none'} />
                  )}
                  <div>
                    <div className="font-semibold text-blue-800">{selectedProduce.name_produce}</div>
                    <div className="text-xs text-blue-600">
                      {selectedProduce.produce_main_type_name} | {selectedProduce.agro_name}
                    </div>
                  </div>
                </div>
                <button
                  onClick={clearProduceSelection}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>

          {/* Starting Stage - Dropdown */}
          {selectedProduce && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Starting Stage *</label>
              {errors.stage && <p className="text-red-500 text-xs mb-1">{errors.stage}</p>}
              <select
                value={selectedStage || ''}
                onChange={(e) => setSelectedStage(parseInt(e.target.value))}
                className="input"
              >
                <option value="">Select starting stage...</option>
                {startingStages.map(stage => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name} {stage.name_malay ? `(${stage.name_malay})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Planting System */}
          {selectedProduce && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Planting System *</label>
              {errors.plantingSystem && <p className="text-red-500 text-xs mb-1">{errors.plantingSystem}</p>}
              <select
                value={selectedPlantingSystem || ''}
                onChange={(e) => {
                  setSelectedPlantingSystem(parseInt(e.target.value));
                  setPlantingSize('');
                }}
                className="input"
              >
                <option value="">Select planting system...</option>
                {plantingSystems.map(system => (
                  <option key={system.id} value={system.id}>
                    {system.size_unit_name} {system.size_unit_name_malay ? `(${system.size_unit_name_malay})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Planting Size */}
          {selectedPlantingSystem && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">{getPlantingSizeLabel()}</label>
              <input
                type="number"
                value={plantingSize}
                onChange={(e) => setPlantingSize(e.target.value)}
                placeholder="e.g. 100"
                className="input"
                min="0"
                step="1"
              />
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-end mt-6 pt-4 border-t">
            <button
              onClick={handleNext}
              disabled={!selectedProduce || !selectedStage || !selectedPlantingSystem}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next: Schedule Crop
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Schedule Crop */}
      {currentStep === 2 && (
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Schedule Crop</h2>
            <button onClick={handleBack} className="btn-secondary text-sm">
              ← Back
            </button>
          </div>

          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-4">
              {errors.submit}
            </div>
          )}

          {/* Summary */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-1 mb-6">
            <div className="text-sm text-gray-600">
              <span className="font-medium">Produce:</span> {selectedProduce?.name_produce}
              {selectedProduce?.name_produce_malay && ` (${selectedProduce.name_produce_malay})`}
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">Category:</span> {selectedProduce?.produce_main_type_name} ({selectedProduce?.agro_name})
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">Starting Stage:</span> {startingStages.find(s => s.id === selectedStage)?.name}
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">Planting System:</span> {plantingSystems.find(p => p.id === selectedPlantingSystem)?.size_unit_name}
            </div>
            {plantingSize && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">Planting Size:</span> {plantingSize}
              </div>
            )}
          </div>

          {/* Cycle Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Cycle Name *</label>
            {errors.cycleName && <p className="text-red-500 text-xs mb-1">{errors.cycleName}</p>}
            <input
              type="text"
              value={cycleName}
              onChange={(e) => setCycleName(e.target.value)}
              placeholder="e.g. Season 1 2026"
              className={`input ${errors.cycleName ? 'border-red-500' : ''}`}
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              {errors.dateStart && <p className="text-red-500 text-xs mb-1">{errors.dateStart}</p>}
              <div className="relative">
                <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="date"
                  value={dateStart}
                  onChange={(e) => setDateStart(e.target.value)}
                  className={`input pl-10 ${errors.dateStart ? 'border-red-500' : ''}`}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              {errors.dateEnd && <p className="text-red-500 text-xs mb-1">{errors.dateEnd}</p>}
              <div className="relative">
                <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="date"
                  value={dateEnd}
                  onChange={(e) => setDateEnd(e.target.value)}
                  className={`input pl-10 ${errors.dateEnd ? 'border-red-500' : ''}`}
                />
              </div>
            </div>
          </div>

          {/* Expected Harvest */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Expected Harvest (optional)</label>
            <div className="flex gap-2">
              <input
                type="number"
                value={expectedHarvest}
                onChange={(e) => setExpectedHarvest(e.target.value)}
                placeholder="e.g. 1000"
                className="input flex-1"
                min="0"
                step="0.01"
              />
              <select
                value={harvestUnit}
                onChange={(e) => setHarvestUnit(e.target.value)}
                className="input w-24"
              >
                <option value="kg">kg</option>
                <option value="ton">ton</option>
                <option value="unit">unit</option>
              </select>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between mt-6 pt-4 border-t">
            <button
              onClick={handleBack}
              className="flex items-center gap-1 px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              <ChevronLeftIcon className="h-4 w-4" /> Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading || !cycleName.trim() || !dateStart || !dateEnd}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                'Create Production Cycle'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================================
// 🏗️ CREATE FARM COMPONENT
// ===================================

const CreateFarm = ({ onFarmCreated }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    size: '',
    address: '',
    postcode: '',
    city: '',
    state: '',
    country: 'Malaysia', // Default country
    mobileNumber: '',
    farmerId: '',
    subscriptionEndDate: '',
    season: 1
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMapsModal, setShowMapsModal] = useState(false);

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Farm name is required';
    }
    
    if (!formData.size.trim()) {
      newErrors.size = 'Farm size is required';
    }
    
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    
    if (!formData.postcode.trim()) {
      newErrors.postcode = 'Postcode is required';
    }
    
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    
    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    }

    if (!formData.subscriptionEndDate) {
      newErrors.subscriptionEndDate = 'Subscription end date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form is complete for button enabling
  const isFormComplete = () => {
    return formData.name.trim() && 
           formData.size.trim() && 
           formData.address.trim() && 
           formData.postcode.trim() && 
           formData.city.trim() && 
           formData.state.trim() &&
           formData.mobileNumber.trim() &&
           formData.subscriptionEndDate;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      address: addressData.address,
      postcode: addressData.postcode,
      city: addressData.city,
      state: addressData.state,
      country: addressData.country,
      coordinates: addressData.coordinates
    }));
    
    // Clear address-related errors
    setErrors(prev => ({
      ...prev,
      address: '',
      postcode: '',
      city: '',
      state: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!user?.email) {
      alert('User not authenticated');
      return;
    }

    setIsSubmitting(true);
    
    try {
      console.log('🏗️ Creating new farm...');

      // Get user ID
      const { data: userData, error: userError } = await supabase
        .from('user')
        .select('id')
        .eq('email', user.email)
        .single();

      if (userError || !userData) {
        console.error('❌ Error getting user:', userError);
        alert('Error: Could not get user information');
        setIsSubmitting(false);
        return;
      }

      const userId = userData.id;
      console.log('👤 User ID:', userId);

      // Create farm in database
      const farmData = {
        farmName: formData.name,
        farmsize: parseFloat(formData.size),
        address: formData.address,
        postcode: formData.postcode,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        mobileNumber: formData.mobileNumber,
        subscriptionEndDate: formData.subscriptionEndDate,
        // Optional fields
        farmerId: formData.farmerId || null,
        coordinates: formData.coordinates || null
      };

      console.log('📝 Farm data:', farmData);

      const { data: newFarm, error: insertError } = await supabase
        .from('farm')
        .insert([farmData])
        .select()
        .single();

      if (insertError) {
        console.error('❌ Error creating farm:', insertError);
        alert(`Error creating farm: ${insertError.message}`);
        setIsSubmitting(false);
        return;
      }

      console.log('✅ Farm created:', newFarm);

      // Now link user to farm as Owner (role_id = 2)
      const farmUserRole = {
        farm: newFarm.id,
        user: userId,
        role: 2, // Owner role
        default_season: null
      };

      console.log('🔗 Linking user to farm...');

      const { error: roleError } = await supabase
        .from('farm_user_role')
        .insert([farmUserRole]);

      if (roleError) {
        console.error('⚠️ Error linking user to farm:', roleError);
        // Farm was created but link failed - show warning
        alert(`Farm created but there was an issue linking it to your account: ${roleError.message}`);
      } else {
        console.log('✅ User linked to farm as Owner');
      }

      // Success feedback
      alert(`✅ Farm "${formData.name}" created successfully!`);
      
      // Reset form
      setFormData({
        name: '',
        size: '',
        address: '',
        postcode: '',
        city: '',
        state: '',
        country: 'Malaysia',
        mobileNumber: '',
        farmerId: '',
        subscriptionEndDate: '',
        season: 1
      });
      
      // Refresh farms list
      if (onFarmCreated) {
        onFarmCreated();
      }
      
    } catch (error) {
      console.error('Error creating farm:', error);
      alert('Error creating farm. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="card">
        <div className="text-center mb-6">
          <BuildingStorefrontIcon className="mx-auto h-12 w-12 text-green-500" />
          <h2 className="text-xl font-semibold text-gray-900 mt-3">Create New Farm</h2>
          <p className="text-sm text-gray-500 mt-2">Enter your farm details to get started</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Farm Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Farm Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`input ${errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
              placeholder="Enter farm name (e.g., Green Valley Farm)"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Farm Size */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Farm Size *
            </label>
            <input
              type="text"
              name="size"
              value={formData.size}
              onChange={handleInputChange}
              className={`input ${errors.size ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
              placeholder="Enter farm size (e.g., 5.2 hectares)"
            />
            {errors.size && (
              <p className="mt-1 text-sm text-red-600">{errors.size}</p>
            )}
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address *
            </label>
            <div 
              onClick={() => setShowMapsModal(true)}
              className={`input cursor-pointer transition-colors ${
                errors.address ? 'border-red-300' : 'border-gray-300 hover:border-blue-400'
              } ${formData.address ? 'bg-white' : 'bg-gray-50'}`}
            >
              <div className="flex items-center space-x-2">
                <MapPinIcon className="h-4 w-4 text-gray-400" />
                <span className={formData.address ? 'text-gray-900' : 'text-gray-500'}>
                  {formData.address || 'Click here to search location...'}
                </span>
              </div>
            </div>
            {errors.address && (
              <p className="mt-1 text-sm text-red-600">{errors.address}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              📍 Click to open location search (Mock Google Maps)
            </p>
          </div>

          {/* Location Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Postcode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Postcode *
              </label>
              <input
                type="text"
                name="postcode"
                value={formData.postcode}
                onChange={handleInputChange}
                className={`input ${errors.postcode ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="e.g., 43000"
              />
              {errors.postcode && (
                <p className="mt-1 text-sm text-red-600">{errors.postcode}</p>
              )}
            </div>

            {/* City */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City *
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className={`input ${errors.city ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="e.g., Kajang"
              />
              {errors.city && (
                <p className="mt-1 text-sm text-red-600">{errors.city}</p>
              )}
            </div>

            {/* State */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                State *
              </label>
              <select
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                className={`input ${errors.state ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
              >
                <option value="">Select State</option>
                <option value="Selangor">Selangor</option>
                <option value="Kuala Lumpur">Kuala Lumpur</option>
                <option value="Johor">Johor</option>
                <option value="Penang">Penang</option>
                <option value="Perak">Perak</option>
                <option value="Sabah">Sabah</option>
                <option value="Sarawak">Sarawak</option>
                <option value="Kedah">Kedah</option>
                <option value="Kelantan">Kelantan</option>
                <option value="Terengganu">Terengganu</option>
                <option value="Pahang">Pahang</option>
                <option value="Negeri Sembilan">Negeri Sembilan</option>
                <option value="Melaka">Melaka</option>
                <option value="Perlis">Perlis</option>
                <option value="Putrajaya">Putrajaya</option>
                <option value="Labuan">Labuan</option>
              </select>
              {errors.state && (
                <p className="mt-1 text-sm text-red-600">{errors.state}</p>
              )}
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country *
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className="input bg-gray-100"
                readOnly
              />
            </div>
          </div>

          {/* Business & Subscription Information */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-4">Business & Subscription Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Mobile Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleInputChange}
                  className={`input ${errors.mobileNumber ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="e.g., 0123456789"
                />
                {errors.mobileNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.mobileNumber}</p>
                )}
              </div>

              {/* Farmer ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Farmer ID
                </label>
                <input
                  type="text"
                  name="farmerId"
                  value={formData.farmerId}
                  onChange={handleInputChange}
                  className="input"
                  placeholder="Auto-generated if empty"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave blank to auto-generate
                </p>
              </div>

              {/* Subscription End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subscription End Date *
                </label>
                <input
                  type="date"
                  name="subscriptionEndDate"
                  value={formData.subscriptionEndDate}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split('T')[0]}
                  className={`input ${errors.subscriptionEndDate ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                />
                {errors.subscriptionEndDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.subscriptionEndDate}</p>
                )}
              </div>

              {/* Season */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Season
                </label>
                <input
                  type="number"
                  name="season"
                  value={formData.season}
                  onChange={handleInputChange}
                  min="1"
                  className="input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Current growing season number
                </p>
              </div>
            </div>
          </div>

          {/* Form Status Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Form Validation</h4>
            <div className="text-xs text-blue-700 space-y-1">
              <div>• All fields marked with * are required</div>
              <div>• Complete all fields to enable the "Create Farm" button</div>
              <div>• Address search with Google Maps will be added next</div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex space-x-4">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormComplete() || isSubmitting}
              className={`flex-1 font-medium py-2 px-4 rounded-md transition-colors ${
                isFormComplete() && !isSubmitting
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? 'Creating Farm...' : 'Create Farm'}
            </button>
          </div>
        </form>

        {/* Google Maps Modal */}
        <GoogleMapsModal
          isOpen={showMapsModal}
          onClose={() => setShowMapsModal(false)}
          onAddressSelect={handleAddressSelect}
        />
      </div>
    </div>
  );
};

const FarmDiary = ({ farmId }) => {
  const [cycles, setCycles] = useState([]);
  const [sopActivitiesMap, setSopActivitiesMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (farmId) {
      loadFarmDiaryData();
    }
  }, [farmId]);

  const loadFarmDiaryData = async () => {
    if (!farmId) return;

    setLoading(true);
    try {
      console.log('📖 Loading farm diary for farm:', farmId);

      // Load seasons for this farm
      const { data: seasons, error: seasonsError } = await supabase
        .from('season')
        .select('*')
        .eq('farm', farmId)
        .eq('is_soft_deleted', false)
        .order('created_at', { ascending: false });

      if (seasonsError) {
        console.error('❌ Error loading seasons:', seasonsError);
        setLoading(false);
        return;
      }

      console.log('✅ Seasons loaded:', seasons?.length || 0);

      // Collect sop_record IDs to fetch real activities
      const sopRecordIds = seasons?.filter(s => s.sop_record).map(s => s.sop_record) || [];

      // Load real SOP activities for all seasons that have an sop_record
      let activitiesBySeasonSop = {};
      if (sopRecordIds.length > 0) {
        const { data: sopActs, error: sopActsError } = await supabase
          .from('sop_record_activity')
          .select('*, process:process(id, process_name)')
          .in('sop_record', sopRecordIds)
          .order('date_activity', { ascending: true });

        if (sopActsError) {
          console.error('⚠️ Error loading SOP activities:', sopActsError);
        } else {
          // Group activities by sop_record
          (sopActs || []).forEach(act => {
            if (!activitiesBySeasonSop[act.sop_record]) activitiesBySeasonSop[act.sop_record] = [];
            activitiesBySeasonSop[act.sop_record].push(act);
          });
          console.log('✅ SOP activities loaded for', Object.keys(activitiesBySeasonSop).length, 'SOP records');
        }
      }

      setSopActivitiesMap(activitiesBySeasonSop);

      // Transform seasons to match cycle structure
      const transformedCycles = seasons?.map(season => {
        const startDate = season.date_start ? new Date(season.date_start) : null;
        const harvestDate = season.date_harvest_start ? new Date(season.date_harvest_start) : null;
        const endDate = season.date_end ? new Date(season.date_end) : null;
        const today = new Date();

        let progress = 0;
        let currentStage = 'Planning';
        let status = 'active';

        if (startDate && endDate && today > endDate) {
          progress = 100;
          currentStage = 'Completed';
          status = 'completed';
        } else if (startDate && harvestDate) {
          const totalDays = Math.ceil((harvestDate - startDate) / (1000 * 60 * 60 * 24));
          const daysPassed = Math.ceil((today - startDate) / (1000 * 60 * 60 * 24));
          progress = Math.min(100, Math.max(0, Math.round((daysPassed / totalDays) * 100)));

          if (today >= harvestDate) {
            currentStage = 'Harvesting';
          } else if (today >= startDate) {
            currentStage = 'Growing';
          }
        } else if (startDate) {
          // No harvest date - calculate from end date
          const end = endDate || new Date(startDate.getTime() + 180 * 24 * 60 * 60 * 1000);
          const totalDays = Math.ceil((end - startDate) / (1000 * 60 * 60 * 24));
          const daysPassed = Math.ceil((today - startDate) / (1000 * 60 * 60 * 24));
          progress = Math.min(100, Math.max(0, Math.round((daysPassed / totalDays) * 100)));
          if (today >= startDate) currentStage = 'Growing';
        }

        return {
          id: season.id,
          farmId: season.farm,
          produceType: season.name_season || 'Unknown',
          variant: season.name_season || '',
          quantity: season.planting_system_size || 0,
          unit: 'units',
          progress: progress,
          currentStage: currentStage,
          status: status,
          seasonStartDate: season.date_start,
          dateEnd: season.date_end,
          expectedFirstHarvest: season.date_harvest_start || season.date_end,
          expectedYield: season.expected_harvest_amount || 0,
          yieldUnit: season.expected_harvest_amount_unit || 'kg',
          produceNameId: season.produce_name,
          sopRecordId: season.sop_record
        };
      }) || [];

      setCycles(transformedCycles);

      console.log('✅ Farm diary loaded successfully');
      setLoading(false);

    } catch (error) {
      console.error('❌ Error loading farm diary data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Farm Diary</h2>
        <div className="text-sm text-gray-500">
          {cycles.length} Active Cycle{cycles.length !== 1 ? 's' : ''}
        </div>
      </div>

      {cycles.length > 0 ? (
        <>
          {/* Production Cycles */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {cycles.map((cycle) => (
              <CycleCard 
                key={cycle.id} 
                cycle={cycle} 
                sopActivities={cycle.sopRecordId ? (sopActivitiesMap[cycle.sopRecordId] || []) : []}
                onRefresh={loadFarmDiaryData}
              />
            ))}
          </div>

        </>
      ) : (
        /* No Cycles State */
        <div className="text-center py-12">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900">No Production Cycles</h3>
          <p className="mt-1 text-sm text-gray-500">Start by creating your first produce to begin tracking.</p>
          <div className="mt-6">
            <button className="btn-primary flex items-center space-x-2 mx-auto">
              <PlusIcon className="h-4 w-4" />
              <span>Create First Produce</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================================
// 🔎 SEARCHABLE PROCESS-TYPE PICKER
// Drop-in replacement for the plain <select> on the SOP activity rows.
// Same value/onChange contract; adds type-to-filter and a scrollable list.
// ===================================
const ProcessTypeSelect = ({ value, onChange, options, placeholder = 'Select process...' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  // Close on outside-click / Escape so it behaves like a native select.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = options.find(o => String(o.id) === String(value));
  const filtered = query
    ? options.filter(o => (o.process_name || '').toLowerCase().includes(query.toLowerCase()))
    : options;

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="input text-sm text-left flex items-center justify-between w-full"
      >
        <span className={selected ? 'text-gray-900' : 'text-gray-400'}>
          {selected ? selected.process_name : placeholder}
        </span>
        <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-green-400"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-500">No matches for "{query}"</div>
            ) : (
              filtered.map(o => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => { onChange(String(o.id)); setOpen(false); setQuery(''); }}
                  className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-green-50 ${
                    String(o.id) === String(value) ? 'bg-green-50 text-green-700 font-medium' : 'text-gray-700'
                  }`}
                >
                  {o.process_name}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ===================================
// 📋 ADD ACTIVITY MODAL (SOP Selection)
// ===================================

// Searchable material/item picker. Lists the org's materials (from the backend
// `material` catalog) and lets the user add a new material inline so an SOP item
// can be linked to a product without leaving the form. INSERT RLS on `material`
// allows any authenticated user, so the inline add works client-side.
const MATERIAL_SELECT_MAX_SHOWN = 200;

const MaterialSelect = ({ value, onChange, options, orgId, onCreated, materialType = null, placeholder = 'Select material / item…' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = options.find(o => String(o.id) === String(value));
  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter(o => (o.name_material || '').toLowerCase().includes(q)) : options;
  const exact = options.some(o => (o.name_material || '').toLowerCase() === q);

  const addMaterial = async () => {
    const name = query.trim();
    if (!name || !orgId) return;
    setCreating(true);
    const { data, error } = await supabase
      .from('material')
      .insert({ name_material: name, created_by_org: orgId, ...(materialType ? { material_type: materialType } : {}) })
      .select('id, name_material')
      .single();
    setCreating(false);
    if (error || !data) return;
    onCreated && onCreated(data);
    onChange(String(data.id));
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="input text-sm text-left flex items-center justify-between w-full"
      >
        <span className={selected ? 'text-gray-900' : 'text-gray-400'}>
          {selected ? selected.name_material : placeholder}
        </span>
        <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search or type a new material…"
              className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-green-400"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {value && (
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
                className="block w-full text-left px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50"
              >— none —</button>
            )}
            {filtered.slice(0, MATERIAL_SELECT_MAX_SHOWN).map(o => (
              <button
                key={o.id}
                type="button"
                onClick={() => { onChange(String(o.id)); setOpen(false); setQuery(''); }}
                className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-green-50 ${
                  String(o.id) === String(value) ? 'bg-green-50 text-green-700 font-medium' : 'text-gray-700'
                }`}
              >{o.name_material}</button>
            ))}
            {filtered.length > MATERIAL_SELECT_MAX_SHOWN && (
              <div className="px-3 py-2 text-xs text-gray-500">
                Showing {MATERIAL_SELECT_MAX_SHOWN} of {filtered.length} — type to narrow the list.
              </div>
            )}
            {q && !exact && (
              <button
                type="button"
                onClick={addMaterial}
                disabled={creating || !orgId}
                className="block w-full text-left px-3 py-2 text-sm text-green-700 hover:bg-green-50 border-t border-gray-100 disabled:opacity-50"
              >{creating ? 'Adding…' : `+ Add "${query.trim()}" to materials`}</button>
            )}
            {filtered.length === 0 && !q && (
              <div className="px-3 py-2 text-xs text-gray-500">No materials yet — type a name to add one.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AddActivityModal = ({ isOpen, onClose, cycle, onSopSelected }) => {
  const { user } = useAuth();
  // Step flow:
  //   - If the cycle already has an sop_record_id, we open straight into
  //     'add-to-existing' so the user can append a single new activity to the
  //     existing SOP instead of being asked to clone or create another one.
  //   - Otherwise we keep the original SOP-template picker flow.
  const [step, setStep] = useState('select');
  const [sopTemplates, setSopTemplates] = useState([]);
  const [processTypes, setProcessTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSop, setSelectedSop] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // New SOP form state
  const [newSopName, setNewSopName] = useState('');
  const [newSopDescription, setNewSopDescription] = useState('');

  // Activities state for new SOP
  const [sopActivities, setSopActivities] = useState([
    { id: 1, day: 1, process: '', description: '' }
  ]);

  // Single-activity append form (only used when the cycle already has a SOP).
  const [appendActivity, setAppendActivity] = useState({
    day: 1, process: '', description: '', dosage_amount: '', date_activity: '', material: ''
  });
  const [appendError, setAppendError] = useState('');
  const [materialOptions, setMaterialOptions] = useState([]); // org material catalog for the item picker
  const [farmOrgId, setFarmOrgId] = useState(null);
  const [unitOptions, setUnitOptions] = useState([]); // dosage units (for paste import resolution)
  // Paste-from-spreadsheet import (bulk-build a lengthy SOP at once)
  const [pasteText, setPasteText] = useState('');
  const [pasteMsg, setPasteMsg] = useState('');
  const [parsing, setParsing] = useState(false);

  // Load the farm's organisation + material catalog so the append form's item
  // picker has options and inline-add can stamp created_by_org. Also load units
  // so the paste importer can resolve dosage-unit names/symbols.
  useEffect(() => {
    if (!isOpen || !cycle?.farmId) return;
    (async () => {
      const { data: farmRow } = await supabase
        .from('farm').select('organisation').eq('id', cycle.farmId).maybeSingle();
      const org = farmRow?.organisation ?? null;
      setFarmOrgId(org);
      if (org) {
        const { data: mats } = await supabase
          .from('material').select('id, name_material').eq('created_by_org', org).order('name_material');
        setMaterialOptions(mats || []);
      }
      const { data: units } = await supabase
        .from('unit').select('id, unit_name, unit_symbol').order('unit_name');
      setUnitOptions(units || []);
    })();
  }, [isOpen, cycle?.farmId]);

  // Parse pasted spreadsheet rows (tab- or comma-separated) into builder rows.
  // Columns: Day | Process | Description | Dosage Amount | Dosage Unit | Material.
  // Process is matched by name; Unit by symbol/name; Material is found-or-created
  // in the org catalog. Unmatched processes are reported and skipped.
  const parsePastedRows = async () => {
    setPasteMsg('');
    const text = pasteText || '';
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) { setPasteMsg('Nothing to parse — paste some rows first.'); return; }
    setParsing(true);
    try {
      const matsLocal = [...materialOptions];
      const added = [];
      const warnings = [];
      let withMat = 0, withDose = 0;
      let seq = 0;
      for (const line of lines) {
        const cells = line.split(/\t|,/).map(c => c.trim());
        const [dayRaw, procRaw, desc, doseRaw, unitRaw, matRaw] = cells;
        const day = parseInt(dayRaw, 10);
        if (!Number.isFinite(day)) {
          // Treat a non-numeric first cell as a header only on the first line.
          if (added.length === 0 && warnings.length === 0) continue;
          warnings.push(`Skipped "${line.slice(0, 40)}" (Day is not a number)`);
          continue;
        }
        const proc = processTypes.find(p => (p.process_name || '').toLowerCase() === (procRaw || '').toLowerCase());
        if (!proc) { warnings.push(`Day ${day}: process "${procRaw || ''}" not found — skipped`); continue; }
        let unitId = '';
        if (unitRaw) {
          const u = unitOptions.find(o => [o.unit_symbol, o.unit_name].some(x => (x || '').toLowerCase() === unitRaw.toLowerCase()));
          if (u) unitId = String(u.id); else warnings.push(`Day ${day}: unit "${unitRaw}" not recognised — left blank`);
        }
        let matId = '';
        if (matRaw) {
          let m = matsLocal.find(x => (x.name_material || '').toLowerCase() === matRaw.toLowerCase());
          if (!m && farmOrgId) {
            const { data } = await supabase
              .from('material').insert({ name_material: matRaw, created_by_org: farmOrgId })
              .select('id, name_material').single();
            if (data) { m = data; matsLocal.push(data); }
          }
          if (m) { matId = String(m.id); withMat++; }
        }
        if (doseRaw && !isNaN(parseFloat(doseRaw))) withDose++;
        added.push({
          id: `paste-${Date.now()}-${seq++}`,
          day,
          process: String(proc.id),
          description: desc || '',
          dosage_amount: doseRaw && !isNaN(parseFloat(doseRaw)) ? doseRaw : '',
          dosage_unit: unitId,
          material: matId,
        });
      }
      if (added.length) {
        setMaterialOptions(matsLocal.sort((a, b) => (a.name_material || '').localeCompare(b.name_material || '')));
        // Replace the initial empty starter row if it's still untouched.
        setSopActivities(prev => {
          const base = prev.filter(r => r.process || (r.description && r.description.trim()));
          return [...base, ...added];
        });
      }
      const parts = [`${added.length} row${added.length === 1 ? '' : 's'} added`];
      if (withMat) parts.push(`${withMat} with material`);
      if (withDose) parts.push(`${withDose} with dosage`);
      if (warnings.length) parts.push(`${warnings.length} skipped`);
      setPasteMsg(parts.join(' · ') + (warnings.length ? ' — ' + warnings.slice(0, 4).join('; ') : ''));
      if (added.length) setPasteText('');
    } catch (e) {
      setPasteMsg(`Import error: ${e.message || e}`);
    } finally {
      setParsing(false);
    }
  };

  useEffect(() => {
    if (isOpen && cycle?.produceNameId) {
      fetchSopTemplates();
      fetchProcessTypes();
    }
  }, [isOpen, cycle?.produceNameId]);

  // When the modal opens, pick the right starting step depending on whether
  // the cycle already has a SOP. This avoids the confusing "Select SOP
  // Template" prompt for users who only want to add one more activity.
  useEffect(() => {
    if (!isOpen) return;
    setStep(cycle?.sopRecordId ? 'add-to-existing' : 'select');
  }, [isOpen, cycle?.sopRecordId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNewSopName('');
      setNewSopDescription('');
      setSopActivities([{ id: 1, day: 1, process: '', description: '' }]);
      setSelectedSop(null);
      setSearchQuery('');
      setAppendActivity({ day: 1, process: '', description: '', dosage_amount: '', date_activity: '', material: '' });
      setAppendError('');
      setPasteText('');
      setPasteMsg('');
    }
  }, [isOpen]);

  // Insert a single activity into the existing sop_record. Date is computed
  // relative to the cycle start so it aligns with sibling activities.
  const handleAppendActivity = async () => {
    setAppendError('');
    const dayNum = parseInt(appendActivity.day, 10);
    if (!appendActivity.process) { setAppendError('Pick a process'); return; }
    if (!Number.isFinite(dayNum) || dayNum < 1) { setAppendError('Day must be 1 or greater'); return; }
    setProcessing(true);
    try {
      // Derive the activity date from the cycle start + (day - 1) days, unless
      // the user typed an explicit date.
      let activityDate = appendActivity.date_activity;
      if (!activityDate) {
        const cycleStartDate = cycle.seasonStartDate ? new Date(cycle.seasonStartDate) : new Date();
        const d = new Date(cycleStartDate);
        d.setDate(d.getDate() + dayNum - 1);
        activityDate = d.toISOString().split('T')[0];
      }
      const insertRow = {
        sop_record: cycle.sopRecordId,
        day: dayNum,
        process: parseInt(appendActivity.process, 10),
        description: appendActivity.description || null,
        date_activity: activityDate,
        status: 2,  // Planned (FK to status; 2=Planned, 4=Completed)
      };
      if (appendActivity.dosage_amount) insertRow.dosage_amount = parseFloat(appendActivity.dosage_amount);
      if (appendActivity.material) insertRow.material = parseInt(appendActivity.material, 10);
      const { error } = await supabase.from('sop_record_activity').insert(insertRow);
      if (error) throw error;
      onSopSelected && onSopSelected(cycle.sopRecordId);
      onClose();
    } catch (err) {
      console.error('Error appending activity:', err);
      setAppendError(err?.message || 'Could not add activity.');
    } finally {
      setProcessing(false);
    }
  };

  const fetchSopTemplates = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('sop_template_combined')
        .select('*')
        .eq('publish', true)
        .eq('active', true)
        .or(`produce_name_id.eq.${cycle.produceNameId},produce_name_id.is.null`)
        .order('sop_name');

      if (error) throw error;

      // Also surface the user's OWN saved SOPs ("My SOPs", from save-as-template),
      // labelled with their version, so they can be cloned into this cycle.
      let mineShaped = [];
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser) {
          const { data: mine } = await supabase
            .from('sop_template')
            .select('id, sop_name, version, sop_template_activity(count)')
            .eq('user_id', authUser.id)
            .order('version', { ascending: false });
          mineShaped = (mine || []).map(t => ({
            id: t.id,
            sop_name: t.sop_name,
            version: t.version,
            isMine: true,
            activity_count: t.sop_template_activity?.[0]?.count ?? 0,
            name_org: `My SOPs · v${t.version || 1}`,
          }));
        }
      } catch { /* ignore — own-templates are best-effort */ }

      setSopTemplates([...mineShaped, ...(data || [])]);
    } catch (error) {
      console.error('Error fetching SOP templates:', error);
      setSopTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProcessTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('process')
        .select('id, process_name')
        .eq('active', true)
        .order('process_name');
      
      if (error) throw error;
      setProcessTypes(data || []);
    } catch (error) {
      console.error('Error fetching process types:', error);
    }
  };

  const handleSelectSop = (sop) => {
    setSelectedSop(sop);
    setStep('confirm');
  };

  const handleConfirmSop = async () => {
    if (!selectedSop || !user?.email) return;
    
    setProcessing(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('User not authenticated');

      // Call clone_sop_template RPC function
      const { data: sopRecordId, error: cloneError } = await supabase.rpc('clone_sop_template', {
        template_id: selectedSop.id,
        season_id: cycle.id
      });

      if (cloneError) throw cloneError;

      console.log('✅ SOP cloned successfully, sop_record_id:', sopRecordId);

      // Update the season with the new sop_record (via edge function to bypass RLS)
      const { error: updateError } = await supabase.functions.invoke('update-season', {
        body: { seasonId: cycle.id, updates: { sop_record: sopRecordId } }
      });

      if (updateError) throw updateError;

      alert(`✅ SOP "${selectedSop.sop_name}" has been applied to this cycle!`);
      onSopSelected(sopRecordId);
      onClose();
    } catch (error) {
      console.error('Error applying SOP:', error);
      alert(`Error applying SOP: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Add new activity row
  const addActivityRow = () => {
    const maxDay = Math.max(...sopActivities.map(a => a.day || 0), 0);
    setSopActivities([...sopActivities, { 
      id: Date.now(), 
      day: maxDay + 1, 
      process: '', 
      description: '' 
    }]);
  };

  // Remove activity row
  const removeActivityRow = (id) => {
    if (sopActivities.length > 1) {
      setSopActivities(sopActivities.filter(a => a.id !== id));
    }
  };

  // Update activity row
  const updateActivityRow = (id, field, value) => {
    setSopActivities(sopActivities.map(a => 
      a.id === id ? { ...a, [field]: value } : a
    ));
  };

  // Proceed to activities step
  const proceedToActivities = () => {
    if (!newSopName.trim()) {
      alert('Please enter an SOP name');
      return;
    }
    setStep('activities');
  };

  // Create SOP with activities
  const handleCreateNewSop = async () => {
    if (!newSopName.trim() || !user?.email) return;
    
    // Validate activities
    const validActivities = sopActivities.filter(a => a.process && a.day);
    if (validActivities.length === 0) {
      alert('Please add at least one activity with a process type and day');
      return;
    }

    setProcessing(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('User not authenticated');

      // Create sop_record directly (skip sop_template due to RLS)
      const { data: sopRecord, error: recordError } = await supabase
        .from('sop_record')
        .insert({
          sop_name: newSopName,
          produce_name: cycle.produceNameId,
          user_id: authUser.id
        })
        .select()
        .single();

      if (recordError) throw recordError;

      console.log('✅ SOP Record created:', sopRecord.id);

      // Calculate activity dates based on cycle start date
      const cycleStartDate = cycle.seasonStartDate ? new Date(cycle.seasonStartDate) : new Date();

      // Create sop_record_activity entries
      const activityInserts = validActivities.map(activity => {
        const activityDate = new Date(cycleStartDate);
        activityDate.setDate(activityDate.getDate() + (parseInt(activity.day) - 1));
        
        return {
          sop_record: sopRecord.id,
          day: parseInt(activity.day),
          process: parseInt(activity.process),
          description: activity.description || null,
          date_activity: activityDate.toISOString().split('T')[0],
          status: 2, // Pending status (FK to status table: 2=Pending, 4=Completed)
          ...(activity.dosage_amount && !isNaN(parseFloat(activity.dosage_amount)) ? { dosage_amount: parseFloat(activity.dosage_amount) } : {}),
          ...(activity.dosage_unit ? { dosage_unit: parseInt(activity.dosage_unit, 10) } : {}),
          ...(activity.material ? { material: parseInt(activity.material, 10) } : {}),
        };
      });

      const { error: activitiesError } = await supabase
        .from('sop_record_activity')
        .insert(activityInserts);

      if (activitiesError) throw activitiesError;

      console.log('✅ SOP Activities created:', activityInserts.length);

      // Update the season with the new sop_record (via edge function to bypass RLS)
      const { error: updateError } = await supabase.functions.invoke('update-season', {
        body: { seasonId: cycle.id, updates: { sop_record: sopRecord.id } }
      });

      if (updateError) throw updateError;

      alert(`✅ SOP "${newSopName}" created with ${validActivities.length} activities!`);
      onSopSelected(sopRecord.id);
      onClose();
    } catch (error) {
      console.error('Error creating SOP:', error);
      alert(`Error creating SOP: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Filter templates based on search
  const filteredTemplates = sopTemplates.filter(sop => 
    sop.sop_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sop.name_produce?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sop.name_produce_malay?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {step === 'add-to-existing' && 'Add Activity'}
              {step === 'select' && 'Select SOP Template'}
              {step === 'confirm' && 'Confirm SOP Selection'}
              {step === 'create' && 'Create New SOP'}
              {step === 'activities' && 'Add Activities'}
            </h3>
            <p className="text-sm text-gray-500">
              {step === 'add-to-existing' && `Append a new activity to this cycle's existing SOP`}
              {step === 'select' && 'Choose an existing SOP or create a new one'}
              {step === 'confirm' && `Apply "${selectedSop?.sop_name}" to this cycle`}
              {step === 'create' && 'Step 1: Name your SOP'}
              {step === 'activities' && 'Step 2: Add activities to your SOP'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {step === 'add-to-existing' && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                This cycle already has an SOP. Fill the details below to add one more activity to it.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Day *</label>
                  <input
                    type="number" min="1"
                    value={appendActivity.day}
                    onChange={(e) => setAppendActivity({ ...appendActivity, day: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Process *</label>
                  <ProcessTypeSelect
                    value={appendActivity.process}
                    onChange={(v) => setAppendActivity({ ...appendActivity, process: v })}
                    options={processTypes}
                    placeholder="— pick process —"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description / Notes</label>
                <textarea
                  rows={3}
                  value={appendActivity.description}
                  onChange={(e) => setAppendActivity({ ...appendActivity, description: e.target.value })}
                  placeholder="What was done? Any observations? (flows into myGAP Form 4)"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Material / Item (optional, linked to inventory catalog)</label>
                <MaterialSelect
                  value={appendActivity.material}
                  onChange={(v) => setAppendActivity({ ...appendActivity, material: v })}
                  options={materialOptions}
                  orgId={farmOrgId}
                  onCreated={(m) => setMaterialOptions(prev => [...prev, m].sort((a, b) => (a.name_material || '').localeCompare(b.name_material || '')))}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Dosage Amount (optional)</label>
                  <input
                    type="number" step="0.01"
                    value={appendActivity.dosage_amount}
                    onChange={(e) => setAppendActivity({ ...appendActivity, dosage_amount: e.target.value })}
                    className="input"
                    placeholder="e.g. 2.5"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Date (auto from Day if blank)</label>
                  <input
                    type="date"
                    value={appendActivity.date_activity}
                    onChange={(e) => setAppendActivity({ ...appendActivity, date_activity: e.target.value })}
                    className="input"
                  />
                </div>
              </div>
              {appendError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{appendError}</div>
              )}
              <div className="text-xs text-gray-400 pt-1">
                Need to start over with a different SOP template?{' '}
                <button onClick={() => setStep('select')} className="text-blue-600 hover:underline">
                  Pick a different SOP
                </button>
              </div>
            </div>
          )}
          {step === 'select' && (
            <>
              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search SOP templates..."
                    className="input pl-10"
                  />
                </div>
              </div>

              {/* Create New SOP Option */}
              <button
                onClick={() => setStep('create')}
                className="w-full p-4 mb-4 border-2 border-dashed border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors flex items-center gap-3"
              >
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <PlusIcon className="h-6 w-6 text-green-600" />
                </div>
                <div className="text-left">
                  <div className="font-medium text-green-700">Create New SOP</div>
                  <div className="text-sm text-green-600">Start from scratch with a custom SOP</div>
                </div>
              </button>

              {/* SOP Templates List */}
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                </div>
              ) : filteredTemplates.length > 0 ? (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    Available SOP Templates ({filteredTemplates.length})
                  </div>
                  {filteredTemplates.map(sop => (
                    <button
                      key={sop.id}
                      onClick={() => handleSelectSop(sop)}
                      className="w-full p-4 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors text-left flex items-center gap-3"
                    >
                      {sop.icon ? (
                        <img src={sop.icon} alt="" className="w-12 h-12 rounded-full object-cover bg-gray-100" 
                          onError={(e) => e.target.style.display = 'none'} />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                          <DocumentTextIcon className="h-6 w-6 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{sop.sop_name}</div>
                        <div className="text-sm text-gray-500">
                          {sop.name_produce} {sop.name_produce_malay && `(${sop.name_produce_malay})`}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {sop.activity_count || 0} activities • by {sop.name_org || 'Community'}
                        </div>
                      </div>
                      <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <DocumentTextIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                  <p>No SOP templates found for this produce</p>
                  <p className="text-sm">Create a new SOP to get started</p>
                </div>
              )}
            </>
          )}

          {step === 'confirm' && selectedSop && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  {selectedSop.icon && (
                    <img src={selectedSop.icon} alt="" className="w-16 h-16 rounded-full object-cover" />
                  )}
                  <div>
                    <h4 className="font-semibold text-blue-900">{selectedSop.sop_name}</h4>
                    <p className="text-sm text-blue-700">
                      {selectedSop.name_produce} • {selectedSop.activity_count || 0} activities
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      by {selectedSop.name_org || 'Community'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <strong>Note:</strong> This will copy all activities from the SOP template to your cycle. 
                    Activity dates will be calculated based on your cycle start date.
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 'create' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SOP Name *</label>
                <input
                  type="text"
                  value={newSopName}
                  onChange={(e) => setNewSopName(e.target.value)}
                  placeholder="e.g., My Tomato Growing SOP"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <textarea
                  value={newSopDescription}
                  onChange={(e) => setNewSopDescription(e.target.value)}
                  placeholder="Brief description of this SOP..."
                  className="input h-24 resize-none"
                />
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700">
                <strong>Next:</strong> You'll add activities to this SOP in the next step.
              </div>
            </div>
          )}

          {step === 'activities' && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="font-medium text-green-800">{newSopName}</div>
                {newSopDescription && <div className="text-sm text-green-600 mt-1">{newSopDescription}</div>}
              </div>

              {/* Paste-from-spreadsheet: build a lengthy SOP in one go */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                <div className="text-sm font-medium text-blue-900">Paste from spreadsheet (build a long SOP at once)</div>
                <div className="text-xs text-blue-700 leading-relaxed">
                  One activity per line, columns separated by <b>Tab</b> (copy straight from Excel/Sheets) or commas:<br />
                  <code className="text-[11px]">Day · Process · Description · Dosage · Unit · Material</code> &nbsp;(Day + Process required; the rest optional).
                </div>
                <textarea
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  rows={4}
                  placeholder={'1\tFertilizer\tBase feeding\t5\tkg\tNPK Green\n3\tFoliar\tLeaf spray\t2\tl\tBayfolan\n7\tPesticide\tAphid control\t2.5\tl\tPrevathon'}
                  className="input text-xs font-mono w-full"
                  disabled={parsing}
                />
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={parsePastedRows}
                    disabled={parsing || !pasteText.trim()}
                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >{parsing ? 'Parsing…' : 'Parse & add rows'}</button>
                  {pasteMsg && <span className="text-xs text-blue-800">{pasteMsg}</span>}
                </div>
              </div>

              <div className="text-sm font-medium text-gray-700">Activities ({sopActivities.length})</div>
              
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {sopActivities.map((activity, index) => (
                  <div key={activity.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Activity {index + 1}</span>
                      {sopActivities.length > 1 && (
                        <button
                          onClick={() => removeActivityRow(activity.id)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Day *</label>
                        <input
                          type="number"
                          min="1"
                          value={activity.day}
                          onChange={(e) => updateActivityRow(activity.id, 'day', e.target.value)}
                          className="input text-sm"
                          placeholder="Day number"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Process Type *</label>
                        <ProcessTypeSelect
                          value={activity.process}
                          onChange={(v) => updateActivityRow(activity.id, 'process', v)}
                          options={processTypes}
                        />
                      </div>
                    </div>
                    <div className="mt-2">
                      <label className="block text-xs text-gray-500 mb-1">Description (optional)</label>
                      <input
                        type="text"
                        value={activity.description}
                        onChange={(e) => updateActivityRow(activity.id, 'description', e.target.value)}
                        className="input text-sm"
                        placeholder="Activity description..."
                      />
                    </div>
                    {(activity.material || activity.dosage_amount) && (
                      <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                        {activity.material && (
                          <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                            🧪 {(materialOptions.find(m => String(m.id) === String(activity.material))?.name_material) || 'Material'}
                          </span>
                        )}
                        {activity.dosage_amount && (
                          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                            ⚖ {activity.dosage_amount}{activity.dosage_unit ? ' ' + (unitOptions.find(u => String(u.id) === String(activity.dosage_unit))?.unit_symbol || '') : ''}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={addActivityRow}
                className="w-full p-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
              >
                <PlusIcon className="h-4 w-4" />
                Add Another Activity
              </button>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                <strong>Note:</strong> Activity dates will be calculated from Day 1 = cycle start date ({cycle.seasonStartDate ? new Date(cycle.seasonStartDate).toLocaleDateString() : 'today'}).
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-between">
          {step !== 'select' ? (
            <button
              onClick={() => {
                if (step === 'activities') setStep('create');
                else if (step === 'create') setStep('select');
                else setStep('select');
              }}
              className="btn-secondary"
              disabled={processing}
            >
              ← Back
            </button>
          ) : (
            <div></div>
          )}
          
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-secondary" disabled={processing}>
              Cancel
            </button>
            {step === 'add-to-existing' && (
              <button
                onClick={handleAppendActivity}
                disabled={processing || !appendActivity.process}
                className="btn-primary flex items-center gap-2"
              >
                {processing ? 'Adding…' : 'Add Activity'}
              </button>
            )}
            {step === 'confirm' && (
              <button
                onClick={handleConfirmSop}
                disabled={processing}
                className="btn-primary flex items-center gap-2"
              >
                {processing ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    Applying...
                  </>
                ) : (
                  'Apply SOP'
                )}
              </button>
            )}
            {step === 'create' && (
              <button
                onClick={proceedToActivities}
                disabled={!newSopName.trim()}
                className="btn-primary flex items-center gap-2"
              >
                Next: Add Activities →
              </button>
            )}
            {step === 'activities' && (
              <button
                onClick={handleCreateNewSop}
                disabled={processing || sopActivities.filter(a => a.process && a.day).length === 0}
                className="btn-primary flex items-center gap-2"
              >
                {processing ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  `Create SOP (${sopActivities.filter(a => a.process && a.day).length} activities)`
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ===================================
// 📋 CYCLE CARD COMPONENT
// ===================================

const CycleCard = ({ cycle, sopActivities = [], onRefresh }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showEndCycleModal, setShowEndCycleModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);
  const [showUpdateProgressModal, setShowUpdateProgressModal] = useState(false);
  // Draft of status changes while the Update Progress modal is open: { [activityId]: 2 | 4 }
  const [progressDraft, setProgressDraft] = useState({});
  const [savingProgress, setSavingProgress] = useState(false);
  const [progressError, setProgressError] = useState('');
  // Inline-edit + delete-confirm state for activity rows in the modal.
  // Both apply immediately on confirm; only the Done/Planned toggle is batched.
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [editActivityDraft, setEditActivityDraft] = useState({ day: '', process: '', description: '', date_activity: '', dosage_amount: '', dosage_unit: '', material: '' });
  const [activityRowBusy, setActivityRowBusy] = useState(null); // id being saved/deleted
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [processTypesForEdit, setProcessTypesForEdit] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]); // dosage units for the inline editor
  const [materialOptions, setMaterialOptions] = useState([]); // org material catalog for the item picker
  const [farmOrgId, setFarmOrgId] = useState(null); // farm's organisation, for filtering + adding materials
  // Whole-SOP management (rename / delete the entire SOP attached to this cycle)
  const [sopName, setSopName] = useState('');
  const [renamingSop, setRenamingSop] = useState(false);
  const [sopNameDraft, setSopNameDraft] = useState('');
  const [sopBusy, setSopBusy] = useState(false);
  const [confirmDeleteSop, setConfirmDeleteSop] = useState(false);
  const [savingToLibrary, setSavingToLibrary] = useState(false);
  const [libraryMsg, setLibraryMsg] = useState('');
  const [endingCycle, setEndingCycle] = useState(false);
  // iTrace Serialization — Plant Batch info after a cycle is ended
  const [itraceBatch, setItraceBatch] = useState(null);
  const [itraceError, setItraceError] = useState(null);
  const [openingITracePortal, setOpeningITracePortal] = useState(false);
  // Middleware-side QR code returned by the end-cycle Edge Function (API 11 /harvest).
  // Today this is usually null because Kapitani middleware has not registered the harvest
  // service yet (verified 2026-05-18). When they do, the Edge Function will return a
  // base64-encoded QR and the modal will render it inline — no portal trip needed.
  const [middlewareQrCode, setMiddlewareQrCode] = useState(null);
  const [endCycleData, setEndCycleData] = useState({
    actualHarvestDate: new Date().toISOString().split('T')[0],
    actualYield: cycle.expectedYield || 0,
    notes: ''
  });
  
  // Calculate REAL cycle statistics from sop_record_activity data
  const today = new Date().toISOString().split('T')[0];
  const totalActivities = sopActivities.length;
  const completedActivities = sopActivities.filter(a => a.status === 4).length;
  const plannedActivities = sopActivities.filter(a => a.status === 2).length;
  const todayActivities = sopActivities.filter(a => a.date_activity === today).length;
  
  // myGAP readiness: deterministic calculation based on completed activities
  const myGapReadiness = totalActivities > 0 
    ? Math.round((completedActivities / totalActivities) * 100) 
    : (cycle.sopRecordId ? 0 : 0);

  const progress = cycle.progress || 0;
  
  // Determine cycle status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'completed': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'paused': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Handle cycle completion — marks season as ended in Supabase via the end-cycle
  // Edge Function (bypasses RLS), then sends the harvest to iTrace as a Plant Batch
  // and opens the portal so the user can create a Commission (which is where the
  // actual QR labels are generated and printed).
  const handleEndCycle = async () => {
    setEndingCycle(true);
    setItraceError(null);
    try {
      // 1. Mark the cycle/season as ended via Edge Function (direct UPDATE is blocked by RLS).
      //    The edge function ALSO calls Kapitani middleware POST /harvest server-side if the
      //    farm has a middleware_id, and may return { qrCode } when middleware finally deploys
      //    the harvest service. We capture it here; today it's typically null.
      const { data: fnData, error: fnErr } = await supabase.functions.invoke('end-cycle', {
        body: {
          seasonId: cycle.id,
          actualHarvestDate: endCycleData.actualHarvestDate,
          farmId: cycle.farmId,
          cropName: cycle.produceType,
          seasonStartDate: cycle.seasonStartDate,
          farmAddress: '',
          fertilizer: [],
          pesticide: [],
        },
      });
      if (fnErr) throw fnErr;
      setMiddlewareQrCode(fnData?.qrCode || null);

      // 2. Send harvest to iTrace → Plant Batch + service token for portal
      const batchName = `${cycle.produceType || 'Harvest'} - ${formatDate(endCycleData.actualHarvestDate)}`;
      // "Cycle:" must be the numeric id (getLabelsForCycle matches on it);
      // the human-readable season name goes in its own field.
      const description = [
        `Farm: ${cycle.farmName || cycle.farmId}`,
        `Cycle: ${cycle.id}`,
        cycle.seasonName ? `Season: ${cycle.seasonName}` : null,
        `Variant: ${cycle.variant || '-'}`,
        `Yield: ${endCycleData.actualYield} ${cycle.yieldUnit || 'kg'}`,
        endCycleData.notes ? `Notes: ${endCycleData.notes}` : null,
      ].filter(Boolean).join(' | ');

      const { plantBatch } =
        await itraceSerializationService.sendHarvestToITrace({ batchName, description, cycleId: cycle.id });

      setItraceBatch(plantBatch);
      setShowEndCycleModal(false);
      setShowQRModal(true);
      // Refresh is deferred until the confirmation modal closes, otherwise
      // the parent re-renders the cycle card (now "completed") and this
      // modal state is wiped out mid-view.
    } catch (error) {
      console.error('Error ending cycle:', error);
      setItraceError(error.message || 'Unknown error');
      alert('Error completing cycle: ' + (error.message || 'Unknown error'));
    } finally {
      setEndingCycle(false);
    }
  };

  // Called when the user dismisses the Plant Batch confirmation modal.
  const handleCloseITraceBatchModal = () => {
    setShowQRModal(false);
    setMiddlewareQrCode(null);
    if (itraceBatch) onRefresh();
  };

  // Save draft status changes from the Update Progress modal. Accepts a map of
  // { [activityId]: 2|4 } — we only UPDATE the rows that changed.
  const handleSaveProgress = async () => {
    const entries = Object.entries(progressDraft);
    if (entries.length === 0) {
      setShowUpdateProgressModal(false);
      return;
    }
    setSavingProgress(true);
    setProgressError('');
    try {
      // Group by new status so we can issue one UPDATE per status bucket
      // instead of one round-trip per activity.
      const toDone = entries.filter(([, s]) => s === 4).map(([id]) => parseInt(id, 10));
      const toPlanned = entries.filter(([, s]) => s === 2).map(([id]) => parseInt(id, 10));

      // sop_record_activity has no date_completed column — completion is
      // tracked solely by status (4 = Completed, 2 = Planned).
      const errors = [];
      if (toDone.length) {
        const { error } = await supabase
          .from('sop_record_activity')
          .update({ status: 4 })
          .in('id', toDone);
        if (error) errors.push(error.message);
      }
      if (toPlanned.length) {
        const { error } = await supabase
          .from('sop_record_activity')
          .update({ status: 2 })
          .in('id', toPlanned);
        if (error) errors.push(error.message);
      }

      if (errors.length) throw new Error(errors.join('; '));

      // Inventory stock-drawdown: for each activity just marked Done that has a
      // material + dosage, deduct it from the farm's inventory via a SECURITY
      // DEFINER RPC (dashboard users may lack direct inventory RLS on the farm).
      let deducted = 0;
      for (const id of toDone) {
        const act = sopActivities.find(a => a.id === id);
        if (!act || !act.material || !(parseFloat(act.dosage_amount) > 0)) continue;
        try {
          const { data } = await supabase.rpc('consume_inventory_for_activity', { p_activity_id: id });
          if (data?.ok) deducted++;
        } catch { /* non-fatal: progress already saved */ }
      }

      setShowUpdateProgressModal(false);
      setProgressDraft({});
      onRefresh && onRefresh();
      if (deducted > 0) {
        alert(`✅ Progress saved. Deducted ${deducted} material${deducted === 1 ? '' : 's'} from inventory for the completed step${deducted === 1 ? '' : 's'}.`);
      }
    } catch (err) {
      console.error('Error updating progress:', err);
      setProgressError(err?.message || 'Could not save progress. Please try again.');
    } finally {
      setSavingProgress(false);
    }
  };

  // Load process options on demand so the inline activity-edit dropdown has data.
  useEffect(() => {
    if (!showUpdateProgressModal || processTypesForEdit.length > 0) return;
    (async () => {
      const { data } = await supabase
        .from('process')
        .select('id, process_name')
        .eq('active', true)
        .order('process_name');
      if (data) setProcessTypesForEdit(data);
    })();
  }, [showUpdateProgressModal, processTypesForEdit.length]);

  // Load dosage units on demand for the inline activity-edit dropdown.
  useEffect(() => {
    if (!showUpdateProgressModal || unitOptions.length > 0) return;
    (async () => {
      const { data } = await supabase
        .from('unit')
        .select('id, unit_name, unit_symbol')
        .order('unit_name');
      if (data) setUnitOptions(data);
    })();
  }, [showUpdateProgressModal, unitOptions.length]);

  // Load the farm's organisation + its material catalog when the modal opens so
  // the item picker has options and inline-add can stamp created_by_org.
  useEffect(() => {
    if (!showUpdateProgressModal || !cycle.farmId) return;
    (async () => {
      const { data: farmRow } = await supabase
        .from('farm').select('organisation').eq('id', cycle.farmId).maybeSingle();
      const org = farmRow?.organisation ?? null;
      setFarmOrgId(org);
      if (org) {
        const { data: mats } = await supabase
          .from('material').select('id, name_material').eq('created_by_org', org).order('name_material');
        setMaterialOptions(mats || []);
      }
    })();
  }, [showUpdateProgressModal, cycle.farmId]);

  // Load the SOP's name when the modal opens so it can be shown / renamed.
  useEffect(() => {
    if (!showUpdateProgressModal || !cycle.sopRecordId) return;
    setRenamingSop(false);
    setConfirmDeleteSop(false);
    (async () => {
      const { data } = await supabase
        .from('sop_record')
        .select('sop_name')
        .eq('id', cycle.sopRecordId)
        .maybeSingle();
      setSopName(data?.sop_name || '');
      setSopNameDraft(data?.sop_name || '');
    })();
  }, [showUpdateProgressModal, cycle.sopRecordId]);

  // Rename the whole SOP (sop_record.sop_name). RLS Update policy already exists.
  const renameSop = async () => {
    if (!cycle.sopRecordId) return;
    const name = sopNameDraft.trim();
    if (!name) { setProgressError('SOP name cannot be empty.'); return; }
    setSopBusy(true);
    setProgressError('');
    const { error } = await supabase
      .from('sop_record')
      .update({ sop_name: name })
      .eq('id', cycle.sopRecordId);
    setSopBusy(false);
    if (error) { setProgressError(`Rename failed: ${error.message}`); return; }
    setSopName(name);
    setRenamingSop(false);
    // No onRefresh: the SOP name only appears inside this modal, and refreshing
    // would re-mount the card and close the modal mid-task.
  };

  // Delete the entire SOP: unlink the season (RLS-blocked client-side, so via the
  // update-season edge function), then delete its activities, then the sop_record.
  const deleteSop = async () => {
    if (!cycle.sopRecordId) return;
    setSopBusy(true);
    setProgressError('');
    try {
      const { error: unlinkErr } = await supabase.functions.invoke('update-season', {
        body: { seasonId: cycle.id, updates: { sop_record: null } }
      });
      if (unlinkErr) throw unlinkErr;
      const { error: actErr } = await supabase
        .from('sop_record_activity').delete().eq('sop_record', cycle.sopRecordId);
      if (actErr) throw actErr;
      const { error: sopErr } = await supabase
        .from('sop_record').delete().eq('id', cycle.sopRecordId);
      if (sopErr) throw sopErr;
      setSopBusy(false);
      setConfirmDeleteSop(false);
      setShowUpdateProgressModal(false);
      onRefresh && onRefresh();
    } catch (e) {
      setSopBusy(false);
      setProgressError(`Delete SOP failed: ${e.message || e}`);
    }
  };

  // Save the current cycle's SOP into the user's reusable, versioned SOP library
  // (sop_template). Uses the save_sop_record_as_template RPC (SECURITY DEFINER).
  const saveSopToLibrary = async () => {
    if (!cycle.sopRecordId) return;
    setSavingToLibrary(true);
    setProgressError('');
    setLibraryMsg('');
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('Not authenticated');
      const { error } = await supabase.rpc('save_sop_record_as_template', {
        p_sop_record_id: cycle.sopRecordId,
        p_owner: authUser.id,
      });
      if (error) throw error;
      setLibraryMsg('✓ Saved to My SOPs');
    } catch (e) {
      setProgressError(`Save to My SOPs failed: ${e.message || e}`);
    } finally {
      setSavingToLibrary(false);
    }
  };

  const beginEditActivity = (activity) => {
    setEditActivityDraft({
      day: activity.day ?? '',
      process: activity.process?.id ?? activity.process ?? '',
      description: activity.description || '',
      date_activity: activity.date_activity || '',
      dosage_amount: activity.dosage_amount ?? '',
      dosage_unit: activity.dosage_unit ?? '',
      material: activity.material ?? ''
    });
    setEditingActivityId(activity.id);
    setConfirmDeleteId(null);
  };

  const cancelEditActivity = () => {
    setEditingActivityId(null);
    setEditActivityDraft({ day: '', process: '', description: '', date_activity: '', dosage_amount: '', dosage_unit: '', material: '' });
  };

  // Keep date_activity in sync when Day changes (Day 1 = cycle start), matching
  // how SOP creation derives dates; the user can still override the date after.
  const dateFromDay = (dayVal) => {
    const start = cycle?.seasonStartDate ? new Date(cycle.seasonStartDate) : null;
    const n = parseInt(dayVal, 10);
    if (!start || isNaN(start.getTime()) || isNaN(n)) return null;
    return new Date(start.getTime() + (n - 1) * 86400000).toISOString().split('T')[0];
  };

  const saveEditActivity = async (id) => {
    setActivityRowBusy(id);
    setProgressError('');
    const payload = {
      day: editActivityDraft.day === '' ? null : parseInt(editActivityDraft.day, 10),
      process: editActivityDraft.process === '' ? null : parseInt(editActivityDraft.process, 10),
      description: editActivityDraft.description || null,
      date_activity: editActivityDraft.date_activity || null,
      dosage_amount: editActivityDraft.dosage_amount === '' || editActivityDraft.dosage_amount === null
        ? null : parseFloat(editActivityDraft.dosage_amount),
      dosage_unit: editActivityDraft.dosage_unit === '' || editActivityDraft.dosage_unit === null
        ? null : parseInt(editActivityDraft.dosage_unit, 10),
      material: editActivityDraft.material === '' || editActivityDraft.material === null
        ? null : parseInt(editActivityDraft.material, 10)
    };
    const { error } = await supabase
      .from('sop_record_activity')
      .update(payload)
      .eq('id', id);
    setActivityRowBusy(null);
    if (error) {
      setProgressError(`Edit failed: ${error.message}`);
      return;
    }
    setEditingActivityId(null);
    onRefresh && onRefresh();
  };

  const confirmDeleteActivity = async (id) => {
    setActivityRowBusy(id);
    setProgressError('');
    const { error } = await supabase
      .from('sop_record_activity')
      .delete()
      .eq('id', id);
    setActivityRowBusy(null);
    if (error) {
      setProgressError(`Delete failed: ${error.message}`);
      return;
    }
    setConfirmDeleteId(null);
    // Drop any pending status draft for this row so it doesn't try to update a
    // deleted activity on the next Save.
    setProgressDraft((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    onRefresh && onRefresh();
  };

  // Re-open the iTrace portal for an already-ended cycle. Generates a fresh single-use
  // service token each time because the previous one is consumed on first use.
  const handleOpenITracePortal = async () => {
    setOpeningITracePortal(true);
    setItraceError(null);
    try {
      const { portalUrl } = await itraceSerializationService.openPortal();
      window.open(portalUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error opening iTrace portal:', error);
      setItraceError(error.message || 'Unknown error');
    } finally {
      setOpeningITracePortal(false);
    }
  };

  return (
    <div className="card hover:shadow-md transition-shadow">
      {/* Cycle Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <h3 className="text-lg font-semibold text-gray-900">
              {cycle.produceType}
            </h3>
            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(cycle.status)}`}>
              {cycle.status || 'Active'}
            </span>
          </div>
          <p className="text-sm text-gray-600">
            {cycle.variant} • {cycle.quantity} {cycle.unit} • {cycle.managementType}
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-blue-600">{progress}%</div>
          <div className="text-xs text-gray-500">Complete</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Progress</span>
          <span>{cycle.currentStage || 'In Progress'}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* Key Information */}
      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
        <div>
          <span className="text-gray-500">Started:</span>
          <div className="font-medium">{formatDate(cycle.seasonStartDate)}</div>
        </div>
        <div>
          <span className="text-gray-500">Expected Harvest:</span>
          <div className="font-medium">{formatDate(cycle.expectedFirstHarvest)}</div>
        </div>
        <div>
          <span className="text-gray-500">Expected Yield:</span>
          <div className="font-medium">{cycle.expectedYield} {cycle.yieldUnit}</div>
        </div>
        <div>
          <span className="text-gray-500">Farming Process:</span>
          <div className="font-medium">{cycle.farmingProcess}</div>
        </div>
      </div>

      {/* Activity Summary */}
      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-900">Activity Summary</h4>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-xs text-blue-600 hover:text-blue-700"
          >
            {showDetails ? 'Hide Details' : 'View Details'}
          </button>
        </div>
        
        <div className="grid grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-lg font-semibold text-orange-600">{todayActivities}</div>
            <div className="text-xs text-gray-500">Today</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-blue-600">{plannedActivities}</div>
            <div className="text-xs text-gray-500">Planned</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-green-600">{completedActivities}</div>
            <div className="text-xs text-gray-500">Completed</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-purple-600">{myGapReadiness}%</div>
            <div className="text-xs text-gray-500">myGAP Ready</div>
          </div>
        </div>

        {/* Detailed Activities - Real SOP activities */}
        {showDetails && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <h5 className="text-sm font-medium text-gray-900 mb-2">
              SOP Activities {totalActivities > 0 ? `(${totalActivities})` : ''}
            </h5>
            {sopActivities.length > 0 ? (
              <div className="space-y-2">
                {sopActivities.slice(0, 8).map((activity) => (
                  <div key={activity.id} className="flex items-center justify-between text-sm">
                    <div className="flex-1 min-w-0">
                      <span className="font-medium">{activity.process?.process_name || 'Activity'}</span>
                      {activity.description && <span className="text-gray-400 ml-1 text-xs truncate">- {activity.description}</span>}
                      <span className="text-gray-500 ml-2 text-xs">
                        Day {activity.day} • {activity.date_activity ? formatDate(activity.date_activity) : 'No date'}
                      </span>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ml-2 flex-shrink-0 ${
                      activity.status === 4 
                        ? 'bg-green-100 text-green-800'
                        : activity.status === 2
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {activity.status === 4 ? 'Done' : activity.status === 2 ? 'Planned' : 'Pending'}
                    </span>
                  </div>
                ))}
                {sopActivities.length > 8 && (
                  <div className="text-xs text-gray-500 text-center pt-2">
                    +{sopActivities.length - 8} more activities
                  </div>
                )}
              </div>
            ) : cycle.sopRecordId ? (
              <p className="text-sm text-gray-500">SOP assigned but no activities found</p>
            ) : (
              <p className="text-sm text-gray-500">No SOP assigned yet. Click "Add Activity" to set up your farm diary.</p>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-2 mt-4 pt-4 border-t border-gray-200">
        {cycle.status === 'completed' ? (
          // Completed cycle buttons
          <>
            <button className="flex-1 btn-outline text-sm">
              View Details
            </button>
            {cycle.sopRecordId && (
              <button
                onClick={saveSopToLibrary}
                disabled={savingToLibrary}
                className="flex-1 btn-outline text-sm flex items-center justify-center gap-1 disabled:opacity-50"
                title="Save this cycle's SOP to your reusable library (My SOPs) as a new version"
              >
                <PlusIcon className="h-4 w-4" />
                <span>{savingToLibrary ? 'Saving…' : (libraryMsg ? 'Saved ✓' : 'Save to My SOPs')}</span>
              </button>
            )}
            <button
              onClick={handleOpenITracePortal}
              disabled={openingITracePortal}
              className="flex-1 btn-primary text-sm flex items-center justify-center space-x-1 disabled:opacity-50"
              title="Opens the iTrace Serialization portal to manage commissions and QR labels for this cycle"
            >
              <QrCodeIcon className="h-4 w-4" />
              <span>{openingITracePortal ? 'Opening…' : 'Open iTrace Portal'}</span>
            </button>
          </>
        ) : (
          // Active cycle buttons
          <>
            <button
              onClick={() => setShowAddActivityModal(true)}
              className="flex-1 btn-outline text-sm"
            >
              Add Activity
            </button>
            <button
              onClick={() => {
                setProgressDraft({});
                setProgressError('');
                setShowUpdateProgressModal(true);
              }}
              className="flex-1 btn-outline text-sm"
            >
              Update Progress
            </button>
            <button 
              onClick={() => setShowEndCycleModal(true)}
              className="btn-primary text-sm px-3 flex items-center space-x-1"
            >
              <CheckCircleIcon className="h-4 w-4" />
              <span>End Cycle</span>
            </button>
          </>
        )}
      </div>

      {/* End Cycle Modal */}
      {showEndCycleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Complete Cycle</h3>
                <button
                  onClick={() => setShowEndCycleModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Cycle Summary</h4>
                  <div className="text-sm text-blue-800 space-y-1">
                    <div><strong>Product:</strong> {cycle.produceType} ({cycle.variant})</div>
                    <div><strong>Expected Yield:</strong> {cycle.expectedYield} {cycle.yieldUnit}</div>
                    <div><strong>Current Progress:</strong> {cycle.progress}%</div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Actual Harvest Date *
                  </label>
                  <input
                    type="date"
                    value={endCycleData.actualHarvestDate}
                    onChange={(e) => setEndCycleData({...endCycleData, actualHarvestDate: e.target.value})}
                    className="input w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Actual Yield ({cycle.yieldUnit}) *
                  </label>
                  <input
                    type="number"
                    value={endCycleData.actualYield}
                    onChange={(e) => setEndCycleData({...endCycleData, actualYield: parseFloat(e.target.value)})}
                    className="input w-full"
                    min="0"
                    step="0.1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Completion Notes
                  </label>
                  <textarea
                    value={endCycleData.notes}
                    onChange={(e) => setEndCycleData({...endCycleData, notes: e.target.value})}
                    className="input w-full h-20 resize-none"
                    placeholder="Optional notes about the harvest..."
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div className="text-sm text-yellow-800">
                      <strong>Note:</strong> Completing this cycle marks it as finished and
                      sends the harvest to iTrace as a <em>Plant Batch</em>. You will then
                      be able to open the iTrace Serialization portal to create a Commission
                      and generate the QR code labels. This action cannot be undone.
                    </div>
                  </div>
                </div>

                {itraceError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                    {itraceError}
                  </div>
                )}
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowEndCycleModal(false)}
                  className="flex-1 btn-secondary"
                  disabled={endingCycle}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEndCycle}
                  className="flex-1 btn-primary flex items-center justify-center gap-2"
                  disabled={endingCycle}
                >
                  {endingCycle ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                      Sending to iTrace...
                    </>
                  ) : 'Complete Cycle & Send to iTrace'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Progress Modal */}
      {showUpdateProgressModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Update Cycle Progress</h3>
                <button
                  onClick={() => setShowUpdateProgressModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Mark each SOP activity as <span className="font-medium text-green-700">Done</span> or keep it as{' '}
                <span className="font-medium text-blue-700">Planned</span>. Progress is recalculated from the count of Done vs total activities.
              </p>
              {cycle.sopRecordId && (
                <div className="mt-3 flex items-center gap-2">
                  {renamingSop ? (
                    <>
                      <input
                        type="text"
                        value={sopNameDraft}
                        onChange={(e) => setSopNameDraft(e.target.value)}
                        className="input text-sm flex-1"
                        placeholder="SOP name"
                        disabled={sopBusy}
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={renameSop}
                        disabled={sopBusy}
                        className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                      >{sopBusy ? 'Saving…' : 'Save'}</button>
                      <button
                        type="button"
                        onClick={() => { setRenamingSop(false); setSopNameDraft(sopName); }}
                        disabled={sopBusy}
                        className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                      >Cancel</button>
                    </>
                  ) : (
                    <>
                      <span className="text-sm font-medium text-gray-700 truncate">
                        SOP: {sopName || '—'}
                      </span>
                      <button
                        type="button"
                        onClick={() => { setSopNameDraft(sopName); setRenamingSop(true); }}
                        title="Rename SOP"
                        className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {sopActivities.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No SOP activities yet.</p>
                  <p className="text-sm mt-1">Add an SOP first via "Add Activity".</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sopActivities.map((activity) => {
                    const currentStatus = progressDraft[activity.id] ?? activity.status;
                    const isDone = currentStatus === 4;
                    const isEditing = editingActivityId === activity.id;
                    const isConfirmingDelete = confirmDeleteId === activity.id;
                    const isBusy = activityRowBusy === activity.id;

                    if (isEditing) {
                      return (
                        <div key={activity.id} className="p-3 rounded-lg border border-yellow-300 bg-yellow-50 space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Day</label>
                              <input
                                type="number" min="1"
                                value={editActivityDraft.day}
                                onChange={(e) => setEditActivityDraft(d => {
                                  const nd = dateFromDay(e.target.value);
                                  return { ...d, day: e.target.value, ...(nd ? { date_activity: nd } : {}) };
                                })}
                                className="input text-sm"
                                disabled={isBusy}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Date</label>
                              <input
                                type="date"
                                value={editActivityDraft.date_activity}
                                onChange={(e) => setEditActivityDraft(d => ({ ...d, date_activity: e.target.value }))}
                                className="input text-sm"
                                disabled={isBusy}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Process Type</label>
                            <ProcessTypeSelect
                              value={editActivityDraft.process}
                              onChange={(v) => setEditActivityDraft(d => ({ ...d, process: v }))}
                              options={processTypesForEdit}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Description</label>
                            <input
                              type="text"
                              value={editActivityDraft.description}
                              onChange={(e) => setEditActivityDraft(d => ({ ...d, description: e.target.value }))}
                              className="input text-sm"
                              disabled={isBusy}
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Material / Item (linked to inventory catalog)</label>
                            <MaterialSelect
                              value={editActivityDraft.material}
                              onChange={(v) => setEditActivityDraft(d => ({ ...d, material: v }))}
                              options={materialOptions}
                              orgId={farmOrgId}
                              onCreated={(m) => setMaterialOptions(prev => [...prev, m].sort((a, b) => (a.name_material || '').localeCompare(b.name_material || '')))}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Dosage Amount</label>
                              <input
                                type="number" min="0" step="any"
                                value={editActivityDraft.dosage_amount}
                                onChange={(e) => setEditActivityDraft(d => ({ ...d, dosage_amount: e.target.value }))}
                                placeholder="e.g. 5"
                                className="input text-sm"
                                disabled={isBusy}
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Dosage Unit</label>
                              <select
                                value={editActivityDraft.dosage_unit}
                                onChange={(e) => setEditActivityDraft(d => ({ ...d, dosage_unit: e.target.value }))}
                                className="input text-sm"
                                disabled={isBusy}
                              >
                                <option value="">— none —</option>
                                {unitOptions.map(u => (
                                  <option key={u.id} value={u.id}>
                                    {u.unit_symbol ? `${u.unit_symbol} (${u.unit_name})` : u.unit_name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="flex justify-end gap-2 pt-1">
                            <button
                              type="button"
                              onClick={cancelEditActivity}
                              disabled={isBusy}
                              className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                            >Cancel</button>
                            <button
                              type="button"
                              onClick={() => saveEditActivity(activity.id)}
                              disabled={isBusy}
                              className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                            >{isBusy ? 'Saving…' : 'Save'}</button>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={activity.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          isDone ? 'border-green-200 bg-green-50' :
                          isConfirmingDelete ? 'border-red-200 bg-red-50' :
                          'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex-1 min-w-0 pr-3">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {activity.process?.process_name || 'Activity'}
                          </div>
                          {activity.description && (
                            <div className="text-xs text-gray-500 truncate">{activity.description}</div>
                          )}
                          <div className="text-xs text-gray-400 mt-0.5">
                            Day {activity.day} • {activity.date_activity ? formatDate(activity.date_activity) : 'No date'}
                          </div>
                        </div>
                        {isConfirmingDelete ? (
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span className="text-xs text-red-700 mr-1">Delete?</span>
                            <button
                              type="button"
                              onClick={() => confirmDeleteActivity(activity.id)}
                              disabled={isBusy}
                              className="px-2.5 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                            >{isBusy ? '…' : 'Confirm'}</button>
                            <button
                              type="button"
                              onClick={() => setConfirmDeleteId(null)}
                              disabled={isBusy}
                              className="px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                            >Cancel</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => {
                                setProgressDraft((prev) => {
                                  const next = { ...prev };
                                  const newStatus = isDone ? 2 : 4;
                                  if (newStatus === activity.status) {
                                    delete next[activity.id];
                                  } else {
                                    next[activity.id] = newStatus;
                                  }
                                  return next;
                                });
                              }}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                                isDone
                                  ? 'bg-green-600 text-white hover:bg-green-700'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {isDone ? '✓ Done' : 'Mark as Done'}
                            </button>
                            <button
                              type="button"
                              onClick={() => beginEditActivity(activity)}
                              title="Edit activity"
                              className="p-1.5 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700"
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => { setConfirmDeleteId(activity.id); setEditingActivityId(null); }}
                              title="Delete activity"
                              className="p-1.5 rounded hover:bg-red-100 text-gray-500 hover:text-red-600"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {progressError && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                  {progressError}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {cycle.sopRecordId && (
                  confirmDeleteSop ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-red-700">Delete entire SOP?</span>
                      <button
                        type="button"
                        onClick={deleteSop}
                        disabled={sopBusy}
                        className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                      >{sopBusy ? 'Deleting…' : 'Yes, delete'}</button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteSop(false)}
                        disabled={sopBusy}
                        className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                      >Cancel</button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteSop(true)}
                      className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                    >
                      <TrashIcon className="h-4 w-4" /> Delete SOP
                    </button>
                  )
                )}
                {cycle.sopRecordId && !confirmDeleteSop && (
                  <button
                    type="button"
                    onClick={saveSopToLibrary}
                    disabled={savingToLibrary}
                    title="Save this SOP to your reusable library (My SOPs)"
                    className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 disabled:opacity-50"
                  >
                    <PlusIcon className="h-4 w-4" /> {savingToLibrary ? 'Saving…' : 'Save to My SOPs'}
                  </button>
                )}
                <div className="text-sm text-gray-500 truncate">
                  {libraryMsg
                    ? <span className="text-blue-700">{libraryMsg}</span>
                    : Object.keys(progressDraft).length === 0
                      ? 'No changes yet'
                      : `${Object.keys(progressDraft).length} change${Object.keys(progressDraft).length === 1 ? '' : 's'}`}
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowUpdateProgressModal(false)}
                  className="btn-secondary"
                  disabled={savingProgress}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProgress}
                  disabled={savingProgress || Object.keys(progressDraft).length === 0}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                >
                  {savingProgress ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* iTrace Serialization — Plant Batch Created Modal */}
      {showQRModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {itraceBatch ? 'Plant Batch Created in iTrace' : 'iTrace QR Generation'}
                </h3>
                <button
                  onClick={handleCloseITraceBatchModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {itraceBatch ? (
                  <>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5" />
                        <div className="text-sm text-green-900">
                          <strong>Harvest sent to iTrace.</strong> A Plant Batch has been
                          created and is ready to be linked to a Commission in the iTrace
                          Serialization portal — that's where you will generate and print
                          the QR code labels.
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 text-left">
                      <h4 className="font-medium text-gray-900 mb-2">Plant Batch</h4>
                      <div className="space-y-1 text-sm text-gray-700">
                        <div><strong>Batch Name:</strong> {itraceBatch.batchName}</div>
                        <div className="break-all"><strong>EPC Code:</strong> <code className="text-xs bg-white px-1 py-0.5 rounded border">{itraceBatch.batchCode}</code></div>
                        <div><strong>Batch ID:</strong> {itraceBatch.id}</div>
                        <div><strong>Site ID:</strong> {itraceBatch.siteId}</div>
                        <div><strong>Status:</strong> {itraceBatch.isAssigned ? 'Assigned' : 'Available'}</div>
                      </div>
                    </div>

                    {middlewareQrCode && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-900">
                        <strong>QR code generated by Kapitani middleware:</strong>
                        <div className="mt-3 flex justify-center">
                          <img
                            src={middlewareQrCode.startsWith('data:') ? middlewareQrCode : `data:image/png;base64,${middlewareQrCode}`}
                            alt="Harvest QR code"
                            className="w-48 h-48 border border-gray-200 rounded bg-white p-2"
                          />
                        </div>
                        <p className="mt-2 text-xs">
                          Scan with the iTrace consumer PWA to verify the harvest record.
                        </p>
                      </div>
                    )}

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
                      <strong>Next steps (in the iTrace Serialization portal):</strong>
                      <ol className="list-decimal list-inside mt-2 space-y-1">
                        <li>Click <em>Commission Tasks → New Commission</em></li>
                        <li>Enter the number of QR labels you want</li>
                        <li>Select the product</li>
                        <li>In <em>Batch Source</em>, search and pick the Plant Batch above</li>
                        <li>Click <em>Create Commission</em> → labels are generated with status <code className="bg-white px-1 rounded">CREATED</code></li>
                        <li>Click <em>Print Labels</em> → status becomes <code className="bg-white px-1 rounded">PRINTED</code></li>
                        <li>Physically scan each printed label with the PDA app</li>
                        <li>Click <em>Validate Commission</em> → status becomes <code className="bg-white px-1 rounded">VERIFIED</code> and consumer scans start resolving</li>
                      </ol>
                      <p className="mt-3 text-xs italic">
                        Until steps 6–8 are done, consumer scans of the QR will show
                        "product not found". This is by design — the trace code only goes
                        live after physical validation in the field.
                      </p>
                    </div>

                    {itraceError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                        {itraceError}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-gray-400 text-center">
                    <QrCodeIcon className="h-16 w-16 mx-auto mb-2" />
                    <p className="text-sm">No plant batch yet. Complete the cycle to create one.</p>
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    onClick={handleCloseITraceBatchModal}
                    className="flex-1 btn-secondary"
                  >
                    Close
                  </button>
                  {itraceBatch && (
                    <button
                      onClick={handleOpenITracePortal}
                      disabled={openingITracePortal}
                      className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {openingITracePortal ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                          </svg>
                          Opening...
                        </>
                      ) : (
                        <>
                          <QrCodeIcon className="h-4 w-4" />
                          Open iTrace Portal
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Activity Modal (SOP Selection) */}
      <AddActivityModal
        isOpen={showAddActivityModal}
        onClose={() => setShowAddActivityModal(false)}
        cycle={cycle}
        onSopSelected={(sopRecordId) => {
          console.log('SOP applied:', sopRecordId);
          onRefresh();
        }}
      />
    </div>
  );
};

export default FarmManagement;
